/**
 * ScanPage — 카메라 스캔 **전체화면 activity** (2026-08-01 라벨링 UX 재설계. 네이티브 전용).
 *
 * 플로우:
 *   live    고정 가이드 + 검출 코너 **점** 표시(위=주황/아래=하늘). 촬영은 **수동 셔터**
 *           (사진 버튼) — 자동 셔터 발화는 일단 꺼둔다(AUTO_SHUTTER_FIRE, 2026-08-15
 *           사용자 결정. 판정 로직은 상태 칩 안내용으로 유지). still 은 엔진 stillHybrid
 *           경로에서 seg 정밀화가 돈다.
 *   editing 가이드 밖 검정 80% 딤 + **이중 기준선**: 모델(테두리 인식) 기준선은 읽기
 *           전용(초록), 사용자 라벨링 선은 8핸들 드래그(보라). 매치 버튼 2개 —
 *           모델 영역/사용자 영역 각각 온디바이스 matcher 를 돌려 **별도 결과 레이어**로
 *           표시 (라벨링·모델 검증 병행용).
 *
 * 우상단 X = 스캔 종료(activity 닫기 — 열었던 화면으로 복귀). 라벨링 중에도 바로 닫는다.
 * 뒤로가기(하드웨어/제스처)는 라벨링 중이면 **다시 찍기**로 되돌린다 (close-guard).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { Camera } from "@capacitor/camera";
import { type LiveMatchHintEvent, CardEngine } from "@duckflex/capacitor-card-engine";
import {
  LiveVision,
  mapQuadToView,
  type CaptureAndCropResult,
  type DetectionEvent,
} from "../../native/liveVision";
import { isNative, nativeWarn } from "../../native";
import { applyScanLanguage, ASSET_STREAMS, type AssetLease } from "../../native/assetDelivery";
import { fetchCardsByIds, cardImageUrl, type CardItem } from "../../api/card";
import {
  catalogCardNumber,
  crossCheckCardsFrom,
  crossCheckMatches,
  extractCardNumbers,
  extractRawCardNumbers,
  findCardByNumber,
  parseIndexCatalog,
  type IndexCatalogRow,
} from "./ocrCrossCheck";
import {
  uploadScanLabel,
  SCAN_LABEL_CONDITIONS,
  type ScanLabelCondition,
} from "../../api/scanLabel";
import { useScanSession } from "./useScanSession";
import { acquireCardMatcherLeases, CARD_MATCHER_KINDS, releaseLeases } from "./useScanSession";
import { useAutoShutter, type AutoCapturePayload } from "./useAutoShutter";
import { computeCardGuide, computeCardGuideBox, type GuideBand } from "./scanGuide";
import { CORNER_TOP_COLOR, CORNER_BOTTOM_COLOR, isTopCornerIndex } from "./scanColors";
import { useMainTabActive } from "../../components/layout/mainTabActiveContext";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { useDialogBackBinding } from "../../navigation/useDialogBackBinding";
import Icon from "../../components/primitives/Icon";
import CardOutlineEditor from "./CardOutlineEditor";
import { outlineFromCorners, virtualCorners, type Pt } from "./outlineGeometry";
import { useAuthStore } from "../../store/authStore";
import { useDebugStore } from "../../store/debugStore";
import { useActivityStore, type ActivityStoreState } from "../../store/activityStore";
import { useRequireAuth } from "../../navigation/useRequireAuth";
import { useGuestScanStore, GUEST_SCAN_LIMIT } from "../../store/guestScanStore";
import { maskPrice } from "../../lib/priceMasking";
import { formatKrw } from "../../lib/formatPrice";
import { recordRecentScan } from "../../lib/recentScans";
import ScanResultRail from "../../components/scan/ScanResultRail";
import ConfirmDialog from "../../components/feedback/ConfirmDialog";
import { scanTotals, useScanSessionStore } from "../../store/scanSessionStore";
import AddToCollectionSheet from "../../components/collection/AddToCollectionSheet";
import { useAssetSyncStore, selectAssetSyncProgress } from "../../store/assetSyncStore";
import {
  useScanLangStore,
  SCAN_LANGS,
  SCAN_LANG_LABEL,
  SCAN_LANG_SHORT,
} from "../../store/scanLangStore";
import { HStack, VStack } from "@astryxdesign/core/Layout";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { ToggleButton } from "@astryxdesign/core/ToggleButton";
import Sheet from "../../components/feedback/Sheet";
import { track } from "../../lib/tracking";
import { scanEvidenceOf, scanSimilarityOf } from "./scanTelemetry";
import styles from "./ScanPage.module.css";

type ViewMode = "live" | "editing";
type MatchTarget = "model" | "user";

interface CapturedPhoto {
  /** file:// 경로 (lv_crops — deleteCrop 으로 정리) */
  path: string;
  /** convertFileSrc 처리된 표시용 URL */
  src: string;
  width: number;
  height: number;
}

interface MatchEntry {
  cardSerlno: number;
  similarity: number;
  /** 사이드카(card_meta) 탑재 릴리스에서만 — 즉시 적재 스텁·교차검증의 재료 */
  name?: string;
  number?: string | null;
  setCode?: string | null;
}

interface MatchResult {
  matched: boolean;
  matches: MatchEntry[];
  /** 카탈로그 조회 결과 — 조회 실패/미조회면 null (유사도만 표시로 폴백) */
  cards: Map<number, CardItem> | null;
  /** still OCR 카드번호와 카탈로그 번호가 일치한 후보 — "번호 일치" 표시 */
  confirmedSerlnos: Set<number>;
  /** OCR 텍스트에 카탈로그 카드명이 나타난 후보 — 번호 일치와 겹치면 "이름·번호 일치" */
  nameConfirmedSerlnos: Set<number>;
  /** OCR 텍스트에 세트 코드가 나타난 후보 — 번호가 안 읽힐 때의 재판 판별 축 */
  setConfirmedSerlnos: Set<number>;
  /** 확신 매칭인데 top-1 언어가 인식 언어와 다르면 그 언어 코드 — 안내용 */
  langMismatch: string | null;
  /** 확신 미달인데 top 후보 중 번호 일치가 유일하게 top-1 — "번호 일치로 확정" 헤더 */
  numberResolved: boolean;
  /** 확신 미달인데 이름+번호 동시 일치가 유일하게 top-1 — 최상급 확정 헤더 */
  nameNumberResolved: boolean;
  /** top 후보엔 없지만 카드명 확장 검색에서 OCR 번호가 유일 일치한 카드 — 별도 행 */
  ocrFound: CardItem | null;
  /** 인식 정보 패널용 — 재정렬 반영된 전체 후보(엔진 top-5) */
  allMatches: MatchEntry[];
  /**
   * 촬영 시점 라이브 힌트의 top-1 (확신 힌트가 없었으면 null).
   * 라이브는 거친 quad, 스틸은 정밀화된 quad — **서로 다른 크롭**이라 둘이 같은 답을
   * 내면 독립 신호 2개다. 지금은 인식 정보 패널에만 노출한다: 자동 확정(a안)으로
   * 넘어갈 임계를 실기기 일치율로 잡기 전까지는 판정에 쓰지 않는다.
   */
  liveTop: number | null;
  /** liveTop 과 스틸 top-1 의 일치 여부 — liveTop 이 없거나 후보가 없으면 null */
  liveAgreed: boolean | null;
  /** still OCR 원문 — 실물 언어(문자 종류) 검증용. OCR 실패/미지원이면 null */
  ocrLines: string[] | null;
  /** 원본 quad crop 휘도 평균·표준편차 (0~255) — 노출 게이트 근거. 구 엔진이면 null */
  cropLuma: number | null;
  cropStd: number | null;
}

/** 인식 정보 패널용 OCR 상태 — null=아직 없음, "failed"=OCR 실패/미지원 */
type OcrInfo = { lines: string[]; canonical: string[]; raw: string[] } | "failed" | null;

/** 라이브 힌트 배지에 띄울 top-1 후보 */
/**
 * 자동 셔터 발화 (2026-08-16 재도입). 8/15 에 껐던 이유는 발화 조건이 **기하**였기
 * 때문이다 — 카드 모양이 가이드를 채우기만 하면 찍어서, 라이브 quad 가 거친 프레임까지
 * 스틸로 넘어갔다. 지금은 라이브 힌트 매칭의 프레임 간 일관성(streak)이 게이트라
 * "이 카드다"가 연속으로 나올 때만 눌린다 (autoCaptureGate).
 */
const AUTO_SHUTTER_FIRE = true;
/**
 * 라벨 수집 UI·진단줄 노출은 **디버그 메뉴 토글**이 정한다 (2026-08-28 사용자 결정).
 * 종전 기준은 빌드 모드(비운영)였는데, dev 빌드로 실기기 인식을 보는 동안에도
 * 모드 전환 버튼과 진단줄이 항상 떠 화면을 덮었다. 이제 로그인 워드마크 10탭 →
 * 디버그 메뉴에서 켠 세션에만 존재한다 (sessionStorage — 앱 재시작 시 자동 off).
 */

/** 스캔/라벨링 모드 저장 키 — 라벨링 세션이 앱 재시작을 넘겨 이어지게 한다 */
const SCAN_MODE_STORAGE_KEY = "peeka.scanMode";

/**
 * 재장전 판정 — 이만큼 **연속으로** 미검출이면 다음 장을 잡을 준비.
 *
 * 2026-08-31 에 프레임 수(4)에서 시간으로 바꿨다. 두 가지 이유다:
 *
 * ① **기기마다 뜻이 달랐다.** 같은 "4프레임"이 폴드7에서는 ~440ms, Note 9 에서는
 *    1.4~3초였다(검출 속도가 그대로 이벤트 주기였으므로). 카드를 치우는 동작 하나가
 *    기기에 따라 3~7배 다르게 읽혔다.
 * ② **엔진 무검출 cadence 와 충돌한다.** 엔진이 카드 없는 프레임의 검출을 건너뛰면
 *    (CardQuadDetector IDLE_INTERVAL_MS) `detect()` 가 즉시 반환하므로 **빈 이벤트가
 *    카메라 fps 로 빨라진다.** 프레임을 세면 그만큼 빨리 재장전되고, 재장전은
 *    중복 가드(lastConfirmedSerlnosRef)를 푸는 자리라 방금 담은 카드가 다시 담긴다.
 *    시간 기준은 이벤트 주기와 무관해 그 커플링 자체가 없다.
 *
 * 값은 폴드7의 종전 동작(~440ms)에 맞췄다 — 손떨림·순간 흐림으로 검출이 한둘
 * 빠지는 것과 "카드를 치웠다"를 가르는 선이다.
 */
const LOST_ARM_MS = 500;
/**
 * 카드 전환 재장전 (2026-08-25 실기기) — 카드를 치우지 않고 다음 장으로 훑어가면
 * (테이블 스윕) 검출이 연속으로 끊기지 않아 LOST_ARM_MS 재장전이 영영 안 걸리고,
 * sim 은 계속 나오는데 브래킷·확정이 멈춘 것처럼 보였다. 마지막 확정 카드와 **다른**
 * top-1 이 이 유사도로 이 횟수 연속 잡히면 검출 단절 없이 재장전한다. 같은 카드는
 * 재장전하지 않는다 — 중복 적재 방지 (같은 카드 추가는 종전대로 치웠다 다시 댄다).
 */
/**
 * 0.35→0.2 (2026-08-27 실기기 데드락): 확정 직후 sim 0.31 짜리 카드(약한 매칭 —
 * 토플로더·조명)로 넘어가면 전환 재장전 문턱(0.35)을 영영 못 넘고, 검출은 계속
 * 이어져 단절 재장전도 안 걸려 **흰 브래킷에 잠겼다**. 정체(다른 카드) 판별 조건은
 * 유지되므로 하한은 노이즈 컷 역할만 한다.
 */
const REARM_MIN_SIM = 0.2;
const REARM_VOTE = 2;
/**
 * 재장전 타임아웃 (2026-08-27, 위 데드락의 안전망): 확정 후 이 시간이 지나도록
 * 검출이 이어지면 힌트 정체와 무관하게 재장전한다. 같은 카드 재적재는 가드 집합
 * (lastConfirmedSerlnosRef)이 계속 막으므로 안전하다 — 열리는 것은 셔터뿐이고
 * 판정·가드는 그대로다.
 */
const REARM_TIMEOUT_MS = 4000;
// 언어 판독 게이트(이름 증거·글자량 모순 등)는 2026-08-25 에 도입했다가 전부
// 걷어냈다 — OCR 은 정확도 보조 전용이다. 경위는 confirmScan 의 주석 참조.

/**
 * 줌 배율 (SC-003 · FN-024). 0.6× 는 명세에 없어 뺐다 — 카드 한 장을 화면에 채우는
 * 일에 축소 배율은 쓸 일이 없고, 네 칸이 되면 열이 길어져 엄지가 닿는 높이를 벗어난다.
 */
const ZOOM_STEPS = [1, 2, 3] as const;

/**
 * `.guideBand` 를 재서 브래킷 대기 구간을 **캔버스 backing-store 픽셀**로 돌려준다.
 * 표식은 CSS px 로 배치되므로 캔버스 픽셀비를 곱한다 — 그리기 좌표계가 그쪽이다.
 * 아직 레이아웃이 안 잡혔으면 null (호출부가 종전 비율 배치로 폴백).
 */
function readGuideBand(
  canvas: HTMLCanvasElement,
  band: HTMLElement | null,
): GuideBand | undefined {
  if (!band) return undefined;
  const rect = band.getBoundingClientRect();
  const cssH = canvas.clientHeight;
  if (rect.height <= 0 || cssH <= 0) return undefined;
  const ratio = canvas.height / cssH;
  const canvasTop = canvas.getBoundingClientRect().top;
  return { top: (rect.top - canvasTop) * ratio, bottom: (rect.bottom - canvasTop) * ratio };
}

const ERROR_STATES = ["model-pending", "permission-denied", "engine-error"] as const;
const DOT_R = 7; // 라이브 검출 점 반경 (CSS px)
/**
 * 결과에 보여줄 후보 수. 엔진(MatcherEngine.match)이 내려주는 최대치와 같아 후보를
 * 자르지 않는다. 패널이 길어져 라벨링 핸들을 덮던 문제는 개수 제한이 아니라 목록
 * 자체의 스크롤(.resultList max-height)로 막는다 — 접전에서 3~5위가 정답인 사례가
 * 있어 잘라내면 사용자가 확인할 방법이 없다.
 */
const RESULT_TOP_K = 5;
/**
 * 매칭 **판정 풀** 상한 (2026-08-27) — 같은 그림 재판 접전에서 정답이 5위 밖으로
 * 밀려 번호 교차검증이 대조할 후보 자체가 없던 실기기 사례. 엔진에서 10개를 받아
 * 교차검증(번호·이름·세트코드 재랭킹)은 풀 전체로 돌리고, 표시는 종전대로
 * [RESULT_TOP_K]로 자른다.
 */
const MATCH_POOL_TOP_K = 10;
/** 고해상 에스컬레이션의 OCR 디코드 상한 — 센서 최대 스틸의 번호 글리프를 살린다 */
const ESCALATE_OCR_LONG_SIDE = 4096;
/**
 * 고해상 재판정 에스컬레이션 스위치 — **꺼짐** (2026-08-27 사용자 결정: 12MP 일시
 * 리바인드 왕복이 Note 9 실측 ~6초라 촬영 전후로 체감 버벅임. 상시 스틸은 계속
 * 1920×1080 이고, 이 스위치는 수락 미달 때의 1회 센서최대 재판정만 막는다 —
 * 배관(셸 takeStillMax·엔진 maxLongSide)은 남겨 두고, 중급기 이상에서 재평가).
 */
const ESCALATE_ENABLED = false;
/**
 * OCR 보조 전체 스위치 — **임시 꺼짐** (2026-08-27 사용자 요청: 임베딩 단독 정확도와
 * OCR 보조의 기여도를 실기기로 A/B). 끄면: still·힌트의 번호/이름/세트코드 교차검증,
 * 번호 확정(numberResolved 류), 확장 번호 검색(ocrFound)이 전부 빠지고 수락은
 * matched(임베딩) 단독이 된다. 어두움×약확신×무증거 보류도 증거 축이 사라져 함께
 * 끈다(실루엣 극단 게이트는 OCR 무관이라 유지). **측정 끝나면 true 로 되돌릴 것.**
 */
const OCR_ASSIST_ENABLED = true;
/**
 * 자동 촬영·배지 승격을 만드는 **프레임 합의** — 같은 top-1 이 이만큼 연속으로 나오면
 * 채택한다.
 *
 * ⚠ 엔진의 `matched`(단일 프레임 확신 = matcher_config 의 sim_accept+sim_margin)를
 * 요구하지 않는다. 라이브 quad 는 정밀화 전이라 거칠어서 그 임계를 좀처럼 넘지 못한다
 * — 실기기에서 "카드 인식됨"만 뜨고 카드명 힌트가 한 번도 안 뜨는 것으로 확인됐다
 * (2026-08-17). 확신을 단일 프레임에 요구하고 **거기에 더해** 연속까지 요구하면
 * 이중 게이트라 영영 안 열린다. 확신은 프레임 합의가 만든다는 게 원래 설계였다.
 *
 * 느슨해 보이지만 이 게이트가 정하는 것은 **언제 찍을지**이지 어떤 카드인지가 아니다.
 * 촬영 뒤 스틸(정밀화 quad + OCR)이 다시 판정하고 확정은 사용자 탭이라, 합의가 틀려도
 * 손해는 사진 한 장이다.
 */
const AUTO_CAPTURE_VOTE = 3;
/**
 * 합의에 참여시킬 top-1 유사도 하한. 이보다 낮으면 후보로도 취급하지 않는다
 * (참고: MonPrice 표기 기준 GOOD 0.85 / MODERATE 0.6 — 그 아래로 잡아 "찍을지" 판단만).
 */
const AUTO_CAPTURE_MIN_SIM = 0.5;
/**
 * 투표 유지 하한 (히스테리시스, 2026-08-25 — 라벨 228 뮤): 같은 그림 재판이 많은
 * 카드는 sim 이 0.5 경계에 걸쳐(0.50~0.55) 한 틱만 0.49 로 떨어져도 투표가 리셋돼
 * 게이트가 영영 안 찼다. 시작은 0.5 이상, **일단 시작된 투표는 같은 카드가 이
 * 값 이상이면 유지**한다.
 */
const AUTO_CAPTURE_KEEP_SIM = 0.45;

/**
 * 스틸 우선 확정 (2026-08-26 사용자 결정 — 몬프라이스 동작 확인 후 재설계).
 *
 * 종전: 라이브 힌트 매칭이 같은 top-1 을 [AUTO_CAPTURE_VOTE]회 연속 낼 때까지 기다렸다가
 * **판정에 쓴 그 프레임**(takeHintFrame)으로 확정했다. 문제는 그 대기가 비쌌다는 것 —
 * 힌트 주기가 250~1000ms 라 3표에 **0.75~3초**가 들고, 같은 그림 재판끼리 유사도가
 * 붙으면 top-1 이 틱마다 튀어 합의가 **영영 안 차는** 실패 모드까지 있었다
 * ([AUTO_CAPTURE_KEEP_SIM] 히스테리시스와 "수락 미달 스틸 재확인"이 그 땜질이다).
 *
 * 지금: 검출이 안정되면(useAutoShutter 의 선명 프레임 3히트 ≈0.3초 — 검출은 ~10fps 라
 * 매칭 투표보다 한 자릿수 싸다) **곧바로 스틸을 찍어 그것으로 판정한다**. 스틸 촬영
 * 0.3~0.6초가 투표 3회보다 짧아 지연이 오히려 줄고, 고해상이라 컬렉터 번호 OCR 이
 * 살아난다(720p 라이브 프레임에서 번호는 ~8px 로 판독 한계 아래 — 약확신 구제를
 * 만들게 한 바로 그 문제다).
 *
 * ⚠ 라이브 힌트 매칭은 **끄지 않는다**. 역할만 바뀐다 — 확정 게이트에서는 빠지고
 * 재장전([REARM_MIN_SIM] — 테이블 스윕에서 검출이 안 끊겨 LOST_ARM_MS 만으로는 다음
 * 장으로 못 넘어간다)·배지·브래킷 표시를 계속 맡는다.
 *
 * ⚠ **iOS 는 아직 이득이 없다** — 세션 프리셋이 720p 고정이라 스틸이 라이브 프레임과
 * 같은 해상도다(Android 는 스틸만 1920×1080). iOS 스틸 고해상화가 선행돼야 한다.
 *
 * false 로 두면 종전(투표 게이트 + 판정 프레임) 동작으로 돌아간다 — 같은 빌드에서
 * 라벨 하네스로 두 구조를 비교하려고 남겨 둔다.
 */
const STILL_FIRST_CONFIRM = true;
/**
 * 스틸 한 장에서 심사하는 카드 수 상한. **1 = 단일 확정(현행)** — 2026-08-26 에
 * 4(스틸 한 장의 카드 일괄 적재)로 넣었다가 같은 날 사용자 결정으로 되돌렸다:
 * 몬프라이스도 일괄이 아니라 **한 장씩 순차·고속**이고, 일괄은 옆에 놓인 의도치 않은
 * 카드까지 담는다(오적재) + 장당 "담김" 피드백이 뭉개진다. 다중 검출 배관(엔진
 * STILL_MAX_QUADS·captureAndCrop quads·이 함수의 목록 처리)은 남긴다 — 중앙 카드
 * 게이트·추적 표시 등 후속 방향 어느 쪽이든 재사용된다.
 */
const STILL_CONFIRM_MAX_CARDS = 1;
/**
 * 노출 게이트 (2026-08-27 — 역광 실루엣 실사고): 아침 역광에서 카드 면이 실루엣으로
 * 찍히자 임베딩이 "어두운 아트의 카드"(망나뇽 EX)에 matched=true 로 붙어 오답을
 * 담았다. crop(임베딩 입력과 같은 픽셀)의 휘도 평균·표준편차가 **둘 다** 하한 미만
 * = 실루엣이면 자동 확정을 보류하고 안내한다. 문턱은 극단만 걸리게 보수적으로 —
 * 어두운 아트 카드는 평균이 낮아도 프레임·텍스트 대비(std)가 남는다. 모든 확정
 * 시도에 `[expo]` 로그를 남기므로 실사용 분포를 보고 조정한다. 수동 셔터는 게이트
 * 밖(의도적 촬영). 구 엔진(cropLuma null)은 fail-open.
 */
const EXPO_MIN_LUMA = 35;
const EXPO_MIN_STD = 30;
/**
 * 어두움 × 약확신 × 무증거 보류 (2026-08-27 실측 서명): 역광 오답(망나뇽 EX)의
 * 실측이 luma=66~71·std=44~51·sim=0.36 — 실루엣 극단(위 EXPO_MIN_*)엔 안 걸리고
 * luma 단독 문턱은 칼날(70 에서 66/71 이 갈렸다)이라, 문턱이 아니라 **증거 구조**로
 * 건다: crop 이 어둡고(luma<90) 임베딩 확신이 약하고(sim<0.45) **OCR 교차 증거가
 * 전무**(이름·번호·세트코드 어느 것도 top-1 과 일치 없음)일 때만 보류. 어두워도
 * 글자 하나라도 맞으면 통과하므로 "아무것도 안 읽히는 어두운 장면의 임베딩 단독
 * 확정"만 막는다 — 밝은 장면(luma≥90)과 확신 매칭(sim≥0.45)은 영향 없다.
 */
const EXPO_WEAK_LUMA = 90;
const EXPO_WEAK_SIM = 0.45;
/**
 * 번호 모순 게이트 — **관측 모드** (2026-08-27 사용자 결정: OCR 을 강하게 믿지
 * 못하므로 보류 발동 없이 `[gate:number-would-hold]` 로그만 남겨 오탐률부터 잰다).
 * 발동 조건(전부 AND): still 확정이 matched=true / OCR 이 전체형 번호를 읽음 /
 * 그 번호가 top-1 과 관대 대조(혼동쌍 치환)로도 불일치 / 그 번호의 카드가 사이드카
 * 카탈로그에 실존(findCardByNumber). 로그 축적 후 실제 보류로 전환할지 판단한다.
 */
const NUMBER_GATE_SHADOW = true;

/**
 * 사이드카 메타(이름·번호·세트코드)만으로 만든 즉시 적재용 카드 (2026-08-27 사용자
 * 결정 — "레일에 먼저 담고 이미지·시세는 API 로 뒤에서"). 카탈로그 API 를 확정
 * 경로에서 떼어내 판정 시간에 네트워크가 끼지 않는다. cardUid 는 비회원 차감
 * dedup 키로만 쓰이므로 serlno 대리키면 충분하다(같은 카드 = 같은 키 유지).
 */
function stubCardFromSidecar(
  serlno: number,
  name: string,
  number: string | null,
  setCode: string | null,
  lang: string,
): CardItem {
  return {
    cardSerialNo: serlno,
    cardUid: `serl:${serlno}`,
    name,
    number,
    language: lang,
    rarity: null,
    imagePathway: null,
    cardsetSerialNo: 0,
    cardsetName: setCode ?? "",
    cardsetCode: setCode ?? "",
    evolutionStage: null,
    listPrice: {
      amount: null,
      currency: null,
      reliability: "LOW",
      observationCount: 0,
      display: "NONE" as CardItem["listPrice"]["display"],
      trendStatus: "FLAT",
      trendRate: null,
      trendBaseDate: null,
    },
  };
}
/**
 * 판정에 쓸 픽셀을 어디서 얻는가 — 게이트(검출 안정 즉시 발화)와는 **별개 축**이다.
 * 속도는 양쪽 같고, 다른 건 출처뿐이다.
 *
 * **플랫폼이 갈린다 (2026-08-26 저녁 재결정 — 양 플랫폼 라이브 통일을 하루 만에 철회).**
 *
 * **Android = 사진 스틸.** ImageCapture 가 분석(720p)과 **별개의 1920×1080 스트림**
 * 이라 라이브를 올릴 필요 없이 고해상 판정을 얻는다. 라이브를 1080p 로 올려 통일해
 * 봤더니 검출 입력·변환·발열이 전부 비싸져 폴드7 브래킷까지 무너졌다 — 별도 스트림이
 * 있는 플랫폼에서 그걸 버릴 이유가 없었다. Android 는 서드파티 앱에 시스템 강제
 * 셔터음이 없어 소리 문제도 없다(오전 실사용 확인).
 *
 * **iOS = 라이브 프레임 (1080p 분석 버퍼).** 세션이 스트림 하나라 사진을 찍어도
 * 분석과 같은 포맷인데 셔터음(한국 기기 시스템 강제)·AF 펄스·검출 공백은 다 문다.
 * 그래서 분석 버퍼를 1920×1080 으로 올리고(startScan analysisSize — iOS 만) 그
 * 프레임을 그대로 판정에 쓴다. 덤으로 "인식한 장면 ≠ 찍힌 장면"(2026-08-17)이 원천
 * 소거된다.
 *
 * **라벨링 모드 = 항상 사진.** 라벨은 학습 데이터라 photo 파이프라인의 화질이 값을 한다.
 */
// A/B 측정(2026-08-27) 결론과 함께 복원: 임베딩 단독은 같은 그림 재판 접전에서
// sim 0.70 인데도 마진 동률로 영영 미확정(스핀로토무 실측) — 그 동률을 깨는 것이
// 스틸+OCR 의 기여분이다. 촬영 멈칫은 셸 ZSL(CAPTURE_MODE_ZERO_SHUTTER_LAG)로 해소.
const PHOTO_STILL = Capacitor.getPlatform() !== "ios";
/**
 * 자동 스틸 최소 간격 — **촬영 시작 기준**. 투표 게이트가 사라진 자리에서 폭주만
 * 막는 바닥값이다(확정에 이르지 못한 촬영이 연달아 나는 것을 끊는다).
 */
const AUTO_STILL_MIN_GAP_MS = 400;
/**
 * 확정 실패가 이어질 때의 추가 대기 — **직전 판정이 끝난 시각 기준**(2026-09-07).
 * 종전에는 촬영 시작 기준이라 확정 소요(~1.6초)와 겹쳐 있었다: backoff 1 은 사실상
 * 0초였고 2 부터 갑자기 물렸다. 끝 기준으로 바꾸면 이 숫자가 곧 "판정 후 쉬는 시간"이다.
 *
 * 배수는 **연속 실패 2회째부터** 붙는다(첫 실패는 유예 — 한 번 미끄러졌다고 카드를
 * 잘 대고 있는 사용자를 3초 세우지 않는다). 상한 [AUTO_STILL_MAX_BACKOFF]=4 에서
 * 실질 대기는 최대 (4-1)×1.5 = 4.5초 — 인식 안 되는 물체를 대고 있을 때만 여기까지 간다.
 */
const AUTO_STILL_COOLDOWN_MS = 1500;
const AUTO_STILL_MAX_BACKOFF = 4;

/**
 * 약확신 스틸 구제 (2026-08-25) — 같은 top-1 이 이 유사도 이상으로 이 횟수만큼
 * 지속되면(자동확정 게이트는 미달) 화면 유지한 채 **진짜 스틸(1080p)** 을 조용히
 * 찍어 확정을 시도한다. 라이브 분석 버퍼(720p)로는 컬렉터 번호가 판독 한계 아래인
 * 기기가 있다 — 실측: 토플로더 폭거북스 sim 0.4x 지속 + OCR 번호 미판독, 같은
 * 장면의 still 매칭은 matched=true (앱 라벨 sn219). 카드당 1회만 시도(스팸 방지).
 * 하한 0.3→0.15 (2026-08-25 확대 결정) — still 확정에 자체 수락 게이트(임베딩
 * accept/번호 확정)가 있어 틀린 카드가 담길 위험 없이 시도 폭만 넓힌 것.
 */
const WEAK_RESCUE_MIN_SIM = 0.15;
const WEAK_RESCUE_STREAK = 3;
/**
 * 락온 오버레이 (2026-08-25) — 가이드가 고정 사각형이 아니라 **코너 브래킷**이고,
 * 검출되면 브래킷이 카드로 날아가 따라붙는다(미검출 hold 후 가이드로 복귀).
 * 미검출 0.6초 유지(→1.2초 완화, LOCK_HOLD_MS). 검출 ~10fps 를 표시 60fps 로
 * 보간해 monprice 급 부드러움.
 *
 * 보간 상수는 testapp 의 0.28(목표 도달 ~120ms)에서 **검출 주기 적응**으로 바뀌었다
 * (2026-08-26) — rAF 루프의 tau 계산 주석 참조. 빠른 기기는 종전 체감 그대로이고,
 * 검출이 0.5초에 한 번인 구형 기기에서 점프-정지 반복이 미끄러짐으로 바뀐다.
 */
/**
 * 미검출 유지 — testapp 실측은 0.6초였지만 슬리브+글레어 카드는 verifier 가 프레임을
 * 자주 떨어뜨려 검출이 간헐적이다(실기기: 배지는 켜져 있는데 브래킷이 가이드로
 * 복귀). 1.2초로 늘려 간헐 검출에서도 붙어 있게 한다 — 카드를 치웠을 때 복귀가
 * 그만큼 늦는 트레이드오프.
 */
const LOCK_HOLD_MS = 1200;
/** 자동확정이 인정하는 화면 가시성(quadVisibleRef) 신선도 — 검출 ~10fps 대비 넉넉히 */
const QUAD_VISIBLE_MAX_AGE_MS = 1000;

/**
 * OBB 는 w/h 스왑(각도 ±90°)으로 코너 순서가 한 칸씩 밀릴 수 있다 — 표시 quad 와의
 * 총거리가 최소인 cyclic 회전을 골라 브래킷이 비틀리며 따라가지 않게 한다
 * (card-track-testapp TrackOverlayView.alignCyclic 포팅. flat 8 = x0,y0,…,x3,y3).
 */
/**
 * 락온 목표로 받아들일 quad 인가 — 4점 전부 화면 안 + 서로 겹치지 않아야 한다
 * (2026-08-25 사용자 규칙). 엔진 isClipped 는 **분석 프레임(9:16)** 기준이라
 * cover 크롭으로 화면 밖에 남는 프레임 좌우 띠(≈9%씩)의 코너를 못 거른다 —
 * 화면(캔버스) 기준으로 재검사한다. 탈락하면 목표를 갱신하지 않으므로 브래킷은
 * 가이드에 남거나 hold 만료 후 복귀한다.
 */
function lockTargetAcceptable(
  pts: { x: number; y: number }[],
  w: number,
  h: number,
  marginRatio = 0.01,
): boolean {
  // marginRatio: 화면 밖 허용 폭 — 표시(브래킷)는 0.1(가까이 댄 카드도 따라감),
  // 확정 게이트는 0.01(4코너 화면 안 규칙). 2026-08-25 분리 — 같은 엄격함으로
  // 묶으면 카드를 꽉 채워 댔을 때 배지만 켜지고 브래킷이 안 움직인다.
  const edge = Math.min(w, h) * marginRatio;
  for (const p of pts) {
    if (p.x < -edge || p.y < -edge || p.x > w + edge || p.y > h + edge) return false;
  }
  let perimeter = 0;
  for (let i = 0; i < 4; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % 4];
    perimeter += Math.hypot(b.x - a.x, b.y - a.y);
  }
  // 평균 변 길이의 5% 미만으로 붙은 코너 쌍 = 축퇴 quad — 표시하지 않는다
  const minSep = (perimeter / 4) * 0.05;
  for (let i = 0; i < 4; i++) {
    for (let j = i + 1; j < 4; j++) {
      if (Math.hypot(pts[j].x - pts[i].x, pts[j].y - pts[i].y) < minSep) return false;
    }
  }
  return true;
}

function alignQuadCyclic(target: number[], current: number[]): number[] {
  let best = target;
  let bestDist = Infinity;
  for (let shift = 0; shift < 4; shift++) {
    let dist = 0;
    for (let i = 0; i < 4; i++) {
      const j = (i + shift) % 4;
      const dx = target[j * 2] - current[i * 2];
      const dy = target[j * 2 + 1] - current[i * 2 + 1];
      dist += dx * dx + dy * dy;
    }
    if (dist < bestDist) {
      bestDist = dist;
      best =
        shift === 0
          ? target
          : Array.from({ length: 8 }, (_, i) => target[(((i >> 1) + shift) % 4) * 2 + (i & 1)]);
    }
  }
  return best;
}
/**
 * 자동 촬영 게이트가 인정하는 힌트 신선도. 표시용 TTL(4초)은 배지 깜빡임을 줄이려고
 * 길게 잡혀 있어 셔터 게이트로 쓰면 카드를 치운 뒤에도 찍힌다.
 *
 * ⚠ 1500 → 700 으로 줄였다가 **되돌렸다**(2026-08-17). 줄인 이유는 "게이트가 열린 뒤
 * 새 스틸을 찍는 사이 화면이 움직인다" 였는데, 이제는 판정에 쓴 **그 프레임**을 그대로
 * 사진으로 쓰므로(takeHintFrame) 힌트가 늙어도 프레임이 같이 늙어 **어긋남이 없다**.
 * 반면 700ms 는 힌트 주기(적응 250~1000ms)+왕복 지연과 겹쳐 게이트가 좀처럼 안 열렸다.
 */
const AUTO_CAPTURE_HINT_MAX_AGE_MS = 2500;
/** takeHintFrame 이 인정하는 프레임 나이 — 게이트보다 넉넉해야 폴백(새 스틸)로 안 샌다 */
const TAKE_HINT_FRAME_MAX_AGE_MS = 2500;
/**
 * 라이브 합의와 스틸 매치가 어긋났을 때 자동으로 다시 찍는 최대 횟수. 어긋남은 보통
 * "촬영 순간에 흔들려 다른 장면이 찍힘"이라 재촬영이 답이다. 무한 재시도는 사용자가
 * 영문을 모른 채 화면이 깜빡이는 꼴이 되므로 여기서 멈추고 결과를 그냥 보여준다.
 */
const AUTO_CAPTURE_MAX_RETAKE = 2;

/**
 * 스캔 activity 위에 다른 activity 가 얹혀 있는가 (요약 SC-011 · 카드 상세 · 담기 폼).
 * ScanPage 는 그 아래에 계속 mount 된 채라 아무것도 안 하면 **보이지 않는 화면에서
 * 인식이 계속 돌아** 요약을 보는 동안 레일에 카드가 쌓인다 (2026-08-26 제보).
 * 스택에 scan 이 없으면(테스트·직접 렌더) 덮인 것으로 치지 않는다.
 */
function selectScanCovered(s: ActivityStoreState): boolean {
  for (let i = s.activities.length - 1; i >= 0; i--) {
    if (s.activities[i].kind === "scan") return i < s.activities.length - 1;
  }
  return false;
}

export default function ScanPage() {
  const { t } = useTranslation("card");
  const activityNav = useActivityNavigator();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dotsCanvasRef = useRef<HTMLCanvasElement | null>(null);
  /** 브래킷 대기 구간 표식 (.guideBand) — 레일·셔터를 피한 자리를 CSS 가 정한다 */
  const guideBandRef = useRef<HTMLDivElement | null>(null);
  const active = useMainTabActive(); // activity 밖 기본값 true — mount/unmount 가 수명

  // 검출 이벤트 배선 — useScanSession(세션) ↔ useAutoShutter(판정)가 상호 참조라
  // ref 로 늦은 바인딩 (아래에서 채운다)
  const detectionSinkRef = useRef<(ev: DetectionEvent) => void>(() => undefined);
  const onDetection = useCallback((ev: DetectionEvent) => detectionSinkRef.current(ev), []);

  // matcher lease 5종 캐시 — 매치 버튼(모델/사용자)·라이브 힌트 매칭이 공유. 페이지
  // 수명 동안 1회 획득해 들고 있다가 unmount 에서 반환. 실패는 10초 백오프 (릴리스
  // 미발행 등). 세션 시작 전에 정의해 engineOptions(라이브 힌트)에도 물린다.
  const matcherLeasesRef = useRef<AssetLease[] | null>(null);
  const matcherLeaseFailedAtRef = useRef(0);
  const ensureMatcherLeases = useCallback(async (): Promise<AssetLease[] | null> => {
    if (matcherLeasesRef.current) return matcherLeasesRef.current;
    if (Date.now() - matcherLeaseFailedAtRef.current < 10_000) return null;
    try {
      matcherLeasesRef.current = await acquireCardMatcherLeases();
      return matcherLeasesRef.current;
    } catch (err) {
      matcherLeaseFailedAtRef.current = Date.now();
      nativeWarn("scan:matcherLeases", err);
      return null;
    }
  }, []);

  /**
   * 사이드카 전수(인덱스에 실린 카드 전량의 일련번호·이름·번호) — 카드번호 확장
   * 대조용. 페이로드가 수백 KB 라 **처음 필요할 때 한 번만** 받아 페이지 수명 동안
   * 재사용한다. 언어를 바꾸면 lease 세대가 갈리므로 함께 버린다.
   * null = 아직 안 받음 / [] = 사이드카 없는 릴리스(확장 대조 없이 진행).
   */
  const catalogRowsRef = useRef<IndexCatalogRow[] | null>(null);
  const ensureCatalogRows = useCallback(async (): Promise<IndexCatalogRow[]> => {
    if (catalogRowsRef.current) return catalogRowsRef.current;
    const leases = await ensureMatcherLeases();
    if (!leases || leases.length <= CARD_MATCHER_KINDS.length) {
      catalogRowsRef.current = [];
      return catalogRowsRef.current;
    }
    const [embed, embedData, index, serlno, matcherConfig, meta] = leases;
    const res = await CardEngine.matcherCatalog({
      embedPath: embed.path,
      embedDataPath: embedData.path,
      indexPath: index.path,
      serlnoPath: serlno.path,
      matcherConfigPath: matcherConfig.path,
      metaPath: meta.path,
    });
    catalogRowsRef.current = res.available ? parseIndexCatalog(res.packed) : [];
    return catalogRowsRef.current;
  }, [ensureMatcherLeases]);

  const { state, matcherAttached, retry, restart, capture, stop } = useScanSession({
    canvasRef,
    active,
    onDetection,
    getMatcherLeases: ensureMatcherLeases,
  });
  // 비동기 완료 시점(언어 재-sync 등)에 최신 세션 상태를 읽기 위한 ref
  const stateRef = useRef(state);
  stateRef.current = state;
  /**
   * 모델·언어팩이 바뀌어 세션을 다시 여는 중 (2026-09-07). 재시작은 stop→start 라
   * 그 사이 세션이 없고 `state` 도 active 가 아니다 — 이 구간을 "준비 중"으로 함께
   * 묶지 않으면 안내가 사라졌다가 다시 뜨고, 그 공백이 깜빡임으로 읽힌다.
   * 갤러리 복귀 재시작은 여기 해당하지 않는다(모델과 무관한 카메라 되살리기).
   */
  const [restarting, setRestarting] = useState(false);
  const restartForModel = useCallback(() => {
    setRestarting(true);
    void restart().finally(() => setRestarting(false));
  }, [restart]);

  // 카탈로그 조회 캐시 — 라이브 힌트(1Hz)가 같은 카드를 반복 조회하지 않게 하고,
  // 힌트가 미리 당겨둔 카드는 still 결과 화면에서 즉시 뜬다 (프리페치 효과).
  // missing = 인덱스 ⊃ 카탈로그의 정상 미적재 — 매번 재조회하지 않는다.
  const cardCacheRef = useRef(new Map<number, CardItem>());
  const cardMissingRef = useRef(new Set<number>());
  const fetchCardsCached = useCallback(async (ids: number[]): Promise<Map<number, CardItem>> => {
    const need = ids.filter(
      (id) => !cardCacheRef.current.has(id) && !cardMissingRef.current.has(id),
    );
    if (need.length > 0) {
      const fetched = await fetchCardsByIds(need);
      for (const id of need) {
        const item = fetched.get(id);
        if (item) cardCacheRef.current.set(id, item);
        else cardMissingRef.current.add(id);
      }
    }
    const out = new Map<number, CardItem>();
    for (const id of ids) {
      const item = cardCacheRef.current.get(id);
      if (item) out.set(id, item);
    }
    return out;
  }, []);

  const [viewMode, setViewMode] = useState<ViewMode>("live");
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);
  /** 모델(테두리 인식) 기준선 — 읽기 전용. 검출 실패 촬영이면 null */
  const [modelOutline, setModelOutline] = useState<Pt[] | null>(null);
  /** 사용자 라벨링 선 — 초기값은 모델 기준선 복제 */
  const [outline, setOutline] = useState<Pt[]>([]);
  const [modelResult, setModelResult] = useState<MatchResult | null>(null);
  const [userResult, setUserResult] = useState<MatchResult | null>(null);
  /** 결과 패널 접힘 — 펼친 패널이 라벨링 핸들을 덮으므로 헤더 탭으로 접을 수 있다 */
  /** 결과 패널 전체 접힘 — 카드를 봐야 하므로 한 번 탭으로 통째로 걷어낸다 */
  const [resultsCollapsed, setResultsCollapsed] = useState(false);
  /** 패널 안에서 지금 보고 있는 결과 (모델/내 영역) — 한 번에 하나만 띄워 높이를 묶는다 */
  const [activeTarget, setActiveTarget] = useState<MatchTarget>("model");
  const [matchingTarget, setMatchingTarget] = useState<MatchTarget | null>(null);
  /** 인식 정보 패널 — OCR 원문·추출 번호·매칭 인덱스 top-5 를 한곳에 (접이식) */
  const [ocrInfo, setOcrInfo] = useState<OcrInfo>(null);
  const [infoCollapsed, setInfoCollapsed] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const photoRef = useRef<CapturedPhoto | null>(null);
  photoRef.current = photo;
  /** 촬영 1장 식별 uuid — 라벨링 업로드 업서트 기준 (enterEditing 에서 재발급) */
  const labelKeyRef = useRef("");

  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const { requireAuth } = useRequireAuth();

  /**
   * 스캔/라벨링 모드 (2026-08-24, 사용자 결정) — 기본은 **카드 스캔 모드**(소비자
   * 연속 스캔: 확정 카드가 화면 전환 없이 레일에 쌓이고 요약(SC-011)에서 담는다).
   * 라벨링 모드 = 종전 still 에디터 플로우 그대로. 상단 버튼으로 전환하며
   * **디버그 메뉴에서 라벨링을 켠 세션에서만** 라벨링 모드가 존재한다.
   */
  const labelingEnabled = useDebugStore((s) => s.scanLabeling);
  const diagnosticsEnabled = useDebugStore((s) => s.scanDiagnostics);
  const [scanMode, setScanMode] = useState<"scan" | "label">(() =>
    typeof localStorage !== "undefined" &&
    localStorage.getItem(SCAN_MODE_STORAGE_KEY) === "label"
      ? "label"
      : "scan",
  );
  // 저장된 모드가 label 이어도 토글이 꺼져 있으면 스캔 모드다 — 디버그를 잠그거나
  // 토글을 끄는 순간 화면이 곧바로 소비자 화면으로 돌아온다.
  const isLabelMode = labelingEnabled && scanMode === "label";
  const isLabelModeRef = useRef(isLabelMode);
  isLabelModeRef.current = isLabelMode;

  /**
   * 스캔 세션 진입 1회. screen_view(/scan)와 겹쳐 보이지만 **인덱스 언어**를 함께 남긴다 —
   * 언어팩이 선택 언어 카드만 담는 구조라(scanLangStore) 확정률을 언어별로 갈라 봐야
   * 의미가 있다. mount = 세션 시작이다(스캔은 독립 activity 라 닫으면 unmount).
   */
  useEffect(() => {
    track("scan_started", {
      mode: isLabelModeRef.current ? "labeling" : "scan",
      scanLang: useScanLangStore.getState().lang,
    });
    // mount 1회 — 모드 토글은 세션을 새로 시작하지 않는다(같은 카메라 세션 안의 전환).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggleScanMode = useCallback(() => {
    setScanMode((m) => {
      const next = m === "scan" ? "label" : "scan";
      try {
        localStorage.setItem(SCAN_MODE_STORAGE_KEY, next);
      } catch {
        // 저장 실패는 무해 — 다음 진입에 기본 모드로 열릴 뿐
      }
      return next;
    });
  }, []);

  // 스캔 세션 (SC-010 인식 결과) — 스캔 모드 전용
  const sessionItems = useScanSessionStore((s) => s.items);
  /** 담기 시트 대상 — null 이면 닫힘 (design SC-012). */
  const [addSheetCard, setAddSheetCard] = useState<{ serialNo: number; name: string } | null>(null);
  const sessionAdd = useScanSessionStore((s) => s.addCard);
  const sessionRemove = useScanSessionStore((s) => s.removeItem);
  const sessionHydrate = useScanSessionStore((s) => s.hydrateCard);
  const sessionClear = useScanSessionStore((s) => s.clear);
  const sessionTotals = sessionItems.length > 0 ? scanTotals(sessionItems) : null;
  const [discardOpen, setDiscardOpen] = useState(false);

  /**
   * 재장전(FN-026) — 확정 후 **검출이 한 번 끊겨야** 다음 장을 잡는다. 시간 쿨다운만
   * 두면 화면에 머무는 동안 같은 카드가 계속 쌓인다(design 실측: 2초에 20장).
   */
  const [scanArmed, setScanArmed] = useState(true);
  const scanArmedRef = useRef(true);
  const setArmed = useCallback((v: boolean) => {
    scanArmedRef.current = v;
    setScanArmed(v);
  }, []);
  /** 연속 미검출이 시작된 시각(이벤트 timestampMs) — null 이면 검출 중 ([LOST_ARM_MS]) */
  const lostSinceRef = useRef<number | null>(null);
  const confirmBusyRef = useRef(false);
  /** confirmScan 은 아래(computeMatchResult 뒤)에서 정의된다 — 셔터/자동촬영이 ref 로 부른다 */
  const confirmScanRef = useRef<
    (p: AutoCapturePayload, opts?: { manual?: boolean; escalated?: boolean }) => Promise<void>
  >(async () => {});
  const [guestLimitReached, setGuestLimitReached] = useState(false);
  const syncStatus = useAssetSyncStore((s) => s.status);
  const syncProgress = useAssetSyncStore(selectAssetSyncProgress);
  const guestCount = useGuestScanStore((s) => s.count);
  const consumeScan = useGuestScanStore((s) => s.consumeScan);
  const scanLang = useScanLangStore((s) => s.lang);
  const setScanLang = useScanLangStore((s) => s.setLang);
  const [langOpen, setLangOpen] = useState(false);
  /** 언어 전환으로 시작된 재-sync 가 도는 중 — 라이브 화면에는 진행 UI 가 없어 따로 밝힌다 */
  const [langSyncing, setLangSyncing] = useState(false);
  const guestRemaining = Math.max(0, GUEST_SCAN_LIMIT - guestCount);
  /**
   * §4.1 소진 오버레이가 떠 있는 동안은 인식 자체를 멈춘다 (2026-08-25 사용자
   * 결정) — 락온 브래킷 추적·힌트 투표·자동확정·엔진 힌트 매칭 전부. 오버레이
   * 뒤에서 브래킷이 계속 카드를 쫓고 확정이 돌면 "막혔는데 움직인다"가 된다.
   * 로그인으로 한도가 풀리면(오버레이 소멸) 즉시 재개된다.
   */
  const guestFrozen = !isLabelMode && guestLimitReached && !isAuthenticated;
  /** 요약·상세가 위에 떠 있는 동안도 같은 이유로 멈춘다 (selectScanCovered 주석) */
  const covered = useActivityStore(selectScanCovered);
  /** 인식 전면 정지 — 투표·확정·자동셔터·브래킷·엔진 힌트 매칭 전부 */
  /** 담기 시트가 떠 있는 동안도 멈춘다 — 시트는 activity 가 아니라 covered 에 안 잡힌다.
   *  안 멈추면 수량을 고르는 사이 뒤에서 인식이 돌아 레일에 카드가 더 쌓인다
   *  (2026-08-26 에 요약·상세로 같은 사고를 겪었다). */
  /**
   * 모델·언어팩을 받고 있거나 그것 때문에 세션을 다시 여는 중 (2026-09-07 사용자
   * 결정) — 이 동안은 **인식 자체를 돌리지 않는다**. 종전에는 카메라가 살아 있고
   * 검출·투표·자동 셔터가 그대로 돌았는데, 판정에 쓸 인덱스가 아직(또는 이미 아닌)
   * 상태라 결과가 의미 없고, 그 헛도는 추론이 다운로드와 CPU 를 다퉈 받는 것까지
   * 느리게 했다. 화면에는 진행률 안내만 남는다.
   */
  const modelPreparing = langSyncing || restarting;
  const scanFrozen = guestFrozen || covered || addSheetCard !== null || modelPreparing;
  const scanFrozenRef = useRef(scanFrozen);
  scanFrozenRef.current = scanFrozen;
  // 소진 상태로 스캔에 들어오면 첫 확정 시도를 기다리지 않고 즉시 안내하고,
  // 로그인하면 해제한다 (안 풀면 autoFire 조건의 guestLimitReached 가 남아
  // 로그인 후에도 자동 셔터가 죽는다)
  useEffect(() => {
    if (isAuthenticated) setGuestLimitReached(false);
    else if (guestRemaining === 0) setGuestLimitReached(true);
  }, [isAuthenticated, guestRemaining]);
  // 정지 동안 엔진 힌트 매칭도 쉰다 (CPU/배터리) — 해제·세션 종료 시 복원
  useEffect(() => {
    if (!isNative() || state !== "active" || !scanFrozen) return;
    void CardEngine.setLiveHintPaused({ paused: true }).catch(() => {});
    return () => {
      void CardEngine.setLiveHintPaused({ paused: false }).catch(() => {});
    };
  }, [scanFrozen, state]);

  /**
   * 촬영·확정 동안 엔진 힌트 매칭을 멈춘다 — 스틸 검출·warp·매칭·OCR 과 CPU 를
   * 다투면 촬영이 길어지고, 그 길어진 만큼 검출이 끊겨 브래킷이 멈춰 보인다.
   * 수동 셔터는 원래 이렇게 하고 있었는데(handleCapture), 스틸 우선 구조에서는
   * **자동 촬영이 주 경로**라 거기에도 필요해졌다.
   * ⚠ 해제는 정지(scanFrozen) 중이면 건너뛴다 — 그 상태의 소유자는 위 effect 다.
   */
  const setHintPaused = useCallback((paused: boolean) => {
    if (!isNative()) return;
    if (!paused && scanFrozenRef.current) return;
    void CardEngine.setLiveHintPaused({ paused }).catch(() => {});
  }, []);

  const showNotice = useCallback((message: string) => {
    setNotice(message);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 2600);
  }, []);

  const deletePhotoFile = useCallback((path: string | null | undefined) => {
    if (!path) return;
    void LiveVision.deleteCrop({ path }).catch((err) => nativeWarn("scan:deletePhoto", err));
  }, []);

  // activity 닫힘(unmount) 시 사진 파일 정리 — 세션 자체는 useScanSession 이 회수
  useEffect(() => {
    return () => {
      deletePhotoFile(photoRef.current?.path);
    };
  }, [deletePhotoFile]);

  // 언어가 바뀌면 새 인덱스를 실제로 내려받아야 한다 — manifest 가 kind 당 하나만 주므로
  // lease 를 바꿔 잡는 걸로는 안 되고 재-sync 가 유일한 경로다. 이미 받아둔 언어팩은
  // 해시가 같아 하드링크로 재사용돼 두 번째부터는 즉시 전환된다.
  // 캐시한 lease 는 이전 세대를 가리키므로 함께 반환하고, 실패 백오프도 푼다.
  const langAppliedRef = useRef(scanLang);
  useEffect(() => {
    if (langAppliedRef.current === scanLang) return;
    langAppliedRef.current = scanLang;
    const stale = matcherLeasesRef.current;
    matcherLeasesRef.current = null;
    matcherLeaseFailedAtRef.current = 0;
    // 사이드카 전수도 언어팩 소속이다 — 안 버리면 이전 언어 카드로 번호 대조를 한다
    catalogRowsRef.current = null;
    if (stale) void releaseLeases(stale);
    setLangSyncing(true);
    void applyScanLanguage({
      channel: (import.meta.env.VITE_ASSET_CHANNEL ?? "PRODUCTION") as
        "INTERNAL" | "BETA" | "PRODUCTION",
      appVersion: __APP_VERSION__,
      formats: ["ONNX_1"],
      streams: [...ASSET_STREAMS],
      variants: [scanLang],
    })
      .then(() => {
        // 재-sync 완료 = 그 사이 lease 실패의 원인(다운로드 중)이 해소된 시점 —
        // 백오프를 풀지 않으면 빠른 다운로드(<10초)에서 아래 restart 가 빈손으로
        // 열려 힌트 없는 세션이 고착된다(최초 설치 직후 실기기 증상).
        matcherLeaseFailedAtRef.current = 0;
        // ⚠ 라이브 세션은 **시작 시점의** matcher lease 경로(engineOptions)를 물고
        // 있어, 재-sync 만으로는 라이브 힌트 매칭이 이전 언어 인덱스로 계속 돈다
        // (실기기 버그: ja 전환 후에도 한글 힌트). 새 언어팩은 세션 재시작으로만
        // 반영된다 — 라이브 화면일 때만 재시작 (editing 중이면 다시찍기 retry 가 처리).
        // ⚠ retry() 가 아니라 restart() 다 — doStart 는 세션이 살아 있으면 재진입
        // no-op 이라, 종전 retry() 호출은 아무 일도 하지 않았다(2026-08-18 발견).
        if (stateRef.current === "active" && viewModeRef.current === "live") restartForModel();
      })
      .finally(() => setLangSyncing(false));
  }, [scanLang, restartForModel]);
  useEffect(() => {
    return () => {
      const leases = matcherLeasesRef.current;
      matcherLeasesRef.current = null;
      if (leases) void releaseLeases(leases);
    };
  }, []);

  // 자산 sync 가 새 세대를 활성화했으면 lease 캐시를 갈아탄다. 이 캐시는 스캔 탭이
  // keep-alive 라 사실상 프로세스 수명이다 — 갱신 없이는 릴리스 전환 뒤에도 구세대
  // (최악은 전환기에 잡힌 조합)를 계속 쥐고, 그 경로로 실패가 고착된다
  // (2026-08-23 실기기: 매치 전 건 실패가 앱 재설치 전까지 지속).
  useEffect(() => {
    if (syncStatus !== "done") return;
    const held = matcherLeasesRef.current;
    if (!held) return; // 캐시가 없으면 다음 획득이 자연히 새 세대를 잡는다
    let cancelled = false;
    void acquireCardMatcherLeases()
      .then((fresh) => {
        if (cancelled || matcherLeasesRef.current !== held) {
          void releaseLeases(fresh);
          return;
        }
        if (fresh[0]?.releaseId === held[0]?.releaseId) {
          void releaseLeases(fresh); // 세대 그대로 — 기존 캐시 유지
          return;
        }
        matcherLeasesRef.current = fresh;
        void releaseLeases(held);
        // 라이브 세션은 시작 시점의 lease 경로(engineOptions)를 물고 있다 —
        // 언어 전환과 같은 이유로 재시작해야 새 세대가 힌트 매칭에 반영된다.
        if (stateRef.current === "active" && viewModeRef.current === "live") restartForModel();
      })
      .catch((err) => nativeWarn("scan:leaseRefresh", err));
    return () => {
      cancelled = true;
    };
  }, [syncStatus, restartForModel]);

  // 자산 sync 가 끝나면 **자동으로 세션을 다시 시작**한다. 이게 없으면 78MB 다운로드가
  // 끝난 뒤에도 "모델 준비 중" 화면에 그대로 머물러 사용자가 재시도를 눌러야만 진행됐다
  // (실기기 제보 "진행이 안되"의 직접 원인). model-pending 일 때만 건드린다.
  useEffect(() => {
    if (state === "model-pending" && syncStatus === "done") retry();
  }, [state, syncStatus, retry]);

  /**
   * 라이브 힌트 없이 시작한 세션 구제 (2026-08-17 실기기 회귀).
   *
   * matcher lease 는 **비필수**라 실패해도 model-pending 이 아니라 그냥 active 로 간다.
   * 그런데 경로는 startScan 인자라 **세션을 다시 열어야만** 붙고, 위 재시작 효과는
   * model-pending 일 때만 돈다 — 그래서 "다운로드가 끝나기 전에 시작된 세션"은
   * 카메라·검출은 멀쩡한데 라이브 힌트만 영구히 죽은 채로 고착된다. 증상은
   * "카드 인식됨 배지는 뜨는데 카드 이름이 영영 안 뜸"이고, 힌트를 입력으로 쓰는
   * 자동 촬영 게이트도 같이 죽는다.
   *
   * lease 가 잡히는 시점에 한 번 재시작한다. 아직 다운로드 중이면(null) 플래그를
   * 세우지 않고 다음 sync 전이에서 다시 본다.
   */
  const matcherRetriedRef = useRef(false);
  useEffect(() => {
    if (matcherAttached) {
      matcherRetriedRef.current = false; // 붙었다 — 다음 기회를 위해 재무장
      return;
    }
    // showLive 는 아래에서 선언된다(TDZ) — 같은 뜻의 viewMode 를 직접 본다
    if (!isNative() || state !== "active" || viewMode !== "live") return;
    if (matcherRetriedRef.current) return; // 세션당 1회 — 반복 재시작 금지
    // sync 가 끝난 뒤라면 직전 lease 실패는 낡은 정보다 — 백오프가 남아 있으면
    // 재획득이 null 로 삼켜지고, 이후 의존성이 안 바뀌어 이 효과가 다시 돌지 않아
    // 힌트가 세션 내내 죽는다(최초 설치에서 모델을 막 받은 직후 10초 창의 레이스).
    if (syncStatus === "done") matcherLeaseFailedAtRef.current = 0;
    let cancelled = false;
    void ensureMatcherLeases().then((leases) => {
      if (cancelled || !leases) return;
      matcherRetriedRef.current = true;
      restartForModel();
    });
    return () => {
      cancelled = true;
    };
  }, [state, matcherAttached, viewMode, syncStatus, ensureMatcherLeases, restartForModel]);

  // 오버레이·점 캔버스 backing store 를 CSS 크기(×dpr)와 동기화 — QuadOverlay/점
  // 그리기는 canvas.width/height 기준이라 동기화 없이는 기본 300×150 이 늘어져 왜곡.
  useEffect(() => {
    const canvases = [canvasRef.current, dotsCanvasRef.current].filter(
      (c): c is HTMLCanvasElement => c !== null,
    );
    if (canvases.length === 0) return;
    const sync = (canvas: HTMLCanvasElement) => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(rect.width * dpr);
      const h = Math.round(rect.height * dpr);
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
    };
    canvases.forEach(sync);
    const ro = new ResizeObserver(() => canvases.forEach(sync));
    canvases.forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, []);

  // ── 자동 셔터 + 검출 점 표시 ──────────────────────────────────────
  const suspendedRef = useRef(false); // 수동 촬영 중 자동 셔터 일시 중지
  const showLive = viewMode === "live";

  // ── 라이브 힌트 매칭(엔진 1Hz) 수신. **판정 전용**이다 — 종전에는 top-1 카드명을
  // 배지로도 띄웠는데 2026-09-07 사용자 결정으로 인식 배지를 전부 걷어냈다(상태는
  // 브래킷이 말한다). 남은 소비자는 투표 게이트·재장전·OCR 교차검증뿐이고,
  // 카탈로그 조회는 **still 확정을 위한 프리페치**로만 남는다.
  /**
   * 자동 촬영 게이트의 근거 — 확신 매칭이 streak 기준을 넘긴 **마지막 시각/카드**.
   * ⚠ 카탈로그 조회(아래 fetchCardsCached)와 무관하게 이벤트에서 바로 갱신한다.
   * 인덱스 ⊃ 카탈로그라 미적재 카드도 정상적으로 존재하는데, 그것 때문에 셔터가
   * 안 눌리면 안 된다 — 배지는 카드 정보가 있어야 뜨지만 셔터는 확신만 보면 된다.
   */
  /**
   * 자동 촬영이 왜 안 눌리는지 화면에서 읽기 위한 진단값(dev APK 전용 표시).
   * 합의가 안 차면 유사도/카드 흔들림, 선명도가 30 미만이면 셔터 히트가 안 쌓임.
   * (가이드 정렬 게이트는 2026-08-20 제거 — 남은 게이트는 이 둘뿐이다.)
   */
  const [autoDebug, setAutoDebug] = useState<{
    vote: number;
    sim: number;
    sharp: number | null;
    /** 셔터 게이트가 열렸는가, 아니면 무엇에 막혔는가 (gateReasonRef) */
    gate: "open" | "busy" | "hidden" | "cooldown";
    /** 연속 확정 실패 수 — 백오프 배수의 근거 */
    fail: number;
  } | null>(null);
  /** 마지막 확정 시도의 판정 이유 — "왜 안 담겼는지"를 화면에서 읽는다 (진단줄 표시) */
  const [confirmDebug, setConfirmDebug] = useState<string | null>(null);
  /**
   * 스틸 진단 (2026-08-26) — 촬영 해상도와 소요를 화면에 띄운다. iOS 는 기기 로그를
   * CLI 로 못 뽑아서(이 macOS 의 `log stream` 에 --device-name 이 없다) 화면이 유일한
   * 관측 창구인데, 마침 두 값 다 captureAndCrop 이 이미 돌려주고 있다.
   * 답해야 할 질문 둘: ① iOS 스틸이 720p 를 넘었는가 ② 멈춤의 몇 %가 스틸 분석인가.
   */
  const [stillDebug, setStillDebug] = useState<string | null>(null);
  /** 라이브(분석 버퍼) 해상도 — 검출 이벤트의 frameW/H */
  const [liveDebug, setLiveDebug] = useState<string | null>(null);
  const detectSamplesRef = useRef<Array<{ at: number; ms: number }>>([]);
  const lastLiveDebugAtRef = useRef(0);
  /** LK 추적 프레임(label="tracked") 수 — 디버그 박스 표시용, 1초 창마다 리셋 */
  const trackedFrameCountRef = useRef(0);
  const stableHintAtRef = useRef(0);
  const stableHintSerlnoRef = useRef<number | null>(null);
  /** 프레임 합의 집계 — 같은 top-1 이 연속으로 나온 횟수 */
  const voteSerlnoRef = useRef<number | null>(null);
  const voteCountRef = useRef(0);
  /** 진단용 — 마지막 힌트/선명도. 인식 정보 패널이 노출한다 */
  const lastHintRef = useRef<{ serlno: number; sim: number; matched: boolean } | null>(null);
  const lastSharpnessRef = useRef<number | null>(null);
  /** 매칭 진행도(0~1) — 스캔 모드 브래킷 시각화 (design SC-003 이식) */
  const scanProgressRef = useRef(0);
  /** --color-accent 캐시 — 프레임마다 getComputedStyle 을 부르지 않게 */
  const accentColorRef = useRef<string | null>(null);
  /** 락온 목표 quad (캔버스 backing px, flat 8) — 검출 sink 가 갱신, rAF 루프가 소비 */
  const lockTargetRef = useRef<{ pts: number[]; at: number } | null>(null);
  /**
   * 최근 검출 quad 의 화면 가시성 (lockTargetAcceptable 판정) — 표시(브래킷)만
   * 거르면 "브래킷은 거부하는데 확정은 되는" 어긋남이 남는다 (2026-08-25 실기기
   * 제보: 화면 밖 코너 카드가 2점 선으로 그려진 채 레일까지 올라감). 스캔 모드
   * 자동확정(투표 게이트·OCR 즉시 확정·약확신 구제)도 이 값을 본다.
   */
  const quadVisibleRef = useRef<{ ok: boolean; at: number } | null>(null);
  /** 락온 표시 quad — 매 프레임 목표를 향해 LOCK_LERP 보간 (없으면 목표에서 시작) */
  const lockDisplayRef = useRef<number[] | null>(null);
  /** 목표 갱신 간격 중앙값(ms) — 검출 주기에 보간 속도를 맞춘다 (rAF 루프 주석) */
  const lockTargetGapRef = useRef(0);
  const lockTargetGapsRef = useRef<number[]>([]);
  /** 브래킷 채움 표시값(0~1) — 힌트 틱(≈1Hz)의 계단이 아니라 차오르게 보간 */
  const lockProgressRef = useRef(0);
  /**
   * 현재 타깃 객체의 연속 검출 프레임 수 (2026-08-27 — 객체 단위 브래킷 상태).
   * 타깃이 **다른 객체**로 바뀌면 1로 리셋되고, 2 이상이어야 색·채움이 활성된다 —
   * "새 카드로 넘어가면 흰색으로 갔다가 다시 잡히며 차오른다"(사용자 스펙).
   */
  const targetStreakRef = useRef(0);
  /**
   * 검출 단절 재장전 이후 확정 스틸들에서 **담겼거나 중복으로 걸러진** 카드들(누적) — 카드 전환 재장전
   * (REARM_*)의 비교 기준이자 다음 스틸의 중복 적재 가드(2026-08-26 다중 확정).
   * 스윕에서 화면이 겹치면 직전 스틸의 카드가 다음 스틸에도 잡히는데, 그건 같은
   * 실물이라 다시 담으면 안 된다. 반대로 검출 단절(LOST_ARM_MS) 재장전은 카드를
   * 치웠다는 뜻이라 이 집합을 비운다 — 같은 카드 재적재(2장 소유, FN-026)는 그
   * 경로로만 허용된다.
   */
  const lastConfirmedSerlnosRef = useRef<Set<number>>(new Set());
  /** 카드 전환 재장전 합의 — 다른 top-1 이 연속으로 나온 횟수 */
  const rearmVoteRef = useRef<{ serlno: number; count: number } | null>(null);
  /** 약확신 지속 추적 — 스틸 구제 트리거 (WEAK_RESCUE_* 주석 참조) */
  const weakStreakRef = useRef<{ serlno: number; count: number } | null>(null);
  /** 이 카드로 이미 스틸 구제를 시도했는가 — 재장전까지 재시도 금지 */
  const rescueTriedRef = useRef<number | null>(null);
  /** 고해상 재판정(에스컬레이션)을 이번 사이클에 이미 시도했는가 — 재장전까지 1회 */
  const escalationTriedRef = useRef<boolean>(false);
  /** 마지막 확정(disarm) 시각 — 재장전 타임아웃(REARM_TIMEOUT_MS) 기준 */
  const disarmedAtRef = useRef(0);
  /**
   * 확정 당시 카드의 위치(정규화 코너) — 기하 재장전 기준 (2026-08-27). 검출 위치가
   * 이 자리에서 크게 벗어나면(2프레임 연속) 카드를 옮긴 것이므로 힌트 정체 판별
   * (1Hz×2틱 = 2~4초)을 기다리지 않고 즉시 재장전한다 — 흰 브래킷이 새 카드 위에서
   * 오래 머물던 체감 문제의 해소. 같은 카드 중복 적재는 가드 집합이 계속 막는다.
   */
  const guardQuadRef = useRef<Array<{ x: number; y: number }> | null>(null);
  const guardMissRef = useRef(0);
  /**
   * 엔진이 타깃을 **다른 객체로 넘긴** 마지막 시각 (풀 검출 프레임 `quads[0].label ===
   * "card-new"`, 2026-09-08). 카메라를 팬해 새 카드가 확정 자리에 들어오면 기하 재장전
   * (위치 이탈)은 못 가르고 힌트 2틱·타임아웃 4s 만 남아 브래킷이 흰색으로 머물렀다.
   * 마지막 자동 스틸 이후에 전환이 있었으면 즉시 재장전한다 — 판정 도중에 넘어간
   * 전환도 놓치지 않도록 기준을 disarm 시각이 아니라 촬영 시각에 둔다.
   */
  const targetSwitchAtRef = useRef(0);
  const escalateConfirmRef = useRef<() => Promise<void>>(async () => {});
  /**
   * 마지막 자동 스틸 시각 — 쿨다운 기준 (AUTO_STILL_COOLDOWN_MS 주석 참조).
   * ⚠ 초기값 0 은 "아주 오래 전"이 아니라 **performance.now() 원점**이라, 세션이
   * 시작되고 쿨다운(1.5초)이 지나기 전에는 게이트가 닫힌 채였다. 첫 카드가 늦게
   * 잡히는 형태로만 드러나 원인이 멀다 — 무한 과거로 둔다.
   */
  const lastAutoStillAtRef = useRef(Number.NEGATIVE_INFINITY);
  /**
   * 마지막 자동 확정이 **끝난** 시각 — 백오프 쿨다운 기준(AUTO_STILL_COOLDOWN_MS).
   * 촬영 시작(lastAutoStillAtRef)과 나눠 두는 이유는 둘의 의미가 다르기 때문이다:
   * 시작 시각은 촬영 폭주를 막는 바닥값이고, 끝 시각은 "판정하고 나서 얼마나 쉬는가"다.
   */
  const lastJudgeEndAtRef = useRef(Number.NEGATIVE_INFINITY);
  /** 연속 확정 실패 횟수 — 쿨다운 백오프 배수. 확정 성공·재장전에서 0 으로 */
  const autoStillFailRef = useRef(0);
  /**
   * 자동 촬영 게이트가 **왜 닫혔는가** (2026-09-07) — 브래킷이 90% 에서 멎는 이유가
   * 하나가 아닌데 표시가 전부 같아 "다 찼는데 안 찍힌다"로 읽혔다. 사용자가 고칠 수
   * 있는 원인(hidden = 카드가 화면에 다 안 들어옴)만 색으로 구분한다 — 나머지
   * (busy·cooldown)는 기다리면 되는 것이라 종전대로 액센트 90% 유지.
   */
  const gateReasonRef = useRef<{
    reason: "open" | "busy" | "hidden" | "cooldown";
    at: number;
  }>({ reason: "open", at: 0 });
  const handleCaptureRef = useRef<() => Promise<void>>(async () => {});
  const showNoticeRef = useRef<(msg: string) => void>(() => {});
  const tRef = useRef<(k: string) => string>((k) => k);
  useEffect(() => {
    if (!isNative()) return;
    let removed = false;
    let handle: { remove: () => Promise<void> } | null = null;
    const handleLiveHint = async (ev: LiveMatchHintEvent) => {
      // 판정 중(촬영·확정)에는 새 힌트를 **무시**한다 (2026-08-26 — 한 장을 판정하는
      // 동안 다른 카드 판단으로 넘어가지 않는다는 원칙). 엔진 pause 와 이중 방어이고,
      // 투표·재장전 상태는 리셋하지 않고 그대로 얼려 둔다 — 판정이 끝나면 이어진다.
      if (!isLabelModeRef.current && confirmBusyRef.current) return;
      // 정지(§4.1 소진 오버레이 / 요약·상세가 위에 덮임) — 투표·확정·구제 전부 중지
      // (엔진 pause 와 이중 방어 — pause 직전에 떠난 이벤트가 도착할 수 있다)
      if (scanFrozenRef.current) {
        voteSerlnoRef.current = null;
        voteCountRef.current = 0;
        scanProgressRef.current = 0;
        setAutoDebug(null); // 낡은 sim 이 화면에 남아 "계속 도는 중"으로 읽히지 않게
        return;
      }
      let entries = ev.matches;
      let matchedLike = ev.matched;
      let ocrResolved = false;
      // 화면 가시성 게이트 (스캔 모드 전용 — quadVisibleRef 주석 참조). 라벨링
      // 모드는 sink 가 이 값을 갱신하지 않으므로 항상 통과시킨다.
      const visible = quadVisibleRef.current;
      const quadVisible =
        isLabelModeRef.current ||
        (visible !== null &&
          visible.ok &&
          performance.now() - visible.at <= QUAD_VISIBLE_MAX_AGE_MS);
      // 라이브 OCR 교차검증 (2026-08-24) — 확신 미달 힌트에 엔진이 실어 보낸 OCR 로
      // still 확정과 같은 규칙(번호 유일 일치 / 이름+번호)을 적용해 재정렬·확정한다.
      // 임베딩과 독립인 강신호라 확정 시 vote 를 기다리지 않는다 (최종 권위는 여전히
      // still confirm — 거기서도 같은 OCR 재검증을 거친다).
      // matched(0.35+)여도 자동확정 게이트(0.5) 미만이면 번호로 확정 기회를 준다 —
      // 토플로더가 몰리는 0.35~0.5 약확신 사각지대 (2026-08-25 실기기 제보)
      const weakTop = entries.length > 0 && entries[0].similarity < AUTO_CAPTURE_MIN_SIM;
      if (OCR_ASSIST_ENABLED && (!ev.matched || weakTop) && ev.ocrLines && ev.ocrLines.length > 0 && entries.length > 0) {
        try {
          // 사이드카(card_meta.bin)가 실린 릴리스면 엔진이 이름·번호를 함께 보내므로
          // **네트워크를 타지 않는다**. 종전엔 이 await 가 1Hz 힌트 틱 안에 들어 있어,
          // 전파가 나쁘면 catch 로 떨어져 교차검증이 통째로 건너뛰어졌다.
          const local = crossCheckCardsFrom(entries);
          const cards =
            local.size === entries.length
              ? local
              : await fetchCardsCached(entries.map((m) => m.cardSerlno));
          const canonical = extractCardNumbers(ev.ocrLines);
          if (canonical.size > 0) {
            const raw = extractRawCardNumbers(ev.ocrLines);
            const checked = crossCheckMatches(entries, cards, canonical, raw, ev.ocrLines);
            const both = [...checked.confirmedSerlnos].filter((sn) =>
              checked.nameConfirmedSerlnos.has(sn),
            );
            const top0 = checked.matches[0]?.cardSerlno;
            const nameNumber = both.length === 1 && both[0] === top0;
            const numberOnly =
              !nameNumber &&
              checked.confirmedSerlnos.size === 1 &&
              top0 !== undefined &&
              checked.confirmedSerlnos.has(top0);
            if (nameNumber || numberOnly) {
              entries = checked.matches;
              matchedLike = true;
              ocrResolved = true;
            }
          }
        } catch (err) {
          nativeWarn("scan:hintOcr", err);
        }
      }
      const top = entries.length > 0 ? entries[0] : null;
      lastHintRef.current = top
        ? { serlno: top.cardSerlno, sim: top.similarity, matched: matchedLike }
        : null;
      // 카드 전환 재장전 — REARM_* 상수 주석 참조 (검출 단절 없이 다음 장으로 스윕)
      if (!isLabelModeRef.current && !scanArmedRef.current) {
        if (
          top &&
          top.similarity >= REARM_MIN_SIM &&
          !lastConfirmedSerlnosRef.current.has(top.cardSerlno)
        ) {
          if (rearmVoteRef.current?.serlno === top.cardSerlno) {
            rearmVoteRef.current.count += 1;
          } else {
            rearmVoteRef.current = { serlno: top.cardSerlno, count: 1 };
          }
          if (rearmVoteRef.current.count >= REARM_VOTE) {
            rearmVoteRef.current = null;
            lostSinceRef.current = null;
            rescueTriedRef.current = null;
            escalationTriedRef.current = false;
            weakStreakRef.current = null;
            autoStillFailRef.current = 0;
            setArmed(true);
          }
        } else {
          rearmVoteRef.current = null;
        }
      }
      // 프레임 합의 — 엔진 streak 은 matched 일 때만 오르므로 쓰지 않고 여기서 센다
      // (AUTO_CAPTURE_VOTE 주석 참조). 유사도 하한 미달·후보 없음은 합의를 끊는다.
      // 단 OCR 확정은 유사도와 무관하게 통과 (번호는 임베딩과 독립 신호).
      // 진행 중인 투표는 같은 카드가 KEEP_SIM 이상이면 유지 (히스테리시스 주석 참조).
      // ⚠ 홀더가 top-1 일 필요는 없다 — 같은 그림 재판이 여럿이면(뮤 5장, 라벨 228)
      // 동률(±0.01) 사이에서 top-1 정체가 틱마다 튀어 "같은 카드 연속"이 영영 안
      // 찼다(실기기 sim 0.59 지속인데 미확정). 홀더가 **top-3 안에** KEEP_SIM 으로
      // 남아 있으면 합의를 유지한다 — 어느 재판인지는 스틸 확정(재매칭+OCR 재랭킹)이
      // 가른다. 라이브 투표의 역할은 "이 카드를 계속 보고 있다"의 확인뿐이다.
      const holderEntry =
        voteSerlnoRef.current !== null && voteCountRef.current > 0
          ? entries
              .slice(0, 3)
              .find((m) => m.cardSerlno === voteSerlnoRef.current)
          : undefined;
      const holderHolds =
        holderEntry !== undefined && holderEntry.similarity >= AUTO_CAPTURE_KEEP_SIM;
      const voteEligible =
        top !== null && (top.similarity >= AUTO_CAPTURE_MIN_SIM || holderHolds);
      if (!top || (!ocrResolved && !voteEligible)) {
        voteSerlnoRef.current = null;
        voteCountRef.current = 0;
        scanProgressRef.current = 0;
        setAutoDebug({
          vote: 0,
          sim: top?.similarity ?? 0,
          sharp: lastSharpnessRef.current,
          gate: gateReasonRef.current.reason,
          fail: autoStillFailRef.current,
        });
        // 약확신 스틸 구제 — 게이트 미달로 여기서 계속 리셋되는 카드가 대상이다.
        // ⚠ 이 분기가 return 하므로 구제는 반드시 여기서 발화해야 한다 (2026-08-25
        // 실기기 확인 — 아래쪽에 두면 영영 실행되지 않는다).
        // ⚠ 스틸 우선 구조에서는 발화하지 않는다 — 자동 셔터가 이미 스틸을 찍으므로
        // 여기서 또 찍으면 같은 카드에 촬영이 겹친다(중복 적재·촬영 폭주).
        if (
          !STILL_FIRST_CONFIRM &&
          top &&
          !isLabelModeRef.current &&
          scanArmedRef.current &&
          quadVisible &&
          top.similarity >= WEAK_RESCUE_MIN_SIM
        ) {
          if (weakStreakRef.current?.serlno === top.cardSerlno) {
            weakStreakRef.current.count += 1;
          } else {
            weakStreakRef.current = { serlno: top.cardSerlno, count: 1 };
          }
          if (
            weakStreakRef.current.count >= WEAK_RESCUE_STREAK &&
            rescueTriedRef.current !== top.cardSerlno
          ) {
            rescueTriedRef.current = top.cardSerlno;
            // 스틸 촬영의 AF 펄스가 "갑자기 줌됐다"로 읽힌다 — 의도된 정밀 확인임을 안내
            showNoticeRef.current(tRef.current("scan.rescueChecking"));
            void handleCaptureRef.current();
          }
        } else if (top && weakStreakRef.current?.serlno !== top.cardSerlno) {
          weakStreakRef.current = null;
        }
        return;
      }
      if (top.cardSerlno === voteSerlnoRef.current || holderHolds) {
        // 홀더 유지 — top-1 이 쌍둥이로 튀어도 홀더 카드로 계속 센다
        voteCountRef.current += 1;
      } else {
        voteSerlnoRef.current = top.cardSerlno;
        voteCountRef.current = 1;
      }
      setAutoDebug({
        vote: voteCountRef.current,
        sim: top.similarity,
        sharp: lastSharpnessRef.current,
        gate: gateReasonRef.current.reason,
        fail: autoStillFailRef.current,
      });
      // 화면 가시성 미달이면 확신이 서도 게이트를 열지 않는다 — 브래킷이 거부한
      // 카드가 레일로 올라가는 어긋남 방지 (진행도도 0.9 에 머문다)
      const stable = (voteCountRef.current >= AUTO_CAPTURE_VOTE || ocrResolved) && quadVisible;
      // 매칭 진행 시각화 (design SC-003 — 외곽선 브래킷이 차오른다)
      scanProgressRef.current = stable
        ? 1
        : Math.min(0.9, (voteCountRef.current / AUTO_CAPTURE_VOTE) * 0.9);
      // 약확신 스틸 구제 — 같은 카드가 게이트 미달로 버티면 1080p 스틸로 확정 시도
      if (stable) {
        stableHintAtRef.current = Date.now();
        // 홀더 유지로 확정된 경우 게이트 카드도 홀더 — top-1(쌍둥이)이 아니라
        stableHintSerlnoRef.current = voteSerlnoRef.current ?? top.cardSerlno;
      }
      // 카탈로그 **프리페치** — 캐시 경유(같은 카드가 연속으로 오면 네트워크 0회)라
      // still 확정이 카드 정보를 즉시 쓴다. 표시는 없앴지만 이 예열은 남긴다.
      void fetchCardsCached([top.cardSerlno]).catch((err) =>
        nativeWarn("scan:hintFetch", err),
      );
    };
    void CardEngine.addListener("liveMatchHint", (ev) => {
      void handleLiveHint(ev);
    }).then((h) => {
      if (removed) void h.remove();
      else handle = h;
    });
    return () => {
      removed = true;
      if (handle) void handle.remove();
    };
  }, [fetchCardsCached, setArmed]);

  /** 자동 촬영으로 들어온 사진인지 — 매치까지 자동으로 돌릴지의 신호 */
  const pendingAutoMatchRef = useRef(false);
  /** 라이브↔스틸 불일치로 자동 재촬영한 횟수 (AUTO_CAPTURE_MAX_RETAKE 에서 멈춘다) */
  const autoRetakeRef = useRef(0);
  /**
   * 라벨링 판정 (2026-08-18). 매칭 top-1 이 틀렸을 때 **어느 후보가 정답인지**를
   * 사용자가 직접 고른다 — 종전 수집은 사진·외곽선·matcher 결과만 남아 "이 결과가
   * 맞았는지" 를 알 수 없었다. "none" 은 후보에 정답이 없는 실패 사례(그 자체가 귀한 학습감).
   */
  const [labelVerdict, setLabelVerdict] = useState<
    Record<MatchTarget, number | "none" | null>
  >({ model: null, user: null });
  /** 촬영 조건 태그 — 슬리브/탑로더/빛번짐/기울어짐 (다중) */
  const [labelConditions, setLabelConditions] = useState<ScanLabelCondition[]>([]);
  const [labelUploading, setLabelUploading] = useState(false);
  /** 이번 사진이 자동 촬영인지 수동 셔터인지 — 라벨에 그대로 실린다 */
  const captureModeRef = useRef<"auto" | "manual">("manual");
  /**
   * 촬영 순간의 라이브 신호 — 수동 촬영이면 "자동이 왜 안 됐는지"의 측정값이 된다.
   * enterEditing 에서 한 번 찍어두고 라벨에 그대로 싣는다(계약: ScanLabelMeta.liveSignal).
   */
  const liveSignalRef = useRef<{
    vote: number;
    topSim: number | null;
    sharpness: number | null;
    alignStatus: "searching" | "align" | "holding";
    hadStableHint: boolean;
  } | null>(null);
  /** autoStatus 최신값 — enterEditing 이 useCallback 이라 클로저가 낡는 것을 피한다 */
  const autoStatusRef = useRef<"searching" | "align" | "holding">("searching");
  /** 이번 사진이 판정 프레임(takeHintFrame)인지 — 폴백(새 스틸)이면 false */
  const photoFromHintFrameRef = useRef(false);
  /** 촬영 시점의 라이브 힌트 top-1 — 스틸 매치 결과와 교차 확인 */
  const liveTopAtCaptureRef = useRef<number | null>(null);

  /** 촬영 결과 → 라벨링 화면 진입 (자동/수동 공용) */
  const enterEditing = useCallback(
    (p: AutoCapturePayload, opts?: { autoMatch?: boolean }) => {
      const modelPts =
        p.modelCorners && p.modelCorners.length === 4
          ? outlineFromCorners(
              p.modelCorners.map((c) => ({ x: c.x * p.width, y: c.y * p.height })),
            )
          : null;
      labelKeyRef.current = crypto.randomUUID();
      setPhoto({
        path: p.path,
        src: Capacitor.convertFileSrc(p.path.replace(/^file:\/\//, "")),
        width: p.width,
        height: p.height,
      });
      setModelOutline(modelPts);
      // 사용자 라벨링 선 초기값 = 모델 기준선 복제. 모델이 없으면 **가이드 박스**를
      // 시드로 — cover 표시에서 화면 안(가이드 위치)에 정확히 놓인다.
      const guideSeed = computeCardGuide(
        canvasRef.current?.clientWidth || window.innerWidth,
        canvasRef.current?.clientHeight || window.innerHeight,
        p.width,
        p.height,
      ).map((c) => ({ x: c.x * p.width, y: c.y * p.height }));
      setOutline(
        modelPts ? modelPts.map((pt) => ({ ...pt })) : outlineFromCorners(guideSeed),
      );
      setModelResult(null);
      setUserResult(null);
      setOcrInfo(null);
      setInfoCollapsed(true);
      // 자동 촬영 경로만 매치까지 자동 — 수동 셔터는 종전대로 라벨링 후 버튼이다
      // (수동은 "직접 라벨링하겠다"는 의사표시라 결과 패널을 먼저 띄우지 않는다).
      pendingAutoMatchRef.current = opts?.autoMatch === true;
      captureModeRef.current = opts?.autoMatch === true ? "auto" : "manual";
      // 촬영 순간의 신호를 박아둔다 — 나중엔 복원할 수 없는 값들이다
      liveSignalRef.current = {
        vote: voteCountRef.current,
        topSim: lastHintRef.current?.sim ?? null,
        sharpness: lastSharpnessRef.current,
        alignStatus: autoStatusRef.current,
        hadStableHint:
          Date.now() - stableHintAtRef.current <= AUTO_CAPTURE_HINT_MAX_AGE_MS,
      };
      setLabelVerdict({ model: null, user: null });
      setLabelConditions([]);
      // 라이브↔스틸 교차 확인용 스냅샷. 게이트를 통과해 찍힌 자동 경로에서는 값이
      // 있고, 수동 촬영이면 최근 확신 힌트가 없을 수 있어 null 이 정상이다.
      liveTopAtCaptureRef.current =
        Date.now() - stableHintAtRef.current <= AUTO_CAPTURE_HINT_MAX_AGE_MS
          ? stableHintSerlnoRef.current
          : null;
      setViewMode("editing");
      void stop(); // 카메라 내리기 — 다시 찍기에서 retry()
    },
    [stop],
  );

  /**
   * 자동 촬영 게이트 — **찍을 만한 프레임인가**만 본다 ([STILL_FIRST_CONFIRM]).
   * 어떤 카드인지는 촬영한 스틸이 판정하므로 매칭 합의를 기다리지 않는다.
   *
   * ⚠ 종전에는 여기가 매칭 확신 게이트였고 그것이 촬영 폭주도 함께 막고 있었다
   * (fail-closed — 힌트를 못 받으면 자동 촬영 없음). 그 방어가 사라진 자리를
   * **바닥 간격 + 실패 백오프**(AUTO_STILL_MIN_GAP_MS · AUTO_STILL_COOLDOWN_MS)가
   * 대신한다. 빼면 인식 안 되는 물체를 대고 있을 때 셔터가 끝없이 눌린다.
   *
   * 닫힐 때는 이유를 [gateReasonRef] 에 남긴다 — 브래킷이 90% 에서 멎는 까닭이
   * 셋(busy·cooldown·hidden)인데 표시가 하나뿐이면 원인을 못 읽는다.
   */
  const autoCaptureGate = useCallback(() => {
    if (!STILL_FIRST_CONFIRM) {
      return Date.now() - stableHintAtRef.current <= AUTO_CAPTURE_HINT_MAX_AGE_MS;
    }
    // 확정(판정) 진행 중에는 다음 촬영을 받지 않는다 (2026-08-27 사용자 결정) —
    // 쿨다운(1.5s)이 확정 소요(~1.6s)보다 짧아 확정 도중 다음 스틸이 발화했고,
    // 그 촬영이 CPU 를 뺏어 확정을 늦춘 뒤 busy 가드에서 버려지는 순수 낭비였다.
    // 큐는 원래 없다 — 이 게이트는 "찍는 것" 자체를 막는다.
    if (confirmBusyRef.current) {
      gateReasonRef.current = { reason: "busy", at: performance.now() };
      return false;
    }
    // 스틸 우선 구조 — "무엇인지"는 스틸이 판정하므로 여기서 매칭을 기다리지 않는다.
    // 남는 조건은 **찍을 만한 프레임인가** 뿐: 검출 안정성·선명도는 useAutoShutter 가
    // 이미 보고(AUTO_SHUTTER_REQUIRED_HITS + AUTO_SHUTTER_MIN_SHARPNESS), 여기서는
    // 화면 가시성과 쿨다운을 본다.
    // ⚠ 라벨링 모드는 가시성 검사를 건너뛴다 — 검출 sink 가 스캔 모드에서만
    // quadVisibleRef 를 갱신하므로(drawDetectionDots) 여기서 요구하면 라벨링 자동
    // 셔터가 영영 안 눌린다. handleLiveHint 의 quadVisible 도 같은 예외를 둔다.
    if (!isLabelModeRef.current) {
      const visible = quadVisibleRef.current;
      if (
        visible === null ||
        !visible.ok ||
        performance.now() - visible.at > QUAD_VISIBLE_MAX_AGE_MS
      ) {
        gateReasonRef.current = { reason: "hidden", at: performance.now() };
        return false;
      }
    }
    // 바닥값(촬영 시작 기준) — 확정으로 이어지지 않은 촬영이 연달아 나는 것만 끊는다
    const now = performance.now();
    if (now - lastAutoStillAtRef.current < AUTO_STILL_MIN_GAP_MS) {
      gateReasonRef.current = { reason: "cooldown", at: now };
      return false;
    }
    // 실패 백오프(판정 종료 기준) — 첫 실패는 유예라 배수가 0 이다(AUTO_STILL_COOLDOWN_MS 주석)
    const backoff = Math.max(
      0,
      Math.min(autoStillFailRef.current, AUTO_STILL_MAX_BACKOFF) - 1,
    );
    if (now - lastJudgeEndAtRef.current < AUTO_STILL_COOLDOWN_MS * backoff) {
      gateReasonRef.current = { reason: "cooldown", at: now };
      return false;
    }
    gateReasonRef.current = { reason: "open", at: now };
    return true;
  }, []);

  /**
   * 자동 촬영의 캡처 — [STILL_FIRST_CONFIRM] 이면 **항상 진짜 스틸**을 찍는다.
   * 고해상 프레임에서 컬렉터 번호 OCR 을 살리는 것이 이 구조의 존재 이유라,
   * 판정 프레임(takeHintFrame)을 쓰면 얻는 게 없다.
   *
   * ⚠ 그 판정 프레임 우선 경로는 2026-08-17 에 "게이트가 열린 뒤 새 스틸을 찍는 사이
   * 손이 움직여 인식된 장면이 아닌 사진이 찍힌다"는 제보로 들어온 것이다. 지금은
   * **촬영 전에 확신을 세우지 않으므로** 그 어긋남 자체가 성립하지 않는다 — 찍힌
   * 장면이 곧 판정 대상이다. 아래 false 분기는 종전 구조 비교용으로 남긴다.
   * ⚠ 반환 형태를 captureAndCrop 과 맞춰 useAutoShutter 가 분기 없이 쓰게 한다.
   */
  const captureForAuto = useCallback(async (): Promise<CaptureAndCropResult | null> => {
    if (STILL_FIRST_CONFIRM && !PHOTO_STILL && !isLabelModeRef.current) {
      // 라이브 프레임을 그대로 판정에 쓴다 — 엔진이 붙들어 둔 그 프레임이라
      // "인식한 장면 = 판정한 장면"이 자동으로 성립한다(2026-08-17 이슈가 원천 소거).
      lastAutoStillAtRef.current = performance.now();
      const held = await CardEngine.takeHintFrame({
        maxAgeMs: TAKE_HINT_FRAME_MAX_AGE_MS,
      }).catch((err: unknown) => {
        nativeWarn("scan:takeHintFrame", err);
        return null;
      });
      if (held?.available && held.path && held.width && held.height) {
        photoFromHintFrameRef.current = true;
        setStillDebug(`${held.width}×${held.height} (라이브 프레임) age=${held.ageMs ?? "?"}ms`);
        return {
          detected: (held.corners?.length ?? 0) === 4,
          corners: held.corners,
          cropPath: undefined,
          fullPath: held.path,
          fullWidth: held.width,
          fullHeight: held.height,
          engine: "card-hint-frame",
          elapsedMs: held.ageMs ?? 0,
        } as CaptureAndCropResult;
      }
      // 붙들린 프레임이 없다(힌트가 아직 안 돌았다) — 이번 판은 건너뛴다.
      // 사진으로 폴백하면 iOS 에서 없애려던 셔터음이 그대로 돌아온다.
      return null;
    }
    if (STILL_FIRST_CONFIRM) {
      // 판정 프레임을 쓰지 않는다 — 고해상 스틸이 이 구조의 존재 이유다.
      photoFromHintFrameRef.current = false;
      lastAutoStillAtRef.current = performance.now();
      setHintPaused(true);
      const startedAt = performance.now();
      try {
        const shot = await capture();
        if (shot) {
          // elapsedMs = 네이티브 촬영+재검출+warp, wall = FE 왕복까지 포함한 실제 공백
          setStillDebug(
            `${shot.fullWidth ?? "?"}×${shot.fullHeight ?? "?"} native=${
              shot.elapsedMs ?? "?"
            }ms wall=${Math.round(performance.now() - startedAt)}ms`,
          );
        }
        // 확정으로 이어지지 않는 경우(busy·실패)는 여기서 되돌린다 —
        // 이어지면 confirmScan 의 finally 가 푼다
        if (!shot) setHintPaused(false);
        return shot;
      } catch (err) {
        setHintPaused(false);
        throw err;
      }
    }
    if (isNative()) {
      const held = await CardEngine.takeHintFrame({
        maxAgeMs: TAKE_HINT_FRAME_MAX_AGE_MS,
      }).catch((err: unknown) => {
        nativeWarn("scan:takeHintFrame", err);
        return null;
      });
      if (held?.available && held.path && held.width && held.height) {
        photoFromHintFrameRef.current = true;
        return {
          detected: (held.corners?.length ?? 0) === 4,
          corners: held.corners,
          cropPath: undefined,
          fullPath: held.path,
          fullWidth: held.width,
          fullHeight: held.height,
          engine: "card-hint-frame",
          elapsedMs: held.ageMs ?? 0,
        } as CaptureAndCropResult;
      }
    }
    photoFromHintFrameRef.current = false;
    return capture();
  }, [capture, setHintPaused]);

  const {
    status: autoStatus,
    handleDetection,
    progressRef: shutterProgressRef,
    resetHits: shutterResetHits,
  } = useAutoShutter({
    enabled: showLive && state === "active",
    autoFire:
      AUTO_SHUTTER_FIRE && !scanFrozen && (isLabelMode || (scanArmed && !guestLimitReached)),
    matchGate: autoCaptureGate,
    capture: captureForAuto,
    // 라벨링 모드 = 종전 still 에디터 / 스캔 모드 = 무화면 확정(세션 레일)
    onCaptured: (p) => {
      if (isLabelModeRef.current) enterEditing(p, { autoMatch: true });
      else void confirmScanRef.current(p);
    },
    deleteFile: deletePhotoFile,
    suspendedRef,
  });
  // enterEditing(useCallback) 이 촬영 순간의 정렬 상태를 읽을 수 있게 최신값을 들고 있는다
  autoStatusRef.current = autoStatus;

  /**
   * 라이브 검출 표시.
   * 라벨링 모드 = 코너 점 4개 (위=주황/아래=하늘 — 뒤집힌 카드 라벨링 대비).
   * 스캔 모드 = 락온 브래킷의 **목표만 갱신** — 그리기는 아래 rAF 루프 소유라
   * 여기서 캔버스를 만지면 안 된다 (검출 ~10fps 가 60fps 보간을 지운다).
   */
  const drawDetectionDots = useCallback((ev: DetectionEvent) => {
    const canvas = dotsCanvasRef.current;
    if (!canvas || canvas.width === 0) return;
    let quad = ev.detected && ev.quads.length > 0 ? ev.quads[0] : null;
    // LK 추적 프레임 (엔진 label="tracked", 2026-08-28) — 풀 검출 사이를 잇는
    // **표시 전용** 코너다. 브래킷 목표(lockTargetRef)·보간 간격 중앙값은 갱신하되,
    // 판정에 닿는 상태(가시성 게이트 quadVisibleRef, 객체 연속 streak·셔터 히트
    // 리셋)는 풀 검출 프레임만 만진다 — 판정 경로 무변경 계약.
    const isTracked = quad?.label === "tracked";

    if (!isLabelModeRef.current) {
      // 판정 중(촬영·확정 — 고해상 재판정 포함)에는 **갈아타기만 금지**한다
      // (2026-08-27 재결정): 종전엔 통째로 동결했더니 고해상 촬영 ~6초 동안 카메라를
      // 움직이면 브래킷이 옛 자리에 박혀 "멈췄다"로 보였다. Android 는 촬영 중에도
      // 분석 프레임이 흐르므로, 판정 대상 **그 객체**(점착 IoU 일치)는 계속 따라가고
      // 다른 카드로의 전환만 막는다. 대상이 안 보이면 갱신하지 않는다(rAF hold).
      const judging = capturingRef.current || confirmBusyRef.current;
      // 점착(sticky) 타깃 — 프레임 1등이 아니라 **직전에 잡던 그 객체**를 우선한다.
      // 두 카드가 보일 때 verifier 점수 1등이 프레임마다 튀면 브래킷·힌트가 카드
      // 사이를 오간다. 직전 목표와 bbox 가 겹치는(IoU 최대) quad 가 있으면 그쪽.
      if (quad && lockTargetRef.current && (judging || ev.quads.length > 1)) {
        const t = lockTargetRef.current.pts;
        const tb = {
          x0: Math.min(t[0], t[2], t[4], t[6]),
          x1: Math.max(t[0], t[2], t[4], t[6]),
          y0: Math.min(t[1], t[3], t[5], t[7]),
          y1: Math.max(t[1], t[3], t[5], t[7]),
        };
        let best = 0;
        let bestQuad: (typeof ev.quads)[number] | null = null;
        for (const cand of ev.quads) {
          if (cand.corners.length !== 4) continue;
          const c = mapQuadToView(
            cand.corners,
            { width: ev.frameW, height: ev.frameH },
            { width: canvas.width, height: canvas.height },
            "cover",
          );
          const cb = {
            x0: Math.min(...c.map((p) => p.x)),
            x1: Math.max(...c.map((p) => p.x)),
            y0: Math.min(...c.map((p) => p.y)),
            y1: Math.max(...c.map((p) => p.y)),
          };
          const ix = Math.max(0, Math.min(tb.x1, cb.x1) - Math.max(tb.x0, cb.x0));
          const iy = Math.max(0, Math.min(tb.y1, cb.y1) - Math.max(tb.y0, cb.y0));
          const inter = ix * iy;
          const union =
            (tb.x1 - tb.x0) * (tb.y1 - tb.y0) + (cb.x1 - cb.x0) * (cb.y1 - cb.y0) - inter;
          const iou = union > 0 ? inter / union : 0;
          if (iou > best) {
            best = iou;
            bestQuad = cand;
          }
        }
        if (best >= 0.2 && bestQuad) {
          quad = bestQuad;
        } else if (
          judging &&
          // 판정 중 유지창은 짧게(0.4s) — 실측(2026-08-27): 판정 1.6초 동안 검출은
          // 0.4~0.6s 간격으로 계속 오는데, 카메라를 움직이면 프레임 사이 이동폭이
          // 커서 IoU 연결이 끊기고, 1.2s 유지가 "확정 순간 내내 멈춤"으로 보였다.
          // 0.4s = 검출 한 프레임 분량의 가림만 흡수하고 즉시 새 위치를 따라간다.
          performance.now() - lockTargetRef.current.at <= 400
        ) {
          // 짧은 가림·블러는 유지(hold). 그 이상 타깃이 안 보이면 — 카메라를 옮긴
          // 것이다 — 판정 중이라도 새 top-1 을 따라간다 (2026-08-27 제보: 판정
          // 계산 동안 브래킷이 옛 자리에 무기한 박혀 있었음. 판정은 이미 찍힌
          // 스틸에서 계속 돌고, 결과는 레일로 온다 — 브래킷이 남을 이유가 없다).
          quad = null;
        }
      }
      // 브래킷은 재장전 상태와 무관하게 **항상** 검출 카드를 따라간다 (2026-08-25
      // 재결정 — 확정 후 가이드 복귀 연출은 "인식되는데 안 움직인다"로 두 번
      // 오독됐다). 상태 구분은 색이 맡는다: 재장전 대기 = 흰색, 활성 = 액센트.
      if (quad && quad.corners.length === 4 && !scanFrozenRef.current) {
        const pts = mapQuadToView(
          quad.corners,
          { width: ev.frameW, height: ev.frameH },
          { width: canvas.width, height: canvas.height },
          "cover",
        );
        // 확정 게이트(strict)와 표시(관대)를 분리 — lockTargetAcceptable 주석 참조
        const strictOk = lockTargetAcceptable(pts, canvas.width, canvas.height, 0.01);
        const displayOk =
          strictOk || lockTargetAcceptable(pts, canvas.width, canvas.height, 0.1);
        if (!isTracked) quadVisibleRef.current = { ok: strictOk, at: performance.now() };
        if (displayOk) {
          // 객체 연속성 (targetStreakRef 주석) — 직전 목표와 bbox 가 이어지면 연속,
          // 끊기면(다른 카드) 스트릭 1 + 셔터 히트 리셋 → 브래킷이 흰색·빈 채움으로
          // 돌아갔다가 새 객체 위에서 다시 차오른다.
          // 추적 프레임은 정의상 같은 객체의 연속이라 streak·히트 리셋을 만지지
          // 않는다 — 새 객체 판정(연속 2프레임)은 풀 검출 프레임 단위 그대로.
          if (!isTracked) {
            const prevPts = lockTargetRef.current?.pts ?? null;
            if (prevPts) {
              const ax0 = Math.min(prevPts[0], prevPts[2], prevPts[4], prevPts[6]);
              const ax1 = Math.max(prevPts[0], prevPts[2], prevPts[4], prevPts[6]);
              const ay0 = Math.min(prevPts[1], prevPts[3], prevPts[5], prevPts[7]);
              const ay1 = Math.max(prevPts[1], prevPts[3], prevPts[5], prevPts[7]);
              const bx0 = Math.min(...pts.map((c) => c.x));
              const bx1 = Math.max(...pts.map((c) => c.x));
              const by0 = Math.min(...pts.map((c) => c.y));
              const by1 = Math.max(...pts.map((c) => c.y));
              const ix = Math.max(0, Math.min(ax1, bx1) - Math.max(ax0, bx0));
              const iy = Math.max(0, Math.min(ay1, by1) - Math.max(ay0, by0));
              const inter = ix * iy;
              const union = (ax1 - ax0) * (ay1 - ay0) + (bx1 - bx0) * (by1 - by0) - inter;
              if (union > 0 && inter / union >= 0.2) {
                targetStreakRef.current += 1;
              } else {
                targetStreakRef.current = 1;
                shutterResetHits();
              }
            } else {
              targetStreakRef.current = 1;
              shutterResetHits();
            }
          }
          const nowAt = performance.now();
          // 목표 갱신 간격의 **중앙값** — 검출이 느린 기기에서 브래킷 보간을 늦추는
          // 근거. ⚠ EMA 를 썼다가 폴드7 회귀(2026-08-26): 빠른 기기도 verifier 가
          // 프레임을 몇 개 떨어뜨리면(글레어·슬리브) 그 끊김 한 번에 평균이 확 올라
          // 브래킷이 구형 기기처럼 굼떠졌다. 중앙값은 산발적 끊김을 무시하고
          // **지속적인** 검출 속도만 반영한다.
          const prevAt = lockTargetRef.current?.at ?? 0;
          if (prevAt > 0) {
            const gap = Math.min(2000, nowAt - prevAt);
            const gaps = lockTargetGapsRef.current;
            gaps.push(gap);
            if (gaps.length > 9) gaps.shift();
            const sorted = [...gaps].sort((a, b) => a - b);
            lockTargetGapRef.current = sorted[sorted.length >> 1];
          }
          lockTargetRef.current = {
            pts: pts.flatMap((p) => [p.x, p.y]),
            at: nowAt,
          };
        }
      }
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!quad || quad.corners.length !== 4) return;
    const pts = mapQuadToView(
      quad.corners,
      { width: ev.frameW, height: ev.frameH },
      { width: canvas.width, height: canvas.height },
      "cover",
    );
    const dpr = window.devicePixelRatio || 1;
    pts.forEach((p, i) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, DOT_R * dpr, 0, Math.PI * 2);
      ctx.fillStyle = isTopCornerIndex(i) ? CORNER_TOP_COLOR : CORNER_BOTTOM_COLOR;
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 2 * dpr;
      ctx.stroke();
    });
  }, []);

  /**
   * 락온 브래킷 루프 (스캔 모드, 2026-08-25) — 가이드 자리에 대기하던 코너 브래킷이
   * 검출되면 카드로 날아가 따라붙고(시간 기반 lerp — LOCK_* 주석), 놓치면 0.6초
   * 유지 후 가이드로 복귀한다. 진행 브래킷(design SC-003)의 성장·액센트 규칙은
   * 추적 중에만 적용 — 대기 브래킷은 흰색·기본 길이의 "가이드"다.
   */
  useEffect(() => {
    if (!(showLive && state === "active" && !isLabelMode)) return;
    let raf = 0;
    let last = 0;
    // ── 정지 프레임 스킵 (2026-08-31 발열 라운드) ──────────────────────────
    // 실측(Note 9, 스캔 중 카드 없음): 앱 CPU 207% 중 **웹뷰·GPU 가 94%** 로 엔진
    // 분석 스레드(95%)와 맞먹었다. 스캔을 닫으면 같은 앱이 0~13% 이므로 전부 이
    // 화면 몫이다. 이 루프가 카드가 없어 아무것도 안 움직이는 동안에도 매 vsync
    // 전체 캔버스를 지우고 브래킷을 다시 그어 왔다 — 그릴 때마다 합성 프레임이
    // 새로 올라가고 RenderThread·mali·Viz 가 깨어난다.
    //
    // 그려질 그림을 결정하는 값은 여섯 개뿐이다: display 8좌표 · 채움 진행도 ·
    // 색 · tracking(흐린 윤곽 유무·투명도) · 캔버스 크기. 전부 직전 프레임과
    // 같으면 캔버스에 이미 그 그림이 그대로 있으므로 다시 그릴 이유가 없다.
    // **애니메이션 상태 갱신(lerp)은 건너뛰지 않는다** — 그리기만 생략한다.
    //
    // ⚠ 스캔 모드에서 이 캔버스의 소유자는 이 루프 하나다(검출 sink 는
    //   lockTargetRef 만 갱신하고 ctx 를 만지지 않는다 — drawDetectionDots 의
    //   early-return). 라벨링 모드는 이 이펙트가 아예 안 돈다. 그래서 "안 지우면
    //   직전 그림이 남아 있다"가 성립한다.
    // ⚠ canvas.width/height 대입은 캔버스를 지우므로 크기 변화도 비교에 넣는다.
    let drawnW = -1;
    let drawnH = -1;
    let drawnStroke = "";
    let drawnTracking = false;
    let drawnProgress = Number.NaN;
    const drawnPts = new Float64Array(8).fill(Number.NaN);
    // 서브픽셀 — 이보다 작은 이동은 화면에 차이를 만들지 않는다
    const PTS_EPS = 0.05;
    // 0.0005 × 변길이 0.32 배 ≈ 0.1px 미만
    const PROGRESS_EPS = 0.0005;
    // 가이드 기하 캐시 — readGuideBand 는 getBoundingClientRect 를 **2회** 부른다.
    // 대기 상태(=카드 없음)에서 매 프레임 강제 레이아웃 3회를 물던 자리라 60Hz →
    // 4Hz 로 낮춘다. 레이아웃에서 오는 값이라 250ms 지연은 보이지 않고, 캔버스
    // 크기가 바뀌면 즉시 다시 잰다.
    const GUIDE_TTL_MS = 250;
    let guideGoal: number[] | null = null;
    let guideAt = 0;
    let guideW = -1;
    let guideH = -1;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const canvas = dotsCanvasRef.current;
      if (!canvas || canvas.width === 0) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const dt = last === 0 ? 16.7 : Math.min(100, Math.max(1, now - last));
      last = now;

      const target = lockTargetRef.current;
      // 판정 중(촬영·확정)에는 **검출 이벤트가 끊긴다** — iOS 는 photo 촬영이
      // AF/AE 재조정을 돌려 분석 프레임이 잠깐 멎는다. 그동안 LOCK_HOLD_MS 가
      // 만료되면 브래킷이 카드를 떠나 가이드로 날아가는데, 사용자에겐 "찍는 순간
      // 끊긴다"로 읽힌다(2026-08-26 실기기 제보). 판정 중에는 마지막 목표를 그대로
      // 붙들어 "잡아둔 채 판정 중"으로 보이게 한다 — 멈춤이 사고가 아니라 연출이 된다.
      const judging = capturingRef.current || confirmBusyRef.current;
      // 판정 중 유지도 **시간 상한** — 종전 무기한 유지는 카메라를 옮겨도 브래킷이
      // 옛 자리에 남는 문제를 만들었다(2026-08-27). 상한 2×LOCK_HOLD 는 iOS photo
      // 촬영의 분석 프레임 정지(~1초)를 흡수하는 값이다. 타깃이 계속 보이면 검출
      // sink 가 target.at 을 갱신하므로 이 상한에 걸리지 않는다.
      const tracking =
        target !== null && now - target.at <= (judging ? LOCK_HOLD_MS * 2 : LOCK_HOLD_MS);
      let goal: number[];
      if (tracking) {
        goal = target.pts;
      } else {
        if (
          guideGoal === null ||
          canvas.width !== guideW ||
          canvas.height !== guideH ||
          now - guideAt >= GUIDE_TTL_MS
        ) {
          guideGoal = computeCardGuideBox(
            canvas.width,
            canvas.height,
            readGuideBand(canvas, guideBandRef.current),
          ).flatMap((p) => [p.x, p.y]);
          guideAt = now;
          guideW = canvas.width;
          guideH = canvas.height;
        }
        goal = guideGoal;
      }
      let display = lockDisplayRef.current;
      if (!display) {
        display = goal.slice();
        lockDisplayRef.current = display;
      }
      const aligned = alignQuadCyclic(goal, display);
      // 보간 속도를 **목표 갱신 간격**에 맞춘다 (2026-08-26 Note 9 "뚝뚝 끊긴다").
      // LOCK_LERP=0.28 은 검출 ~10fps 전제 — 목표 도달에 ~120ms 라, 검출이 0.5~1초에
      // 한 번인 기기에서는 도착 후 다음 목표까지 멈춰 서서 점프-정지의 반복으로
      // 보였다. 이동 시간을 갱신 간격의 절반쯤으로 잡으면 다음 목표가 올 즈음 도착해
      // 연속 이동으로 읽힌다. 빠른 기기(간격 ≤150ms)는 종전 속도 그대로.
      const targetGap = lockTargetGapRef.current;
      // 이동 시간 상한 220ms (2026-08-27 "느리고 어색" 제보) — 갱신 간격의 절반을
      // 기다리는 종전 방식은 검출 1fps 기기에서 1초 내내 미끄러져 굼떠 보였다.
      // 빠르게 도착해 잠깐 서 있는 쪽이 "따라붙는다"로 읽힌다.
      const tau = targetGap > 150 ? Math.min(220, Math.max(60, targetGap / 3)) : 60;
      const k = 1 - Math.exp(-dt / tau);
      for (let i = 0; i < 8; i++) display[i] += (aligned[i] - display[i]) * k;

      const dpr = window.devicePixelRatio || 1;
      // 채움은 **셔터를 당기는 신호**를 그린다. 스틸 우선 구조에서 셔터를 당기는 것은
      // 검출 히트(≈0.3초)인데 종전에는 힌트 투표(1Hz)를 그리고 있어, 다 차기도 전에
      // 셔터음이 먼저 나고 채움이 뒤따랐다(2026-08-26 제보). 종전 구조에서는 투표가
      // 곧 셔터라 그 값이 맞았다 — 게이트가 바뀌었으니 표시도 따라간다.
      const rawProgress = STILL_FIRST_CONFIRM
        ? shutterProgressRef.current
        : scanProgressRef.current;
      const t = tracking ? Math.min(1, Math.max(0, rawProgress)) : 0;
      if (accentColorRef.current === null) {
        accentColorRef.current =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--color-accent")
            .trim() || "#FA4C14";
      }
      // 색 규칙 (2026-08-25 재결정): 브래킷은 항상 카드를 따라가고 **색이 상태를
      // 말한다** — 재장전 대기(방금 확정, 같은 카드) 추적은 흰색, 활성 추적·판정
      // (스틸 확정·"자세히 확인" 구제)은 액센트. 대기 가이드도 흰색.
      // 새 객체는 2프레임 연속 잡혀야 활성(색·채움) — 그전엔 흰색 (객체 단위 상태)
      //
      // 여기에 **화면 가시성**도 넣는다 (2026-09-07). 카드가 화면 밖으로 걸치면
      // 표시(10% 여유)는 계속 따라가는데 확정 게이트(1% 여유)는 닫혀 있어, 액센트
      // 브래킷이 90% 에서 멎은 채 영영 안 찍히는 상태가 다른 대기(busy·cooldown)와
      // 똑같이 보였다. 이건 사용자가 고칠 수 있는 원인이므로 색으로 가른다 — 흰색
      // (= 아직 대상이 아님)으로 내려 "카드를 화면 안으로"를 말없이 전한다.
      // ⚠ 이유는 검출 프레임에서만 갱신되므로 신선한 값일 때만 신뢰한다.
      const gate = gateReasonRef.current;
      const gateHidden =
        gate.reason === "hidden" && now - gate.at <= QUAD_VISIBLE_MAX_AGE_MS;
      const activeTracking =
        tracking && scanArmedRef.current && targetStreakRef.current >= 2 && !gateHidden;
      const stroke =
        activeTracking || judging ? accentColorRef.current : "rgba(255,255,255,0.95)";
      // 채움 연출 (design SC-003) — 활성 추적 중 투표 진행은 단계대로, 실제 판정이
      // 도는 동안은 가득 채운다. 표시값은 보간이라 계단 없이 변을 타고 차오른다.
      const progressGoal = judging ? 1 : activeTracking ? t : 0;
      // ⚠ 보간 속도는 **채움이 그리는 신호의 속도**에 맞춰야 한다. 0.88(≈0.4초)은
      // 힌트 투표(1Hz)의 계단을 지우려고 고른 값이라, 0.3초에 다 차는 검출 히트를
      // 그리기엔 너무 느리다 — 그 지연만으로도 셔터음이 채움을 앞지른다.
      const fillDecay = STILL_FIRST_CONFIRM ? 0.72 : 0.88;
      lockProgressRef.current +=
        (progressGoal - lockProgressRef.current) * (1 - Math.pow(fillDecay, dt / 16.7));

      // ── 정지 프레임이면 그리지 않는다 (위 "정지 프레임 스킵" 주석) ──
      // 여기까지가 상태 갱신이고, 아래부터가 그리기다. 그림을 정하는 값이 전부
      // 직전에 그린 것과 같으면 캔버스에 이미 그 그림이 있다.
      let same =
        canvas.width === drawnW &&
        canvas.height === drawnH &&
        tracking === drawnTracking &&
        stroke === drawnStroke &&
        Math.abs(lockProgressRef.current - drawnProgress) < PROGRESS_EPS;
      if (same) {
        for (let i = 0; i < 8; i++) {
          if (Math.abs(display[i] - drawnPts[i]) >= PTS_EPS) {
            same = false;
            break;
          }
        }
      }
      if (same) return;
      drawnW = canvas.width;
      drawnH = canvas.height;
      drawnTracking = tracking;
      drawnStroke = stroke;
      drawnProgress = lockProgressRef.current;
      for (let i = 0; i < 8; i++) drawnPts[i] = display[i];

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const pts = [0, 1, 2, 3].map((i) => ({ x: display![i * 2], y: display![i * 2 + 1] }));
      if (tracking) {
        // 흐린 전체 윤곽 — "여기를 보고 있다" (대기 가이드에는 그리지 않는다)
        ctx.save();
        ctx.globalAlpha = 0.28;
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1.5 * dpr;
        ctx.beginPath();
        pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }
      // 모서리 브래킷 — 변 길이 18% 에서 progress 만큼 자라 50% 에서 변이 이어진다
      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.globalAlpha = tracking ? 1 : 0.85;
      ctx.lineWidth = 3 * dpr;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      const ratio = 0.18 + lockProgressRef.current * 0.32;
      for (let i = 0; i < 4; i++) {
        const cur = pts[i];
        const next = pts[(i + 1) % 4];
        const prev = pts[(i + 3) % 4];
        const toward = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
          x: a.x + (b.x - a.x) * ratio,
          y: a.y + (b.y - a.y) * ratio,
        });
        const a = toward(cur, prev);
        const b = toward(cur, next);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(cur.x, cur.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      ctx.restore();
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lockTargetRef.current = null;
      lockDisplayRef.current = null;
      lockProgressRef.current = 0;
      const canvas = dotsCanvasRef.current;
      if (canvas && canvas.width > 0) {
        canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [showLive, state, isLabelMode]);

  detectionSinkRef.current = (ev) => {
    drawDetectionDots(ev);
    // LK 추적 프레임(엔진 label="tracked") — 브래킷 목표 갱신(위)까지만. 판정 경로
    // (자동 셔터 히트·재장전·가시성·배지·검출 속도 통계)는 풀 검출 프레임만 센다.
    if (ev.detected && ev.quads[0]?.label === "tracked") {
      trackedFrameCountRef.current += 1;
      return;
    }
    handleDetection(ev);
    if (ev.detected && ev.quads[0]?.label === "card-new") {
      targetSwitchAtRef.current = performance.now();
    }
    // 스캔 모드 재장전 — 확정 후 검출이 끊겨야(카드를 치워야) 다음 장을 잡는다
    if (!isLabelModeRef.current && !scanArmedRef.current) {
      if (!ev.detected) {
        // 연속 미검출 **시간**으로 판정한다 (LOST_ARM_MS 주석). 첫 빈 이벤트에
        // 시작 시각을 찍고, 검출이 돌아오면 지운다.
        if (lostSinceRef.current === null) lostSinceRef.current = ev.timestampMs;
        if (ev.timestampMs - lostSinceRef.current >= LOST_ARM_MS) {
          setArmed(true);
          lostSinceRef.current = null;
          rescueTriedRef.current = null;
          escalationTriedRef.current = false;
          weakStreakRef.current = null;
          autoStillFailRef.current = 0;
          // 카드를 치웠다 — 중복 가드 해제 (같은 카드 재적재는 이 경로로만 허용,
          // lastConfirmedSerlnosRef 주석 참조. REARM_* 전환 재장전은 유지한다)
          lastConfirmedSerlnosRef.current = new Set();
        }
      } else {
        lostSinceRef.current = null;
        // 기하 재장전 (guardQuadRef 주석) — 검출 위치가 확정 자리에서 이탈 ×2프레임.
        // quads[0] 은 엔진의 **점착 힌트 타깃**(2026-09-07 — 브래킷 카드)이지 verifier
        // 1등이 아니다. 확정 카드가 1등이 아닌 다중 카드 장면에서 가짜 재장전이 나던
        // 구멍을 엔진 순서로 막았다(StickyTarget.kt). 구 엔진 APK 는 1등을 주므로 그
        // 장면에서만 종전 동작.
        let geomRearm = false;
        const guard = guardQuadRef.current;
        const q = ev.quads[0]?.corners;
        if (guard && q && q.length === 4) {
          const bb = (pts: Array<{ x: number; y: number }>) => ({
            x0: Math.min(...pts.map((c) => c.x)),
            x1: Math.max(...pts.map((c) => c.x)),
            y0: Math.min(...pts.map((c) => c.y)),
            y1: Math.max(...pts.map((c) => c.y)),
          });
          const a = bb(guard);
          const b = bb(q);
          const ix = Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0));
          const iy = Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
          const inter = ix * iy;
          const union =
            (a.x1 - a.x0) * (a.y1 - a.y0) + (b.x1 - b.x0) * (b.y1 - b.y0) - inter;
          const iou = union > 0 ? inter / union : 0;
          if (iou < 0.1) {
            guardMissRef.current += 1;
            if (guardMissRef.current >= 2) geomRearm = true;
          } else {
            guardMissRef.current = 0;
          }
        }
        // 재장전 타임아웃 (REARM_TIMEOUT_MS 주석 — sim 이 낮아 전환 재장전이 영영
        // 안 걸리는 데드락 해소). 가드 집합은 유지 — 같은 카드는 여전히 안 담긴다.
        // 타깃 전환 재장전 (targetSwitchAtRef 주석) — 엔진이 다른 객체로 넘어갔다
        const switchRearm = targetSwitchAtRef.current > lastAutoStillAtRef.current;
        if (
          geomRearm ||
          switchRearm ||
          performance.now() - disarmedAtRef.current > REARM_TIMEOUT_MS
        ) {
          setArmed(true);
          guardQuadRef.current = null;
          guardMissRef.current = 0;
          rescueTriedRef.current = null;
          escalationTriedRef.current = false;
          weakStreakRef.current = null;
          autoStillFailRef.current = 0;
        }
      }
    }
    const top = ev.detected && ev.quads.length > 0 ? ev.quads[0] : null;
    if (top) lastSharpnessRef.current = top.sharpness ?? null;
    // 분석 버퍼 해상도 + **검출 속도**. 속도가 필요한 이유: 자동 셔터는 2초 창에
    // 히트 3회를 요구하므로 검출이 ~2fps 아래로 떨어지면 **영영 발화하지 않는다**.
    // 브래킷도 LOCK_HOLD_MS(1.2초)를 넘기면 가이드로 복귀한다 — 구형 기기에서
    // "갔다 풀렸다"로 드러난 그 증상이다(2026-08-26 Note 9).
    // ⚠ setState 는 1초에 한 번만 (검출마다 갱신하면 재렌더가 검출을 더 느리게 한다)
    detectSamplesRef.current.push({ at: ev.timestampMs, ms: ev.elapsedMs });
    if (detectSamplesRef.current.length > 20) detectSamplesRef.current.shift();
    if (ev.timestampMs - lastLiveDebugAtRef.current >= 1000) {
      lastLiveDebugAtRef.current = ev.timestampMs;
      const xs = detectSamplesRef.current;
      const avg = Math.round(xs.reduce((a, b) => a + b.ms, 0) / Math.max(1, xs.length));
      const span = xs.length > 1 ? xs[xs.length - 1].at - xs[0].at : 0;
      const fps = span > 0 ? ((xs.length - 1) * 1000) / span : 0;
      const trk = trackedFrameCountRef.current;
      trackedFrameCountRef.current = 0;
      setLiveDebug(
        `${ev.frameW}×${ev.frameH} · detect ${avg}ms · ${fps.toFixed(1)}fps` +
          (trk > 0 ? ` · trk ${trk}/s` : ""),
      );
    }
  };

  /** 수동 셔터 — 자동과 동일한 라벨링 화면으로 */
  const [capturing, setCapturing] = useState(false);
  /** 락온 루프(rAF)가 "판단 중" 색을 읽기 위한 ref — capturing 과 동기 */
  const capturingRef = useRef(false);
  capturingRef.current = capturing;
  const handleCapture = useCallback(async () => {
    if (state !== "active" || capturing) return;
    suspendedRef.current = true;
    autoRetakeRef.current = 0; // 사용자가 직접 찍었다 — 자동 재촬영 카운터 초기화
    setCapturing(true);
    // 촬영 동안 라이브 힌트 매칭 중지 — 스틸 검출·warp 와 CPU 경합 방지 (엔진이
    // 세션 재시작 시 자동 해제하므로 실패는 무시해도 안전)
    if (isNative()) {
      void CardEngine.setLiveHintPaused({ paused: true }).catch(() => {});
    }
    try {
      // 자동 셔터가 촬영 중이면 capture 가 busy(null) — 잠깐 대기 후 재시도
      let result: CaptureAndCropResult | null = await capture();
      for (let i = 0; !result && i < 15; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        result = await capture();
      }
      if (!result) return;
      if (result.cropPath) deletePhotoFile(result.cropPath);
      if (!result.fullPath || !result.fullWidth || !result.fullHeight) {
        showNotice(t("scan.captureFailed"));
        return;
      }
      const payload = {
        path: result.fullPath,
        width: result.fullWidth,
        height: result.fullHeight,
        modelCorners: result.detected && result.corners ? result.corners : null,
        modelQuads:
          result.detected && result.quads && result.quads.length > 0
            ? result.quads.map((q) => q.corners)
            : null,
      };
      // 스캔 모드의 수동 셔터도 무화면 확정 — 명시적 의사표시라 재장전을 무시한다
      if (isLabelModeRef.current) enterEditing(payload);
      else await confirmScanRef.current(payload, { manual: true });
    } finally {
      suspendedRef.current = false;
      setCapturing(false);
      // 소진 정지 중에는 해제하지 않는다 — 정지 효과(위 scanFrozen)가 소유
      if (isNative() && !scanFrozenRef.current) {
        void CardEngine.setLiveHintPaused({ paused: false }).catch(() => {});
      }
    }
  }, [state, capturing, capture, deletePhotoFile, enterEditing, showNotice, t]);
  handleCaptureRef.current = handleCapture;
  showNoticeRef.current = showNotice;
  tRef.current = t;

  /** 다시 찍기 — 사진·결과 정리 후 라이브 복귀 */
  const handleRetake = useCallback(() => {
    deletePhotoFile(photo?.path);
    setPhoto(null);
    setModelOutline(null);
    setModelResult(null);
    setUserResult(null);
    setOcrInfo(null);
    setViewMode("live");
    retry();
  }, [photo, deletePhotoFile, retry]);

  // 뒤로가기 가드는 리스너를 재등록하지 않고 항상 최신 상태/핸들러를 보게 한다
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  const handleRetakeRef = useRef(handleRetake);
  handleRetakeRef.current = handleRetake;

  /**
   * 스캔 종료 요청 — X 버튼과 뒤로가기가 **같은 길**을 탄다.
   * 스캔 모드에서 아직 담지 않은 카드가 레일에 있으면 폐기 확인부터(SC-010d · FN-031).
   * 종전엔 X 버튼에만 확인이 걸려 있어 뒤로가기로 나가면 레일이 조용히 사라졌다.
   */
  const requestClose = useCallback(() => {
    if (!isLabelMode && sessionItems.length > 0) setDiscardOpen(true);
    else activityNav.close();
  }, [isLabelMode, sessionItems.length, activityNav]);
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  /**
   * 뒤로가기 가로채기 — 촬영 후 라벨링 화면에서는 activity 를 닫지 않고 **다시 찍기**
   * 로 되돌린다. 한 단계만 되돌리려던 사용자가 메뉴까지 튕기던 문제(실기기 제보).
   * 라이브 상태면 requestClose 로 넘겨 X 버튼과 동일하게 폐기 확인을 거친다.
   * dispatchBack 의 close-guard 계약: 가드 이벤트를 받은 페이지가 close 여부를 직접
   * 결정한다(직접 close 는 재가드 없음).
   * ⚠ 폐기 확인이 떠 있는 동안에는 이 이벤트가 오지 않는다 — 다이얼로그가
   *   activityStore.dialogs 의 top 을 점유해 resolveBack 이 close-dialog 로 먼저
   *   빠지기 때문(useDialogBackBinding). 즉 확인창에서 뒤로가기 = 취소.
   */
  useEffect(() => {
    const onCloseRequest = () => {
      if (viewModeRef.current === "editing") handleRetakeRef.current();
      else requestCloseRef.current();
    };
    window.addEventListener("peeka:request-scan-close", onCloseRequest);
    return () => window.removeEventListener("peeka:request-scan-close", onCloseRequest);
  }, []);

  // 폐기 확인 ↔ 뒤로가기 스택 연결 — 열려 있는 동안 back 한 번은 "취소"로 흡수된다.
  useDialogBackBinding({
    isOpen: discardOpen,
    close: () => setDiscardOpen(false),
    kind: "scan-discard",
  });

  /**
   * 폐기 확인의 "확인" — 다이얼로그를 닫고 **그 뒤에** 스캔 activity 를 닫는다.
   *
   * ⚠ 두 동작은 각각 history 를 한 번씩 되감는다: 다이얼로그는 열릴 때 sentinel
   * 항목을 push 해 뒀다가 닫히며 pop 하고(useDialogBackBinding), activity close 도
   * 자기 entry 를 pop 한다(closeActivityAndNavigate). 이 둘을 **같은 태스크에서**
   * 연달아 부르면 iOS WKWebView 가 두 back 을 하나로 합쳐 sentinel 만 사라지고
   * URL 이 스캔 그대로 남는다 — 확인을 눌러도 스캔 화면이 안 닫힌다(2026-09-14
   * 실기기 제보). Android/Chrome 은 두 pop 이 각각 처리돼 우연히 맞아떨어졌다.
   *
   * 그래서 sentinel pop 이 **실제로 도착한 뒤**(popstate) activity 를 닫는다.
   * pop 이 오지 않는 경우(이미 다른 경로로 정리된 상태)를 위해 짧은 보루를 둔다.
   */
  const closeAfterDialogPop = useCallback(() => {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      window.removeEventListener("popstate", run);
      clearTimeout(timer);
      activityNav.close();
    };
    const timer = setTimeout(run, 300);
    window.addEventListener("popstate", run);
    setDiscardOpen(false);
  }, [activityNav]);

  /**
   * still OCR (카드번호) — 사진 1장당 1회만 돌리고 결과(Promise)를 캐시한다.
   * 모델/사용자 매치가 같은 사진을 쓰므로 두 번째 호출은 대기 없이 재사용.
   * 실패(인식 불가)는 null — 교차 검증만 생략된다.
   * canonical = 대조용(선행 0 무시) / raw = 카탈로그 CARD_NO 정확일치 질의용 원문.
   */
  interface OcrNumbers {
    lines: string[];
    canonical: Set<string>;
    raw: string[];
  }
  const ocrCacheRef = useRef<{ path: string; promise: Promise<OcrNumbers | null> } | null>(null);
  const ocrNumbersFor = useCallback(
    (
      photoPath: string,
      corners: Array<{ x: number; y: number }>,
      maxLongSide?: number,
    ): Promise<OcrNumbers | null> => {
      // 캐시 키에 인식 언어 포함 — 언어를 바꿔 재매치하면 그 언어 recognizer 로 다시 읽는다
      const cacheKey = `${photoPath}|${scanLang}|${maxLongSide ?? 0}`;
      if (ocrCacheRef.current?.path === cacheKey) return ocrCacheRef.current.promise;
      const promise = CardEngine.ocrCard({
        photoPath,
        corners,
        lang: scanLang,
        ...(maxLongSide ? { maxLongSide } : {}),
      })
        .then((r) => ({
          lines: r.lines,
          canonical: extractCardNumbers(r.lines),
          raw: extractRawCardNumbers(r.lines),
        }))
        .catch((err) => {
          nativeWarn("scan:ocr", err);
          return null;
        });
      ocrCacheRef.current = { path: cacheKey, promise };
      return promise;
    },
    [scanLang],
  );

  /**
   * 라벨 업로드 — 사진 + 외곽선 + 매칭 결과 + **사용자 판정·조건·촬영방식**.
   * 같은 촬영은 labelKey 로 업서트라 여러 번 눌러도 사진은 1회만 저장되고 라벨만 갱신된다.
   */
  /**
   * 로컬 파일(convertFileSrc URL) 을 blob 으로 읽는다.
   *
   * ⚠ **원본 fetch 를 써야 한다**(2026-08-18). CapacitorHttp 가 `window.fetch` 를 패치해
   * 두는데 그 패치는 **네트워크 요청용**이라, `_capacitor_file_` 같은 로컬 파일 URL 을
   * 태우면 실패한다. 라벨 업로드가 여태 한 건도 안 올라간 원인이 이것이다
   * (sc_scan_label_hist 가 8/7 기능 추가 이후 0행이었다 — BE·네트워크는 정상인데
   *  앱 안에서 사진을 읽는 단계에서 깨지고 있었다).
   * 반대로 **업로드 요청 자체는 패치된 fetch 를 써야 한다**(api/upload.ts 주석 참조).
   */
  const readLocalBlob = useCallback(async (url: string): Promise<Blob> => {
    const webFetch = (window as { CapacitorWebFetch?: typeof fetch }).CapacitorWebFetch;
    const res = await (webFetch ?? window.fetch)(url);
    return res.blob();
  }, []);

  const uploadLabel = useCallback(
    async (match: MatchResult | null, userOutline: Array<{ x: number; y: number }>) => {
      if (!photo) return;
      const blob = await readLocalBlob(photo.src);
      const file = new File([blob], "scan.jpg", { type: blob.type || "image/jpeg" });
      await uploadScanLabel(file, {
        labelKey: labelKeyRef.current,
        imageWidth: photo.width,
        imageHeight: photo.height,
        schema: "rounded_quad_8_tangent_points_v3",
        modelOutline,
        userOutline,
        scanLang,
        match: match ? { matched: match.matched, matches: match.matches } : null,
        verdicts: labelVerdict,
        conditions: labelConditions,
        captureMode: captureModeRef.current,
        liveSignal: liveSignalRef.current,
      });
    },
    [photo, modelOutline, scanLang, labelVerdict, labelConditions, readLocalBlob],
  );

  /** 매치 버튼 없이도 라벨만 올린다 — 정답·조건을 고른 뒤 누르는 버튼 */
  const handleUploadLabel = useCallback(() => {
    if (labelUploading || !photo) return;
    setLabelUploading(true);
    void uploadLabel(userResult ?? modelResult, outline)
      .then(() => {
        showNotice(t("scan.labelUploaded"));
        // 라벨을 올렸으면 그 사진에 더 할 일이 없다 — 바로 라이브로 돌아가 다음 장을
        // 찍게 한다(사용자 제안). 결과 패널이 사라지므로 성공 토스트도 확실히 보인다.
        handleRetakeRef.current();
      })
      .catch((err: unknown) => {
        nativeWarn("scan:labelUpload", err);
        // 원인이 화면에 보여야 한다 — 일반 문구만 띄우면 또 "버튼만 바뀌고 만다"가 된다
        const detail = err instanceof Error ? err.message : String(err);
        showNotice(`${t("scan.labelUploadFailed")} (${detail.slice(0, 80)})`);
      })
      .finally(() => setLabelUploading(false));
  }, [labelUploading, photo, uploadLabel, userResult, modelResult, outline, showNotice, t]);

  /**
   * 매칭 코어 — 사진·외곽선만 받아 결과를 계산한다. 라벨링 매치(handleMatch)와
   * 카드 스캔 모드의 무화면 확정(confirmScan)이 공유한다 (2026-08-24 분리).
   * 실패(엔진 오류·lease 세대 전환)는 null — 오류 안내와 lease 캐시 폐기는 여기서.
   */
  const computeMatchResult = useCallback(
    async (
      photoLike: { path: string; width: number; height: number },
      src: Pt[],
      judgeOpts?: { ocrMaxLongSide?: number; deferCatalog?: boolean },
    ): Promise<MatchResult | null> => {
      try {
        // lease 는 페이지 수명 캐시(ensureMatcherLeases) 공유 — unmount 에서 일괄 반환
        const leases = await ensureMatcherLeases();
        if (!leases) {
          showNotice(t("scan.matcherPending"));
          return null;
        }
        const corners = virtualCorners(src).map((p) => ({
          x: p.x / photoLike.width,
          y: p.y / photoLike.height,
        }));
        // 6번째는 선택 사이드카 — 없는 릴리스면 undefined 이고 매칭은 그대로 된다
        const [embed, embedData, index, serlno, matcherConfig, meta] = leases;
        const result = await CardEngine.matchCard({
          photoPath: photoLike.path,
          corners,
          embedPath: embed.path,
          embedDataPath: embedData.path,
          indexPath: index.path,
          serlnoPath: serlno.path,
          matcherConfigPath: matcherConfig.path,
          ...(meta ? { metaPath: meta.path } : {}),
          topK: MATCH_POOL_TOP_K,
        });
        // 카탈로그 조회는 부가 정보 — 실패해도 매칭 결과(유사도)는 그대로 보여준다.
        // OCR(카드번호)도 비필수 — 실패/미검출이면 교차 검증 없이 진행.
        // deferCatalog (2026-08-27 사용자 결정): 스캔 확정은 카탈로그를 **기다리지
        // 않는다** — 판정·교차검증은 사이드카(로컬)로 서고, 이미지·시세는 적재 후
        // 하이드레이션이 채운다. 사이드카가 후보를 못 덮는 구 릴리스만 종전대로.
        const localCards = crossCheckCardsFrom(result.matches);
        // ⚠ 비교는 **유니크 serlno 수** 기준 — 인덱스에 동일 임베딩 중복행이 실재해
        // (57,726 중 유일값 57,034) 같은 카드가 top-10 에 두 번 들어오면 Map 크기가
        // 후보 수보다 작아지고, 사이드카가 멀쩡한데도 카탈로그 대기(구 경로)로
        // 빠졌다 (2026-08-27 실기기 — 확정 전 batch 호출이 남아 보인 원인).
        const sidecarComplete =
          localCards.size === new Set(result.matches.map((m) => m.cardSerlno)).size;
        const defer = judgeOpts?.deferCatalog === true && sidecarComplete;
        const [cards, ocr] = await Promise.all([
          defer
            ? Promise.resolve(null)
            : fetchCardsCached(result.matches.map((m) => m.cardSerlno)).catch((err) => {
                nativeWarn("scan:fetchCards", err);
                return null;
              }),
          // 글자 읽기도 같은 사진으로 — 자동 촬영이면 판정 프레임(라이브 해상도)이라
          // 번호 인식률이 스틸보다 낮다(고해상 짝 스틸은 2026-08-20 제거 — iOS 성능
          // 문제로, 정확도는 학습으로 보완하기로 한 사용자 결정).
          OCR_ASSIST_ENABLED
            ? ocrNumbersFor(photoLike.path, corners, judgeOpts?.ocrMaxLongSide)
            : Promise.resolve(null),
        ]);
        // 인식 정보 패널 — OCR 이 실제로 무엇을 읽었는지 화면에서 확인 가능하게
        setOcrInfo(
          ocr ? { lines: ocr.lines, canonical: [...ocr.canonical], raw: ocr.raw } : "failed",
        );
        // OCR 교차 검증 — 번호 단독은 접전(유사도 차 ≤ CROSS_CHECK_MAX_SIM_GAP)에서만,
        // 이름+번호 동시 일치는 격차 제한 없이 우대. 재정렬은 전체 후보(top-5) 기준,
        // 표시는 그 뒤 top-2 를 자른다.
        // 대조에 쓰는 이름·번호는 **사이드카 우선**이다 — 카탈로그 조회가 실패해도
        // (기내·지하 등) 판정은 그대로 선다. 조회는 표시용(세트명·레어도·시세)으로 남는다.
        const crossCheckCards = sidecarComplete ? localCards : cards;
        let entries: MatchEntry[] = result.matches;
        let confirmedSerlnos = new Set<number>();
        let nameConfirmedSerlnos = new Set<number>();
        let setConfirmedSerlnos = new Set<number>();
        if (crossCheckCards && ocr && ocr.canonical.size > 0) {
          const checked = crossCheckMatches(
            entries, crossCheckCards, ocr.canonical, ocr.raw, ocr.lines,
          );
          entries = checked.matches;
          confirmedSerlnos = checked.confirmedSerlnos;
          nameConfirmedSerlnos = checked.nameConfirmedSerlnos;
          setConfirmedSerlnos = checked.setConfirmedSerlnos;
        }
        const top = entries.slice(0, RESULT_TOP_K);
        const topCard = top.length > 0 ? cards?.get(top[0].cardSerlno) ?? null : null;
        // 라이브↔스틸 교차 확인 — 관측만 한다(판정 미반영, MatchResult.liveTop 주석 참조)
        const liveTop = liveTopAtCaptureRef.current;
        const liveAgreed =
          liveTop === null || top.length === 0 ? null : liveTop === top[0].cardSerlno;

        // 번호 일치가 top-5 안에 없으면 **인덱스 전량**에서 번호로 찾는다 — 유사도가
        // 밀려 후보 밖으로 나간 인쇄판의 구제 경로. 대상은 인덱스에 실린 카드뿐이고
        // 서버 검색으로 폴백하지 않는다(2026-08-25 사용자 결정: 인덱스에 없는 카드는
        // 애초에 인식될 수 없으니 번호로만 튀어나오는 것도 맞지 않다).
        // 카드 정보(세트명·시세·이미지)만 id 조회로 채운다 — 판정은 이미 끝나 있다.
        let ocrFound: CardItem | null = null;
        if (ocr && ocr.canonical.size > 0 && confirmedSerlnos.size === 0) {
          try {
            const found = findCardByNumber(
              await ensureCatalogRows(),
              ocr.canonical,
              ocr.raw,
              ocr.lines,
            );
            if (found) {
              if (defer) {
                // 즉시 적재 스텁 — 번호 확장 검색도 카탈로그를 기다리지 않는다
                ocrFound = stubCardFromSidecar(
                  found.cardSerlno, found.name, found.number, null, scanLang,
                );
              } else {
                const fetched = await fetchCardsCached([found.cardSerlno]);
                ocrFound = fetched.get(found.cardSerlno) ?? null;
              }
            }
          } catch (err) {
            nativeWarn("scan:ocrNumberScan", err);
          }
        }

        // 확신 미달 구제 — 이름+번호 동시 일치가 유일하고 그것이 top-1 이면 최상급
        // 확정("이름·번호 일치"), 아니면 종전 규칙(번호 일치 유일 + top-1)로 확정.
        const bothSerlnos = [...confirmedSerlnos].filter((s) => nameConfirmedSerlnos.has(s));
        const nameNumberResolved =
          !result.matched &&
          top.length > 0 &&
          bothSerlnos.length === 1 &&
          bothSerlnos[0] === top[0].cardSerlno;
        const numberResolved =
          !result.matched &&
          !nameNumberResolved &&
          top.length > 0 &&
          confirmedSerlnos.size === 1 &&
          confirmedSerlnos.has(top[0].cardSerlno);

        const payload: MatchResult = {
          matched: result.matched,
          matches: top,
          cards,
          confirmedSerlnos,
          nameConfirmedSerlnos,
          setConfirmedSerlnos,
          numberResolved,
          nameNumberResolved,
          ocrFound,
          allMatches: entries,
          liveTop,
          liveAgreed,
          // 언어 불일치 안내 — 확신 매칭이고 top-1 언어가 인식 언어와 다를 때만.
          // 인덱스가 언어별 배달이라 흔치 않지만, 언어팩 혼합 인쇄판이 실재한다.
          langMismatch:
            result.matched && topCard && topCard.language !== scanLang
              ? topCard.language
              : null,
          ocrLines: ocr?.lines ?? null,
          cropLuma: typeof result.cropLuma === "number" && result.cropLuma >= 0 ? result.cropLuma : null,
          cropStd: typeof result.cropStd === "number" && result.cropStd >= 0 ? result.cropStd : null,
        };
        return payload;
      } catch (err) {
        nativeWarn("scan:match", err);
        // 예외 = 유사도 미달이 아니라 엔진 오류다. 낡은 lease 조합(세대 전환기에
        // 잡힌 경로)이 원인일 수 있고, 엔진은 같은 경로 조합의 로드 실패를 캐시하므로
        // 캐시를 버려야 다음 시도가 새 세대 경로로 다시 열린다.
        const stale = matcherLeasesRef.current;
        matcherLeasesRef.current = null;
        matcherLeaseFailedAtRef.current = 0;
        catalogRowsRef.current = null; // 버리는 세대의 사이드카도 함께 폐기
        if (stale) void releaseLeases(stale);
        showNotice(t("scan.matchFailed"));
        return null;
      }
    },
    [
      scanLang,
      ensureMatcherLeases,
      ensureCatalogRows,
      fetchCardsCached,
      ocrNumbersFor,
      showNotice,
      t,
    ],
  );

  /**
   * 카드 스캔 모드 확정 (SC-010, 2026-08-24) — **화면 전환 없이** 판정 프레임을
   * 매칭해 세션 레일에 쌓는다. 라벨링 모드의 enterEditing 에 대응하는 소비자 경로.
   * 수락 기준은 라벨링 결과 패널의 확정 규칙과 동일(matched/번호/이름·번호) +
   * 번호 확장 검색(ocrFound). 미달이면 사진을 버리고 안내만 띄운다.
   */
  const confirmScan = useCallback(
    async (p: AutoCapturePayload, opts?: { manual?: boolean; escalated?: boolean }) => {
      if (confirmBusyRef.current || (!opts?.manual && !scanArmedRef.current)) {
        deletePhotoFile(p.path);
        return;
      }
      confirmBusyRef.current = true;
      // 자동 스틸의 쿨다운 백오프 기준 — 아래 finally 에서 집계한다. 확정에 이르지 못한
      // 경로(검출 실패·수락 미달·노출 보류)가 실패다.
      let confirmedOk = false;
      /**
       * 담긴 것도 실패한 것도 아닌 경우 — **전 카드가 중복 가드로만** 걸러졌다
       * (2026-09-07). 판정은 정확히 맞았고 "같은 실물을 또 봤다"일 뿐이라 백오프를
       * 올리면 안 된다. 스윕에서 화면이 겹치는 동안 이 경로가 반복되며 간격이
       * 6초까지 늘어 다음 카드가 브래킷 90% 에서 오래 서 있었다.
       */
      let duplicateOnly = false;
      try {
        // 다중 카드 스틸 (2026-08-26) — 엔진이 스틸에서 검출한 quad 전부를 같은 수락
        // 규칙으로 심사해 **한 장의 스틸로 화면 안 카드를 전부** 담는다(몬프라이스식
        // 더미 스윕 — 재생 하네스 실측: 같은 모델로 확정 구조만 바꿔 0장→10장).
        // 구 페이로드(단일 modelCorners)는 1개짜리 목록으로 다뤄 동작이 같다.
        const rawQuads =
          p.modelQuads && p.modelQuads.length > 0
            ? p.modelQuads
            : p.modelCorners && p.modelCorners.length === 4
              ? [p.modelCorners]
              : [];
        const quadList = rawQuads
          .filter((q) => q.length === 4)
          .slice(0, STILL_CONFIRM_MAX_CARDS)
          .map((q) =>
            outlineFromCorners(q.map((c) => ({ x: c.x * p.width, y: c.y * p.height }))),
          );
        if (quadList.length === 0) {
          track("scan_confirm_failed", {
            reason: "no_detection",
            manual: !!opts?.manual,
            scanLang: useScanLangStore.getState().lang,
          });
          setConfirmDebug("검출 실패(외곽선 없음)");
          showNotice(t("scan.confirmFailed"));
          return;
        }
        const scanLangNow = useScanLangStore.getState().lang;
        const addedCards: CardItem[] = [];
        /** 이번 스틸에서 담겼거나 중복으로 걸러진 카드 — 다음 스틸의 가드 집합이 된다 */
        const guardedSerlnos = new Set<number>();
        /** 대표(첫) quad 의 실패 진단 — 전부 미달일 때 종전과 같은 메시지를 띄운다 */
        let primaryFailDebug: string | null = null;
        let primaryResult: MatchResult | null = null;
        /** 노출 게이트 보류 발생 — 실패 안내를 노출 안내로 바꾼다 */
        let exposureHeld = false;
        /**
         * 보류의 종류 — exposureHeld 는 노출·블러 둘 다에 서는 한 플래그라(뒤의 재판정
         * 억제가 둘을 같게 다룬다) 집계용으로만 갈라 둔다. 사용자가 고칠 수 있는 원인이
         * 서로 달라서다: 노출은 조명, 블러는 멈춰서 찍기.
         */
        let heldKind: "exposure_held" | "blur_held" | null = null;
        for (const modelPts of quadList) {
          const result = await computeMatchResult(
            { path: p.path, width: p.width, height: p.height },
            modelPts,
            // deferCatalog: 스캔 확정은 카탈로그 API 를 판정 경로에서 뗀다 (즉시 적재)
            // 고해상 재판정 패스는 OCR 을 원해상으로 읽는다
            opts?.escalated
              ? { ocrMaxLongSide: ESCALATE_OCR_LONG_SIDE, deferCatalog: true }
              : { deferCatalog: true },
          );
          if (primaryResult === null && result !== null) primaryResult = result;
          // 진단 박스용 OCR 원문 전체 — "뭐가 잘못 읽혔는지"를 화면에서 그대로 본다
          const ocrDbg = ` | ocr(${result?.ocrLines?.length ?? 0}): ${(result?.ocrLines ?? []).join(" / ")}`;
          // 노출 실측 로그 — EXPO_* 문턱 튜닝 근거 (항상 남긴다)
          if (result) {
            console.log(
              `[expo] luma=${result.cropLuma?.toFixed(0) ?? "-"} std=${result.cropStd?.toFixed(0) ?? "-"} sim=${result.matches[0]?.similarity.toFixed(2) ?? "-"} matched=${result.matched}`,
            );
          }
          // 노출 게이트 — 실루엣 crop 은 임베딩이 자신 있게 틀린다 (EXPO_* 주석)
          if (
            !opts?.manual &&
            result?.cropLuma !== null &&
            result?.cropLuma !== undefined &&
            result.cropLuma < EXPO_MIN_LUMA &&
            result.cropStd !== null &&
            result.cropStd < EXPO_MIN_STD
          ) {
            exposureHeld = true;
            heldKind ??= "exposure_held";
            if (primaryFailDebug === null) {
              primaryFailDebug = `노출 보류 luma=${result.cropLuma.toFixed(0)} std=${result.cropStd.toFixed(0)}${ocrDbg}`;
            }
            continue;
          }
          // 번호 모순 게이트 (관측 모드 — NUMBER_GATE_SHADOW 주석. 보류하지 않는다)
          if (NUMBER_GATE_SHADOW && result?.matched && result.ocrLines && result.ocrLines.length > 0) {
            try {
              const canonical = extractCardNumbers(result.ocrLines);
              const top1 = result.matches[0]?.cardSerlno;
              if (canonical.size > 0 && top1 !== undefined && !result.confirmedSerlnos.has(top1)) {
                const found = findCardByNumber(
                  await ensureCatalogRows(),
                  canonical,
                  extractRawCardNumbers(result.ocrLines),
                  result.ocrLines,
                );
                if (found && found.cardSerlno !== top1) {
                  console.log(
                    `[gate:number-would-hold] top1=${top1} sim=${result.matches[0]?.similarity.toFixed(2)} ocr=${[...canonical].join(",")} found=${found.cardSerlno} manual=${!!opts?.manual}`,
                  );
                }
              }
            } catch {
              // 관측 전용 — 실패는 판정에 영향 없음
            }
          }
          const accepted =
            result !== null &&
            (result.matched || result.numberResolved || result.nameNumberResolved);
          let card: CardItem | null = null;
          if (accepted) {
            const topEntry = result.matches[0];
            card = (topEntry !== undefined ? result.cards?.get(topEntry.cardSerlno) : null) ?? null;
            // 즉시 적재 스텁 (2026-08-27) — 사이드카 이름·번호로 바로 담고, 이미지·
            // 시세는 하이드레이션(fire-and-forget)이 채운다. 판정 시간에 API 미포함.
            if (!card && topEntry?.name) {
              card = stubCardFromSidecar(
                topEntry.cardSerlno,
                topEntry.name,
                topEntry.number ?? null,
                topEntry.setCode ?? null,
                scanLangNow,
              );
            }
          }
          if (!card && result?.ocrFound) card = result.ocrFound;
          // 어두움 × 약확신 × 무증거 보류 (EXPO_WEAK_* 주석) — 임베딩 단독 근거일 때만.
          // OCR 교차 증거(이름·번호·세트코드)가 하나라도 붙었으면 어두워도 통과한다.
          const gateTop1 = result?.matches[0]?.cardSerlno;
          const gateCorroborated =
            result !== null &&
            gateTop1 !== undefined &&
            (result.confirmedSerlnos.has(gateTop1) ||
              result.nameConfirmedSerlnos.has(gateTop1) ||
              result.setConfirmedSerlnos.has(gateTop1));
          if (
            OCR_ASSIST_ENABLED &&
            !opts?.manual &&
            card &&
            result !== null &&
            result.matched &&
            !result.numberResolved &&
            !result.nameNumberResolved &&
            !gateCorroborated &&
            result.cropLuma !== null &&
            result.cropLuma < EXPO_WEAK_LUMA &&
            (result.matches[0]?.similarity ?? 1) < EXPO_WEAK_SIM
          ) {
            exposureHeld = true;
            heldKind ??= "exposure_held";
            if (primaryFailDebug === null) {
              primaryFailDebug = `어두움×약확신×무증거 보류 luma=${result.cropLuma.toFixed(0)} sim=${result.matches[0]?.similarity.toFixed(2)}${ocrDbg}`;
            }
            continue;
          }
          // 블러 스틸 보류 (2026-08-27 실기기 — 빠른 스윕 중 오적재): 이동 중 찍힌
          // 스틸은 모션블러로 OCR 이 **0줄**이고, 임베딩 단독 sim 0.39~0.40 경계로
          // 마진만 넘어 오답이 담겼다. 정상 정지 샷은 항상 몇 줄이라도 읽으므로
          // (오늘 로그 전부 2~21줄) "OCR 돌았는데 0줄 + 임베딩 단독 약확신"은 블러
          // 서명으로 보고 보류한다 — 손해는 재시도 한 번. 머무름(dwell) 게이트는
          // 사용자 결정으로 넣지 않는다("너무 안 잡힌다") — 이 보류가 그 자리를 맡는다.
          if (
            OCR_ASSIST_ENABLED &&
            !opts?.manual &&
            card &&
            result !== null &&
            result.matched &&
            !result.numberResolved &&
            !result.nameNumberResolved &&
            !gateCorroborated &&
            result.ocrLines !== null &&
            result.ocrLines.length === 0 &&
            (result.matches[0]?.similarity ?? 1) < 0.5
          ) {
            exposureHeld = true;
            heldKind ??= "blur_held";
            if (primaryFailDebug === null) {
              primaryFailDebug = `블러 보류 ocr=0줄 sim=${result.matches[0]?.similarity.toFixed(2)}`;
            }
            continue;
          }
          if (!card) {
            if (primaryFailDebug === null) {
              primaryFailDebug = `수락 미달 sim=${result?.matches[0]?.similarity.toFixed(2) ?? "-"} (매칭·번호·이름 전부 부족)${ocrDbg}`;
            }
            continue;
          }
          // 스윕 중복 가드 — 직전 스틸에서 담긴 카드가 화면 겹침으로 또 잡힌 경우.
          // 같은 실물이므로 걸러내되, 가드 집합에는 남겨 계속 보이는 동안 유지한다
          // (lastConfirmedSerlnosRef 주석 — 같은 카드 재적재는 검출 단절 재장전으로만).
          if (
            lastConfirmedSerlnosRef.current.has(card.cardSerialNo) ||
            guardedSerlnos.has(card.cardSerialNo)
          ) {
            guardedSerlnos.add(card.cardSerialNo);
            continue;
          }
          // TEMP-DIAG: 확정 추적 (진단 후 제거)
          console.log(
            `[confirm] card=${card.cardSerialNo} name=${card.name} lang=${card.language} scanLang=${scanLangNow} manual=${!!opts?.manual} matched=${!!result?.matched} nn=${!!result?.nameNumberResolved} no=${!!result?.numberResolved} ocrFound=${result?.ocrFound?.cardSerialNo ?? "-"} lines=${result?.ocrLines?.length ?? -1}`,
          );
          // §4.1 비회원 5회 — 중복 카드는 차감 없이 통과(guestScanStore 규약)
          if (!isAuthenticated) {
            const consumed = consumeScan(card.cardUid);
            if (!consumed.ok) {
              // §4.1 한도 소진 = 로그인 오버레이가 뜨는 순간. 로그인 전환율의 분모다.
              track("scan_guest_limit_reached", { scanLang: scanLangNow });
              setGuestLimitReached(true);
              break;
            }
          }
          console.log(`[confirm] ADDED card=${card.cardSerialNo} lang=${card.language}`); // TEMP-DIAG
          // 확정 1건 = peeka 의 핵심 전환. 근거(evidence)까지 남겨야 "왜 맞췄나/왜 틀렸나"를
          // 집계로 볼 수 있다 (scanTelemetry.ts — 수락 규칙과 1:1).
          track("scan_card_confirmed", {
            evidence: scanEvidenceOf(result, !!opts?.manual),
            similarity: scanSimilarityOf(result) ?? null,
            scanLang: scanLangNow,
            cardLang: card.language,
            manual: !!opts?.manual,
            guest: !isAuthenticated,
            cardSerialNo: card.cardSerialNo,
          });
          recordRecentScan(card.cardSerialNo);
          const itemId = sessionAdd(card);
          // 스텁이면 카탈로그 하이드레이션 — **판정 경로 밖**(비대기). 미적재 카드
          // (인덱스⊃카탈로그)는 여기서 걷어내 종전 "카탈로그에 없는 카드" 의미 유지.
          if (card.cardUid.startsWith("serl:")) {
            const serlno = card.cardSerialNo;
            void fetchCardsCached([serlno])
              .then(async (map) => {
                const real = map.get(serlno);
                if (!real) {
                  useScanSessionStore.getState().removeItem(itemId);
                  showNotice(t("scan.resultNotInCatalog"));
                  return;
                }
                // 썸네일을 **미리 받아** 데이터와 함께 원자적으로 교체 — 없으면
                // 시세는 채워졌는데 이미지가 늦게 뜨는 두 단계 갱신이 가지각색으로
                // 보인다(2026-08-27 제보). 상한 2.5s — 깨진 이미지에 안 잡혀 있게.
                const url = cardImageUrl(real.imagePathway);
                if (url && import.meta.env.MODE !== "test") {
                  await new Promise<void>((resolve) => {
                    const img = new Image();
                    const done = () => resolve();
                    img.onload = done;
                    img.onerror = done;
                    setTimeout(done, 2500);
                    img.src = url;
                  });
                }
                sessionHydrate(itemId, real);
              })
              .catch(() => {
                // 네트워크 실패 — 이름·번호 스텁 유지. 시세·이미지는 다음 기회에.
              });
          }
          addedCards.push(card);
          guardedSerlnos.add(card.cardSerialNo);
        }
        if (addedCards.length === 0) {
          // 수락 미달이어도 포기 전에 진짜 스틸(1080p) 1회 — 같은 그림 재판이
          // 여럿이면(뮤 5장, 라벨 228) 임베딩 마진이 구조적으로 안 나와 matched 가
          // 영영 안 서고, 판정 프레임(라이브 해상도)은 번호·세트코드가 안 읽혀
          // "수락 미달"만 반복된다. 고해상 OCR 이 번호를 읽으면 번호 확정으로 담긴다.
          const topSerl = primaryResult?.matches[0]?.cardSerlno ?? null;
          // 스틸 우선 구조에선 이 사진이 이미 스틸이라 다시 찍어봐야 같은 해상도다.
          // 재시도는 쿨다운 백오프(autoStillFailRef)가 맡는다.
          if (
            !STILL_FIRST_CONFIRM &&
            !opts?.manual &&
            topSerl !== null &&
            rescueTriedRef.current !== topSerl
          ) {
            rescueTriedRef.current = topSerl;
            setConfirmDebug(
              `수락 미달 sim=${primaryResult?.matches[0]?.similarity.toFixed(2) ?? "-"} → 스틸 재확인`,
            );
            showNotice(t("scan.rescueChecking"));
            void handleCaptureRef.current();
            return;
          }
          // 전 카드가 가드(중복)로만 걸러졌으면 실패 안내가 아니라 침묵 — 종전
          // "재장전 대기 중 같은 카드 재확정 금지" 경로의 다중 카드 버전이다.
          if (guardedSerlnos.size > 0) {
            duplicateOnly = true;
            return;
          }
          // 고해상 재판정 에스컬레이션 (2026-08-27, 사용자 결정 — "verifier 는
          // 통과했는데 sim 이 낮으면 해상도 높은 것까지 해봐야"): 수락 미달이고
          // 노출 보류가 아니면(어두움은 해상도가 못 고친다 — 안내가 정답) 센서 최대
          // 스틸로 딱 한 번 다시 판정한다. sim 은 해상도로 안 오르므로(재생 실측)
          // 표적은 OCR 증거 — 원해상 디코드로 번호 글리프 화소를 살린다.
          if (
            ESCALATE_ENABLED &&
            STILL_FIRST_CONFIRM &&
            !opts?.manual &&
            !opts?.escalated &&
            !exposureHeld &&
            !escalationTriedRef.current
          ) {
            escalationTriedRef.current = true;
            setConfirmDebug(
              `수락 미달 sim=${primaryResult?.matches[0]?.similarity.toFixed(2) ?? "-"} → 고해상 재판정`,
            );
            showNotice(t("scan.rescueChecking"));
            // busy 해제(finally) 후 촬영이 시작되도록 비동기로 — 카드당 1회
            void escalateConfirmRef.current();
            return;
          }
          track("scan_confirm_failed", {
            reason: heldKind ?? "below_threshold",
            similarity: scanSimilarityOf(primaryResult) ?? null,
            manual: !!opts?.manual,
            scanLang: scanLangNow,
          });
          setConfirmDebug(primaryFailDebug ?? "수락 미달");
          // 노출 보류는 실패가 아니라 촬영 환경 안내 — 사용자가 고칠 수 있는 원인을 말한다
          showNotice(t(exposureHeld ? "scan.exposureLow" : "scan.confirmFailed"));
          return;
        }
        /**
         * 언어 판독 게이트는 **의도적으로 없다** (2026-08-25 최종 결정 — 하루 동안
         * 5차 재설계 끝). 인덱스가 선택 언어 카드만 담는 언어팩 구조라 타 언어
         * 실물은 "가장 비슷한 선택 언어 카드"로 조용히 틀리는데, 이를 OCR 로 막는
         * 시도(카탈로그 언어 비교·문자종류·글자량 모순·이름 증거 필수)는 전부
         * 실기기에서 오탐/미탐 어느 한쪽으로 무너졌다 — ko 리코그나이저의 시각
         * 유사 오독("땃쭈르"→"맛두르")과 가나 환각 한글이 글자 수준에서 구분되지
         * 않는다. **OCR 은 정확도 보조로만 쓴다**(crossCheck 재랭킹: 이름+번호 >
         * 번호 접전 > 세트 코드 — 재판·언어판 접전 교정). 언어 칩을 맞게 두는 것은
         * 사용자 몫이다(scanLangStore 주석의 제품 전제 그대로).
         */
        const first = addedCards[0];
        setConfirmDebug(
          addedCards.length === 1
            ? `담김: ${first.name}`
            : `담김: ${addedCards.map((c) => c.name).join(", ")} (${addedCards.length}장)`,
        );
        showNotice(
          addedCards.length === 1
            ? t("scan.confirmAdded", { name: first.name })
            : t("scan.confirmAddedMulti", { name: first.name, count: addedCards.length - 1 }),
        );
        // 재장전 — 검출 단절(LOST_ARM_MS)·카드 전환(REARM_*)·타임아웃이 다음 장
        setArmed(false);
        disarmedAtRef.current = performance.now();
        guardQuadRef.current =
          p.modelCorners && p.modelCorners.length === 4 ? p.modelCorners : null;
        guardMissRef.current = 0;
        confirmedOk = true;
        // 가드는 **누적**한다 (2026-09-08) — 직전 스틸 카드만 기억하면 엔진이 타깃을
        // A→B→A 로 넘길 때 두 장이 번갈아 재적재된다(에뮬레이터 실측). 해제는 검출 단절
        // 재장전(카드를 치움)에서만 — lastConfirmedSerlnosRef 주석의 FN-026 규칙 그대로.
        for (const s of lastConfirmedSerlnosRef.current) guardedSerlnos.add(s);
        lastConfirmedSerlnosRef.current = guardedSerlnos;
        rearmVoteRef.current = null;
        lostSinceRef.current = null;
        scanProgressRef.current = 0;
      } finally {
        deletePhotoFile(p.path);
        confirmBusyRef.current = false;
        // 확정(또는 중복 가드) 끝난 카드 자리를 엔진 점착 후보에서 뺀다 (2026-09-08 —
        // "확정 뒤 다음 장이 느리다" 제보). 엔진이 그 자리를 계속 타깃으로 잡으면 옆 카드가
        // 보여도 quads[0] 이 안 바뀌어 기하 재장전이 영영 안 걸리고, 검출 단절 0.5s 나
        // 타임아웃 4s 만 남았다. 제외하면 브래킷이 즉시 다음 카드로 넘어간다. 구 엔진
        // APK 는 메서드가 없어 거절 — 무시(그 경우 종전 속도 그대로).
        if (isNative() && (confirmedOk || duplicateOnly)) {
          void CardEngine.excludeHintTarget().catch(() => {});
        }
        if (!opts?.manual) {
          // 중복 가드는 성공도 실패도 아니다 — 백오프를 올리지도 내리지도 않는다
          if (confirmedOk) autoStillFailRef.current = 0;
          else if (!duplicateOnly) autoStillFailRef.current += 1;
          // 백오프 쿨다운의 기준점 — **판정이 끝난 지금**이다 (AUTO_STILL_COOLDOWN_MS 주석)
          lastJudgeEndAtRef.current = performance.now();
          setHintPaused(false); // captureForAuto 가 걸어둔 정지 해제
        }
      }
    },
    [
      computeMatchResult,
      deletePhotoFile,
      isAuthenticated,
      consumeScan,
      sessionAdd,
      sessionHydrate,
      fetchCardsCached,
      recordRecentScan,
      setArmed,
      setHintPaused,
      showNotice,
      t,
    ],
  );

  confirmScanRef.current = confirmScan;

  /**
   * 고해상 재판정 촬영 (confirmScan 수락 미달 → 카드당 1회) — 셸 takeStillMax 로
   * 센서 최대 스틸을 받아 escalated 확정을 다시 돈다. FOV 가 상시 스틸과 달라
   * 코너는 재검출분만 쓴다(captureAndCrop 이 원래 그 구조).
   */
  const escalateConfirm = useCallback(async () => {
    try {
      setHintPaused(true);
      const shot = await capture({ quality: "max" });
      if (!shot || !shot.fullPath || !shot.fullWidth || !shot.fullHeight) {
        setHintPaused(false);
        return;
      }
      if (shot.cropPath) deletePhotoFile(shot.cropPath);
      setStillDebug(
        `${shot.fullWidth}×${shot.fullHeight} (고해상 재판정) native=${shot.elapsedMs ?? "?"}ms`,
      );
      await confirmScanRef.current(
        {
          path: shot.fullPath,
          width: shot.fullWidth,
          height: shot.fullHeight,
          modelCorners: shot.detected && shot.corners ? shot.corners : null,
          modelQuads:
            shot.detected && shot.quads && shot.quads.length > 0
              ? shot.quads.map((q) => q.corners)
              : null,
        },
        { escalated: true },
      );
    } catch (err) {
      nativeWarn("scan:escalate", err);
      setHintPaused(false);
    }
  }, [capture, deletePhotoFile, setHintPaused]);
  escalateConfirmRef.current = escalateConfirm;

  /**
   * 줌 (SC-003 · FN-024). CameraX/AVFoundation 모두 배율이 파이프라인 전체에 걸려
   * 분석 프레임까지 확대되므로, 배율을 올리면 검출·OCR 이 보는 카드도 같이 커진다.
   *
   * ⚠ 표시는 **적용된 값**을 따른다 — 3× 를 못 내는 기기에서 요청값을 그리면
   *   눌리는데 안 커지는 것처럼 보인다(플러그인이 클램프한 값을 돌려준다).
   * ⚠ 세션이 다시 서면 카메라는 1× 로 돌아온다(startScan 이 새로 바인드한다).
   *   화면 상태만 남으면 "2× 인데 1× 로 보인다"가 되므로 함께 되돌린다.
   */
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    if (state === "active") setZoom(1);
  }, [state]);
  const applyZoom = useCallback(
    (ratio: number) => {
      if (!isNative()) return;
      void LiveVision.setZoom({ ratio })
        .then((r) => setZoom(r.ratio))
        .catch((err) => nativeWarn("scan:setZoom", err));
    },
    [],
  );

  /**
   * 갤러리 불러오기 (SC-003 · FN-025).
   *
   * 사진 한 장을 골라 **라이브 스틸과 같은 판정 경로**에 태운다 — 검출(detectPhoto)
   * 만 카메라 대신 파일에서 하고, 그 뒤(matchCard · OCR 교차검증 · 수락 게이트 ·
   * 레일 적재 · 비회원 차감)는 confirmScan 이 그대로 든다. 별도 결과 화면을 만들지
   * 않은 이유가 이것이다 — 판정이 두 벌이 되면 한쪽만 고쳐진다.
   *
   * ⚠ 검출은 `detectStillPhoto` — **라이브 세션의 검출기**를 그대로 쓰는 스틸
   *   하이브리드 경로다. 옛 `detectPhoto` 는 `card-seg` 를 분할 모델로 여는데,
   *   하이브리드 전환(2026-08-04) 이후 그 kind 는 YOLO OBB 를 나르므로 현행
   *   릴리스에서 `Unknown input name image` 로 죽는다 (2026-08-28 실기기 확인).
   * ⚠ **피커가 닫히면 세션을 다시 세운다.** 사진 선택은 다른 앱(포토피커)으로
   *   넘어가는 일이라 그동안 앱이 백그라운드가 되고, live-vision 이 세션을
   *   interrupt 하면서 엔진 모델과 lease 를 전부 닫는다. 복귀 자동 재시작
   *   (useAppResume)이 있지만 실기기에서 그 이벤트가 안 온 경우가 있었고
   *   (2026-08-28 Note 9), 오더라도 검출 요청이 재시작보다 먼저 도착해 모델이
   *   없는 검출기를 만난다. 여기서 **명시적으로 기다렸다가** 검출한다 —
   *   취소하고 돌아온 경우도 마찬가지다(안 하면 카메라가 죽은 채 남는다).
   * ⚠ 크기는 **네이티브가 디코드한 값**을 쓴다. 웹뷰가 재면 EXIF 를 반영해
   *   가로세로가 뒤집힐 수 있고, 그러면 코너 매핑이 통째로 어긋난다.
   * ⚠ 수동 셔터와 같은 취급(manual)이다 — 사용자가 명시적으로 고른 사진이라
   *   재장전 상태·노출 게이트로 막지 않는다.
   * ⚠ 고른 파일은 Camera 플러그인이 앱 캐시에 복사한 사본이다. confirmScan 의
   *   정리(deleteCrop)는 lv_crops 밖 경로를 거부하므로 그 경고는 무해하다 —
   *   사본 수명은 플러그인 캐시가 든다.
   */
  const [galleryBusy, setGalleryBusy] = useState(false);
  const handleGallery = useCallback(async () => {
    if (galleryBusy || confirmBusyRef.current) return;
    if (stateRef.current !== "active") {
      showNotice(t("scan.modelPendingBody"));
      return;
    }
    setGalleryBusy(true);
    try {
      const picked = await Camera.pickImages({ limit: 1, quality: 100 });
      const photo = picked.photos[0];
      // 고르든 취소하든 카메라를 되살린다 — 위 ⚠ 참조
      await restart();
      if (!photo?.path) return;
      const det = await CardEngine.detectStillPhoto({ photoPath: photo.path });
      if (!det.detected || !det.corners || det.corners.length !== 4) {
        showNotice(t("scan.galleryNoCard"));
        return;
      }
      await confirmScanRef.current(
        {
          path: photo.path,
          width: det.width,
          height: det.height,
          modelCorners: det.corners,
          // 검출기는 최고 점수 quad 하나만 준다 — 스틸 목록 경로와 형태를 맞춘다
          modelQuads: [det.corners],
        },
        { manual: true },
      );
    } catch (err) {
      // 사용자가 고르지 않고 닫으면 플러그인이 reject 한다 — 안내할 실패가 아니다
      const msg = String((err as { message?: string })?.message ?? err);
      const cancelled = /cancel/i.test(msg);
      if (!cancelled) {
        nativeWarn("scan:gallery", err);
        showNotice(t("scan.galleryFailed"));
      }
      // 취소 reject 도 앱은 이미 백그라운드를 다녀왔다 — 카메라를 되살린다
      await restart().catch(() => undefined);
    } finally {
      setGalleryBusy(false);
    }
  }, [galleryBusy, restart, showNotice, t]);

  /** 매치 — target 의 8접점(가상 4코너)으로 온디바이스 matcher 추론 → 개별 레이어 */
  const handleMatch = useCallback(
    async (target: MatchTarget): Promise<MatchResult | null> => {
      const src = target === "model" ? modelOutline : outline;
      if (!photo || matchingTarget !== null || !src || src.length !== 8) return null;
      setMatchingTarget(target);
      try {
        const payload = await computeMatchResult(photo, src);
        if (!payload) return null;
        if (target === "model") setModelResult(payload);
        else setUserResult(payload);
        // 방금 매치한 쪽만 펼치고 반대쪽은 접는다 — 두 패널이 다 펼쳐지면
        // 라벨링 핸들을 통째로 덮는다 (실기기 제보)
        setActiveTarget(target); // 방금 매치한 쪽을 보여준다
        setResultsCollapsed(false);
        // 홈 §4 최근 스캔 (FN-050) — **확신 카드 + 카탈로그 존재**만 남긴다.
        // 확신 = 엔진 매칭 또는 번호(±이름) 일치 확정 또는 번호 확장 검색.
        // 미달 후보나 미적재 카드를 남기면 홈에 "찾을 수 없는 카드"가 쌓인다.
        const topSerlno = payload.matches[0]?.cardSerlno;
        if (
          (payload.matched || payload.numberResolved || payload.nameNumberResolved) &&
          topSerlno !== undefined &&
          payload.cards?.has(topSerlno)
        ) {
          recordRecentScan(topSerlno);
        } else if (payload.ocrFound) {
          recordRecentScan(payload.ocrFound.cardSerialNo);
        }
        // 라벨링 데이터 수집 — **내 영역 매치** 가 라벨 확정 신호다. 업로드는 백그라운드
        // fire-and-forget 이고 실패해도 스캔 UX 에 영향 없다 (재매치 시 labelKey 업서트).
        if (target === "user") {
          // 라벨 수집 — 내 영역 매치가 확정 신호. fire-and-forget (실패해도 UX 무영향)
          void uploadLabel(payload, src).catch((err) =>
            nativeWarn("scan:labelUpload", err),
          );
        }
        return payload;
      } finally {
        setMatchingTarget(null);
      }
    },
    [
      photo,
      matchingTarget,
      modelOutline,
      outline,
      computeMatchResult,
      uploadLabel,
    ],
  );

  /**
   * 자동 촬영 경로의 자동 매치 — 촬영 직후 **모델 영역**으로 한 번 돌려 결과를 미리
   * 띄운다. 사용자는 확인하고 담기만 하면 된다(확정은 여전히 탭 — 여기서 컬렉션에
   * 아무것도 커밋하지 않는다). 모델 코너가 없으면(테두리 인식 실패) 자동 매치하지
   * 않는다 — 매치할 영역 자체가 없어 사용자가 라벨링해야 하는 사진이다.
   * ⚠ 수동 셔터는 이 경로를 타지 않는다 (enterEditing 의 autoMatch 참조).
   */
  useEffect(() => {
    if (!pendingAutoMatchRef.current) return;
    if (viewMode !== "editing" || !photo || !modelOutline) return;
    pendingAutoMatchRef.current = false;
    void handleMatch("model").then((result) => {
      if (!result) return;
      // 라이브 합의(촬영 직전 여러 프레임) 와 스틸 top-1 이 어긋났다 = 셔터가 열린 뒤
      // 화면이 움직여 **인식된 그 장면이 아닌 사진**이 찍혔다는 신호다. 이때 결과를
      // 그대로 내놓으면 사용자가 대고 있던 카드와 다른 답이 뜬다(2026-08-17 제보).
      // 사진을 버리고 라이브로 되돌려 다시 찍는다 — 확정 전이라 잃는 건 사진뿐이다.
      // ⚠ 판정 프레임으로 찍혔으면 재촬영하지 않는다(2026-08-18). 그 경우 사진과 라이브
      // 판정이 **같은 프레임**이라 어긋남은 "움직여서 다른 장면이 찍힘"이 아니라, 라이브
      // 힌트(거친 quad)와 스틸 매치(정밀화 quad)의 크롭 차이일 뿐이다. 접전에서 흔히
      // 갈리는데 그걸로 되돌리면 결과 화면이 영영 안 뜬다(사용자 제보 "전환이 안 된다").
      // 이 경우 스틸이 더 정확한 크롭이므로 그대로 보여준다.
      if (
        !photoFromHintFrameRef.current &&
        result.liveAgreed === false &&
        autoRetakeRef.current < AUTO_CAPTURE_MAX_RETAKE
      ) {
        autoRetakeRef.current += 1;
        showNotice(t("scan.autoRetake"));
        handleRetakeRef.current();
        return;
      }
      autoRetakeRef.current = 0;
    });
  }, [viewMode, photo, modelOutline, handleMatch, showNotice, t]);

  const errorTitle =
    state === "model-pending" ? t("scan.modelPending")
    : state === "permission-denied" ? t("scan.permissionDenied")
    : t("scan.engineError");
  // 모델 준비 중에는 **실제 다운로드 진행률**을 보여준다 — 78MB(matcher 인덱스 59MB
  // 포함) 를 받는 동안 정적인 "준비 중" 만 뜨면 멈춘 것으로 오인한다 (실기기 제보).
  const syncPct =
    syncProgress !== null && syncStatus === "downloading"
      ? Math.round(syncProgress * 100)
      : null;
  const errorBody =
    state === "model-pending"
      ? syncPct !== null
        ? t("scan.modelDownloading", { percent: syncPct })
        : syncStatus === "verifying" || syncStatus === "activating"
          ? t("scan.modelVerifying")
          : t("scan.modelPendingBody")
      : state === "permission-denied" ? t("scan.permissionDeniedBody")
      : t("scan.engineErrorBody");

  const showEditor = viewMode === "editing" && photo !== null;

  /** 시세 — 표시 가능한 값이 있을 때만. 비회원은 마스킹(기획확정안 §4) */
  const priceText = (card: CardItem): string | null => {
    const p = card.listPrice;
    if (p.display === "NONE" || p.amount === null) return null;
    const amount = isAuthenticated ? formatKrw(p.amount) : maskPrice(p.amount);
    return p.display === "REFERENCE_CASE" ? t("scan.resultPriceCase", { amount }) : amount;
  };

  /**
   * 진화 단계 — 서버가 코드(BASIC/STAGE_1/…)로 내려보내고 문구는 여기서 만든다.
   * 원천(subtypes)은 언어마다 표기가 달라 그대로 쓰면 결과 목록에서 표기가 섞인다.
   * 진화가 없는 카드(트레이너·에너지)와 상세 미보유 카드는 null 이라 줄이 안 생긴다.
   */
  const evolutionLabel = (card: CardItem | null): string | null =>
    card?.evolutionStage ? t(`evolution.${card.evolutionStage}`) : null;

  /**
   * §6.1 "로그인 회원은 스캔 결과에서 원하는 카드만 컬렉션에 저장".
   *
   * 저장 폼은 이 activity **위에 별도 activity 로 open** 한다 — 스캔을 닫으면 결과가
   * 사라져 되돌아올 곳이 없어진다. ⚠ push(엔트리)를 쓰면 안 된다: scan resolver 가
   * entry URL 과 무관하게 ScanPage 만 렌더해, store↔URL 동기화 틈에 ScanPage 가
   * remount 되며 결과가 날아가고 라이브로 복귀한다 (2026-08-15 실기기 버그).
   * 비회원은 저장 자체가 불가라 로그인부터 띄운다(§4 표).
   * 카탈로그에 없는 카드(card === null)는 담을 대상이 없어 버튼을 내지 않는다.
   */
  const addToCollection = (cardSerialNo: number) => {
    if (!isAuthenticated) {
      requireAuth({ type: "none" });
      return;
    }
    const hit = useScanSessionStore
      .getState()
      .items.find((it) => it.card.cardSerialNo === cardSerialNo);
    setAddSheetCard({ serialNo: cardSerialNo, name: hit?.card.name ?? "" });
  };

  /**
   * 카드 상세 보기 — 담기와 같은 규약으로 이 activity 위에 별도 activity 로 open 한다
   * (스캔은 아래에 마운트 유지 — 닫으면 결과로 복귀). 카탈로그 조회는 permitAll 이라
   * 비회원도 가능.
   */
  const viewCardDetail = (cardSerialNo: number) => {
    activityNav.open({ url: `/scan/cards/${cardSerialNo}` });
  };

  /**
   * 후보 목록만 그린다 — 헤더·접기/펼치기·언어경고는 **통합 패널**이 담당한다
   * (2026-08-18: 패널 4개가 카드를 덮던 문제로 하나로 합쳤다).
   */
  /** 인식 정보 본문 — 라이브 신호/OCR/후보 인덱스. 통합 패널의 접이식 하위 절 */
  const debugBody = (result: MatchResult) => (
    <div className={styles.debugBody}>
      {/* 라이브 신호 진단 — 자동 촬영이 안 뜰 때 어느 단계가 막혔는지 가른다 */}
      <p className={styles.debugRow}>
        {t("scan.debugLiveSignal")}: {t("scan.debugLiveSharpness")}{" "}
        {lastSharpnessRef.current === null ? "-" : lastSharpnessRef.current.toFixed(0)}
        {" · "}
        {t("scan.debugLiveLastHint")}{" "}
        {lastHintRef.current === null
          ? "-"
          : `#${lastHintRef.current.serlno} ${lastHintRef.current.sim.toFixed(3)}${
              lastHintRef.current.matched ? " (matched)" : ""
            }`}
      </p>
      <p className={styles.debugRow}>
        {t("scan.debugLiveHint")}:{" "}
        {result.liveTop === null
          ? "-"
          : `#${result.liveTop} · ${t(
              result.liveAgreed ? "scan.debugLiveAgree" : "scan.debugLiveDiffer",
            )}`}
      </p>
      <p className={styles.debugHeading}>{t("scan.debugOcrTitle")}</p>
      {ocrInfo === "failed" ? (
        <p className={styles.debugRow}>{t("scan.debugOcrFailed")}</p>
      ) : ocrInfo === null ? (
        <p className={styles.debugRow}>-</p>
      ) : (
        <>
          {ocrInfo.lines.length === 0 ? (
            <p className={styles.debugRow}>{t("scan.debugOcrEmpty")}</p>
          ) : (
            ocrInfo.lines.map((line, i) => (
              <p key={i} className={styles.debugRow}>
                {line}
              </p>
            ))
          )}
          <p className={styles.debugHeading}>
            {t("scan.debugOcrNumbers")}: {ocrInfo.raw.length > 0 ? ocrInfo.raw.join(", ") : "-"}
          </p>
        </>
      )}
      {result.allMatches.map((m, i) => {
        const card = result.cards?.get(m.cardSerlno);
        return (
          <p key={`${m.cardSerlno}-${i}`} className={styles.debugRow}>
            {i + 1}. #{m.cardSerlno} · {m.similarity.toFixed(3)}
            {card
              ? ` · ${card.number ? catalogCardNumber(card.number) : "-"} · ${card.name}`
              : ` · ${t("scan.resultNotInCatalog")}`}
            {result.confirmedSerlnos.has(m.cardSerlno) ? " \u2713" : ""}
          </p>
        );
      })}
    </div>
  );

  const resultRows = (result: MatchResult, rowsTarget: MatchTarget) => (
      <ul className={styles.resultList}>
        {result.matches.map((m, i) => {
          const card = result.cards?.get(m.cardSerlno) ?? null;
          const price = card ? priceText(card) : null;
          const evolution = evolutionLabel(card);
          const imageUrl = cardImageUrl(card?.imagePathway);
          return (
            <li key={`${m.cardSerlno}-${i}`} className={styles.resultRow}>
              {/* 카드 이미지 — 텍스트보다 앞. 미적재/실패 시 자리는 유지(레이아웃 흔들림 방지) */}
              {imageUrl ? (
                <img
                  className={styles.resultThumb}
                  src={imageUrl}
                  alt={card?.name ?? ""}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              ) : (
                <span className={styles.resultThumbEmpty} aria-hidden="true" />
              )}
              <span className={styles.resultInfo}>
                {/* 카드명 줄 — 이름 옆에 번호·진화. 같은 이름의 다른 인쇄판이 나란히 뜨는
                    일이 흔해(같은 번호에 여러 장이 실재) 이름만으로는 고를 수 없다 */}
                <span className={styles.resultCard}>
                  <span className={styles.resultCardName}>
                    {card ? card.name : t("scan.resultCard", { serlno: m.cardSerlno })}
                  </span>
                  {/* ko CARD_NO 는 "002/081 C" 처럼 레어도 꼬리표가 붙어 온다 — 번호만 */}
                  {card?.number && (
                    <span className={styles.resultCardSub}>{catalogCardNumber(card.number)}</span>
                  )}
                  {/* still OCR 교차 확인 — 이름+번호 동시 일치는 상급 배지로 구분 */}
                  {result.confirmedSerlnos.has(m.cardSerlno) && (
                    <span className={styles.resultNumberOk}>
                      {t(
                        result.nameConfirmedSerlnos.has(m.cardSerlno)
                          ? "scan.nameNumberConfirmed"
                          : "scan.numberConfirmed",
                      )}
                    </span>
                  )}
                  {evolution && <span className={styles.resultCardSub}>{evolution}</span>}
                </span>
                <span className={styles.resultMeta}>
                  {card
                    ? [card.cardsetName, card.rarity, card.variantLabel]
                        .filter(Boolean)
                        .join(" · ")
                    : t("scan.resultNotInCatalog")}
                </span>
                {/* 기술명 — 일본판은 세트명이 아직 한국어로 오는 릴리스가 있어(수집서버
                    localized_name 미제공) 카드를 알아보는 단서가 여기다. 트레이너·에너지는
                    기술이 없어 빈 배열이고, 그때는 줄 자체를 만들지 않는다. */}
                {card && card.attackNames && card.attackNames.length > 0 && (
                  <span className={styles.resultAttacks}>
                    {card.attackNames.join(" / ")}
                  </span>
                )}
              </span>
              <span className={styles.resultRight}>
                <span className={styles.resultSim}>{(m.similarity * 100).toFixed(1)}%</span>
                {price && <span className={styles.resultPrice}>{price}</span>}
                <span className={styles.resultActions}>
                  {/* 라벨링 정답 선택 — matcher top-1 이 틀렸을 때 어느 후보가 정답인지.
                      선택 상태는 채움(resultAdd) / 미선택은 외곽선(+resultView) 으로 구분 */}
                  <button
                    type="button"
                    aria-pressed={labelVerdict[rowsTarget] === m.cardSerlno}
                    className={
                      labelVerdict[rowsTarget] === m.cardSerlno
                        ? styles.resultAdd
                        : `${styles.resultAdd} ${styles.resultView}`
                    }
                    onClick={() =>
                      setLabelVerdict((v) => ({
                        ...v,
                        [rowsTarget]: v[rowsTarget] === m.cardSerlno ? null : m.cardSerlno,
                      }))
                    }
                  >
                    {t("scan.labelCorrect")}
                  </button>
                </span>
                {card && (
                  <span className={styles.resultActions}>
                    <button
                      type="button"
                      className={`${styles.resultAdd} ${styles.resultView}`}
                      onClick={() => viewCardDetail(card.cardSerialNo)}
                    >
                      {t("scan.viewDetail")}
                    </button>
                    <button
                      type="button"
                      className={styles.resultAdd}
                      onClick={() => addToCollection(card.cardSerialNo)}
                    >
                      {t("scan.addToCollection")}
                    </button>
                  </span>
                )}
              </span>
            </li>
          );
        })}
        {/* 카드번호 확장 검색으로 찾은 카드 — matcher top 밖이라 유사도가 없다.
            가짜 점수를 만들지 않고 "번호 일치" 배지로 근거를 밝힌다 */}
        {result.ocrFound && (() => {
          const card = result.ocrFound;
          const price = priceText(card);
          const evolution = evolutionLabel(card);
          const imageUrl = cardImageUrl(card.imagePathway);
          return (
            <li className={styles.resultRow}>
              {imageUrl ? (
                <img
                  className={styles.resultThumb}
                  src={imageUrl}
                  alt={card.name}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              ) : (
                <span className={styles.resultThumbEmpty} aria-hidden="true" />
              )}
              <span className={styles.resultInfo}>
                <span className={styles.resultCard}>
                  <span className={styles.resultCardName}>{card.name}</span>
                  {card.number && (
                    <span className={styles.resultCardSub}>{catalogCardNumber(card.number)}</span>
                  )}
                  <span className={styles.resultNumberOk}>{t("scan.numberConfirmed")}</span>
                  {evolution && <span className={styles.resultCardSub}>{evolution}</span>}
                </span>
                <span className={styles.resultMeta}>
                  {[card.cardsetName, card.rarity, card.variantLabel]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span className={styles.resultMeta}>{t("scan.ocrFoundNote")}</span>
              </span>
              <span className={styles.resultRight}>
                {price && <span className={styles.resultPrice}>{price}</span>}
                <span className={styles.resultActions}>
                  <button
                    type="button"
                    className={`${styles.resultAdd} ${styles.resultView}`}
                    onClick={() => viewCardDetail(card.cardSerialNo)}
                  >
                    {t("scan.viewDetail")}
                  </button>
                  <button
                    type="button"
                    className={styles.resultAdd}
                    onClick={() => addToCollection(card.cardSerialNo)}
                  >
                    {t("scan.addToCollection")}
                  </button>
                </span>
              </span>
            </li>
          );
        })()}
      </ul>
  );

  return (
    <div className={styles.page}>
      {/* 스캔 중임을 알리는 액센트 테두리 — 화면 네 변을 두른다 (design 2026-08-13).
          body 로 portal 하는 이유: Layout 의 페이지 전환 래퍼가 will-change:transform
          이라 position:fixed 의 기준이 그 래퍼로 바뀐다. 그대로 두면 띠가 하단에서
          끊겨 "화면 테두리"로 안 읽힌다.
          라이브(카메라가 도는 동안)에만 띄운다 — 정지·라벨링 중에는 스캔 중이 아니다. */}
      {showLive && createPortal(<div className={styles.scanFrame} aria-hidden="true" />, document.body)}
      <div className={styles.stage}>
        {/* 라이브 모드 — 프리뷰(웹뷰 뒤 네이티브 레이어) + 가이드 오버레이 + 검출 점 */}
        {showLive && state !== "active" && <div className={styles.previewPlaceholder} />}
        {/* 스캔 모드의 가이드는 락온 브래킷(dotsCanvas)이 대신한다 — QuadOverlay 의
            고정 사각 가이드는 라벨링 모드(와 dev-fake 시각화)에서만 보인다 */}
        <canvas
          ref={canvasRef}
          className={styles.overlayCanvas}
          style={
            showLive && (isLabelMode || state === "dev-fake") ? undefined : { display: "none" }
          }
        />
        <canvas
          ref={dotsCanvasRef}
          className={styles.dotsCanvas}
          style={showLive ? undefined : { display: "none" }}
        />
        {/* 브래킷 대기 자리의 세로 구간 표식 — 그려지는 것은 없다(.guideBand 주석) */}
        <div ref={guideBandRef} className={styles.guideBand} aria-hidden="true" />

        {/* 라벨링 화면 — 가이드 밖 딤 + 모델 기준선 + 사용자 8핸들 */}
        {showEditor && photo && (
          <div className={styles.editorWrap}>
            <CardOutlineEditor
              className={styles.editorCanvas}
              imageSrc={photo.src}
              naturalWidth={photo.width}
              naturalHeight={photo.height}
              outline={outline}
              modelOutline={modelOutline}
              onChange={setOutline}
            />
            <p className={styles.adjustHint}>{t("scan.adjustHint")}</p>
            {/* 테두리 인식 실패 케이스 수집용 명시 — 모델 기준선 없음을 알린다 */}
            {modelOutline === null && (
              <p className={styles.modelFailBadge}>{t("scan.modelDetectFailed")}</p>
            )}
          </div>
        )}

        {/* 좌상단 뒤로가기 — activity 닫기 (열었던 화면으로 복귀).
            앱바를 따로 얹지 않고 카메라 위에 띄운다 (design SC-003) — 불투명한
            바를 두면 뷰파인더 윗부분이 잘려 "카메라가 화면을 가득 채운다"가 깨진다. */}
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.closeButton}
            aria-label={t("action.back", { ns: "common" })}
            // 이 버튼은 "스캔 종료" 라 라벨링 중에도 그대로 닫는다 — dispatchBack 을
            // 타면 뒤로가기 가드(다시 찍기)에 걸려 나갈 방법이 없어진다.
            // 스캔 모드에서 담은 카드가 있으면 폐기 확인(SC-010d · FN-031)부터.
            onClick={requestClose}
          >
            {/* 앱바의 뒤로가기와 같은 아이콘 — 카메라 위라 색만 --camera-* 로 덮는다 */}
            <Icon name="Back" size="md" ariaLabel={t("action.back", { ns: "common" })} />
          </button>
          {/* 스캔↔라벨링 모드 전환 (2026-08-24) — 디버그 메뉴 토글 전용, 라이브에서만.
              버튼 글자는 **전환 목적지** 모드다. */}
          {labelingEnabled && showLive && (
            <button
              type="button"
              className={styles.modeToggle}
              onClick={toggleScanMode}
            >
              {t(isLabelMode ? "scan.modeScan" : "scan.modeLabel")}
            </button>
          )}
        </div>

        {/* 카드 스캔 모드 — 인식 결과 레일(SC-010) + 요약(SC-011) 진입 */}
        {showLive && !isLabelMode && (
          <ScanResultRail
            items={sessionItems}
            authenticated={isAuthenticated}
            onRemove={sessionRemove}
          />
        )}
        {showLive && !isLabelMode && sessionTotals && (
          <>
            <button
              type="button"
              className={styles.summaryEntry}
              onClick={() => activityNav.open({ url: "/scan/summary" })}
              aria-label={t("scanResult.summary", { count: sessionTotals.cardCount })}
            >
              {isAuthenticated
                ? formatKrw(sessionTotals.totalAmt)
                : maskPrice(sessionTotals.totalAmt)}
              <span aria-hidden="true">›</span>
            </button>
            {sessionTotals.unknownCount > 0 && (
              <p className={styles.summaryNote}>
                {t("scanResult.unknownShort", { count: sessionTotals.unknownCount })}
              </p>
            )}
          </>
        )}

        {/* §4.1 5회 소진 — 추가 인식을 막고 간편로그인 제시 (스캔 모드) */}
        {showLive && !isLabelMode && guestLimitReached && !isAuthenticated && (
          <div className={styles.limitOverlay}>
            <p className={styles.limitTitle}>{t("guest.limitTitle")}</p>
            <p className={styles.limitBody}>{t("guest.limitBody")}</p>
            {/* 두 버튼 모두 **Astryx** 다 (design 2026-08-28 기준). 카메라 위라 종전에는
                커스텀 버튼(.limitCta)을 썼는데, 대안 CTA 를 나란히 두는 순간 둘의 크기·
                서체가 갈렸다. 딤(--camera-scrim) 위에서도 primary·ghost 대비는 선다. */}
            <Button
              variant="primary"
              label={t("guest.login")}
              clickAction={() => requireAuth({ type: "none" })}
            />
            {/* 대안 CTA (SC-003 · FN-023, design 2026-08-28) — 로그인하지 않아도 시세는
                검색으로 볼 수 있다. 잠금 화면에 닫기가 없으므로 나가는 길이 이것뿐이다.
                ⚠ closeAllTo 다 — 스캔은 activity 라 그냥 navigate 하면 잠금 화면이
                  위에 남는다(design 은 activity 밖이라 navigate 하나로 끝난다). */}
            <Button
              variant="ghost"
              label={t("guest.searchInstead")}
              clickAction={() => activityNav.closeAllTo("/search")}
            />
          </div>
        )}

        {showLive && state === "dev-fake" && (
          <p className={styles.fakeBadge}>{t("scan.fakeEngineBadge")}</p>
        )}

        {/* 언어를 바꾸면 그 언어 인덱스를 실제로 내려받는다(7~12MB). 카메라가 살아 있는
            동안에는 진행 화면이 없어 "안 바뀌었다"로 보였다 — 여기서 상태를 밝힌다.
            ⚠ **재시작 구간까지 이어서** 띄운다 (2026-09-07): 종전 조건(state==="active")
            은 stop→start 사이에 정확히 거짓이라 안내가 잠깐 사라졌고, 그 공백이
            "다 받고 나서 깜빡인다"의 절반이었다(나머지 절반은 웹뷰 투명화 왕복 —
            useScanSession 의 keepScanLiveRef). */}
        {showLive && (state === "active" || restarting) && modelPreparing && (
          <p className={styles.langSyncBadge}>
            {syncPct !== null
              ? t("scan.modelDownloading", { percent: syncPct })
              : t("scan.matcherPending")}
          </p>
        )}

        {notice && <p className={styles.noticeBadge}>{notice}</p>}

        {/* 촬영 중 피드백 — 셔터 즉시 반응 체감용 (기존 noticeBadge 스타일 재사용) */}
        {capturing && !notice && <p className={styles.noticeBadge}>{t("scan.capturing")}</p>}

        {showLive && !isAuthenticated && (
          <p className={styles.guestBadge}>
            {t("guest.remaining", { remaining: guestRemaining, limit: GUEST_SCAN_LIMIT })}
          </p>
        )}

        {/* 자동 셔터 진단줄 — **디버그 토글 전용** (2026-08-28 사용자 결정).
            안내 문구 두 개를 다 걷어냈다: "테두리에 카드를 맞추면…" 은 브래킷이
            카드를 따라가는 지금 틀린 안내이고, "그대로 잠시 유지해 주세요" 는
            브래킷이 차오르는 것과 같은 말을 글로 되풀이했다. 상태는 브래킷이
            말하고, 이 자리에는 숫자만 남는다.
            vote=프레임 합의 / sim=top-1 유사도 / sharp=선명도. */}
        {showLive && state === "active" && diagnosticsEnabled && autoDebug && (
          <p className={styles.autoHint}>
            {`vote ${autoDebug.vote}/${AUTO_CAPTURE_VOTE} · sim ${autoDebug.sim.toFixed(2)} · sharp ${
              autoDebug.sharp === null ? "-" : autoDebug.sharp.toFixed(0)
            } · armed ${scanArmed ? "Y" : "N"} · gate ${autoDebug.gate}${
              autoDebug.fail > 0 ? `(x${autoDebug.fail})` : ""
            }`}
          </p>
        )}

        {/* 마지막 확정 시도의 판정 이유 + OCR 원문 전체 — wrap + 내부 스크롤 박스 */}
        {/* ⚠ 스틸·확정을 한 박스에 합친다 — .confirmDebug 는 absolute 라 두 개를 쓰면
            같은 자리에 겹쳐 그려져 나중 것만 보인다 (2026-08-26 실사고) */}
        {diagnosticsEnabled &&
          showLive &&
          state === "active" &&
          (liveDebug || stillDebug || confirmDebug) &&
          !isLabelMode && (
          <p className={styles.confirmDebug}>
            {[
              liveDebug && `라이브: ${liveDebug}`,
              stillDebug && `스틸: ${stillDebug}`,
              confirmDebug && `확정: ${confirmDebug}`,
            ]
              .filter(Boolean)
              .join("\n")}
          </p>
        )}

        {/* 세션 실패 상태 — 운영에서는 fake 로 숨기지 않고 명시한다 */}
        {showLive && (ERROR_STATES as readonly string[]).includes(state) && (
          <div className={styles.limitOverlay}>
            <p className={styles.limitTitle}>{errorTitle}</p>
            <p className={styles.limitBody}>{errorBody}</p>
            <button type="button" className={styles.limitCta} onClick={retry}>
              {t("scan.retry")}
            </button>
          </div>
        )}

        {/* 매칭 결과 — **패널 하나**로 합쳤다(2026-08-18). 종전엔 모델/내영역/라벨/인식정보
            4개가 세로로 쌓여 카드를 통째로 덮었다(사용자 제보). 한 번에 한 결과만 띄우고
            (모델↔내 영역 전환) 헤더 탭으로 전체를 접는다 — 높이가 목록 하나로 묶인다. */}
        {showEditor && (modelResult || userResult) && (
          <div className={styles.resultLayers}>
            <div className={styles.resultPanel} data-target={activeTarget}>
              {(() => {
                const shown = activeTarget === "user" ? userResult : modelResult;
                const result = shown ?? modelResult ?? userResult;
                if (!result) return null;
                return (
                  <>
                    <button
                      type="button"
                      className={styles.resultToggle}
                      aria-expanded={!resultsCollapsed}
                      onClick={() => setResultsCollapsed((c) => !c)}
                    >
                      <span className={styles.resultTitle}>
                        {t(activeTarget === "model" ? "scan.resultModel" : "scan.resultUser")}
                        {" · "}
                        {result.matched
                          ? t("scan.resultMatched")
                          : result.nameNumberResolved
                            ? t("scan.resultNameNumberResolved")
                            : result.numberResolved
                              ? t("scan.resultNumberResolved")
                              : t("scan.resultUncertain")}
                      </span>
                      <span className={styles.resultChevron} aria-hidden="true">
                        {resultsCollapsed ? "\u25b8" : "\u25be"}
                      </span>
                    </button>

                    {!resultsCollapsed && (
                      <>
                        {/* 모델/내 영역 전환 — 둘 다 있을 때만. 동시에 펼치지 않는다 */}
                        {modelResult && userResult && (
                          <HStack gap={1} wrap="wrap">
                            {(["model", "user"] as const).map((tg) => (
                              <button
                                key={tg}
                                type="button"
                                aria-pressed={activeTarget === tg}
                                className={
                                  activeTarget === tg
                                    ? styles.resultAdd
                                    : `${styles.resultAdd} ${styles.resultView}`
                                }
                                onClick={() => setActiveTarget(tg)}
                              >
                                {t(tg === "model" ? "scan.resultModel" : "scan.resultUser")}
                              </button>
                            ))}
                          </HStack>
                        )}

                        {result.langMismatch && (
                          <p className={styles.resultLangWarn}>
                            {t("scan.langMismatch", {
                              selected: SCAN_LANG_LABEL[scanLang],
                              lang:
                                (SCAN_LANG_LABEL as Record<string, string>)[result.langMismatch] ??
                                result.langMismatch,
                            })}
                          </p>
                        )}

                        {resultRows(result, activeTarget)}

                        {/* 라벨 수집 — 정답 선택·촬영 조건·업로드 (디버그 토글 전용) */}
                        {labelingEnabled && (
                        <VStack gap={1}>
                          <span className={styles.resultMeta}>{t("scan.labelConditions")}</span>
                          <HStack gap={1} wrap="wrap">
                            <button
                              type="button"
                              aria-pressed={labelVerdict[activeTarget] === "none"}
                              className={
                                labelVerdict[activeTarget] === "none"
                                  ? styles.resultAdd
                                  : `${styles.resultAdd} ${styles.resultView}`
                              }
                              onClick={() =>
                                setLabelVerdict((v) => ({
                                  ...v,
                                  [activeTarget]:
                                    v[activeTarget] === "none" ? null : "none",
                                }))
                              }
                            >
                              {t("scan.labelNoneOfThese")}
                            </button>
                            {SCAN_LABEL_CONDITIONS.map((c) => (
                              <button
                                key={c}
                                type="button"
                                aria-pressed={labelConditions.includes(c)}
                                className={
                                  labelConditions.includes(c)
                                    ? styles.resultAdd
                                    : `${styles.resultAdd} ${styles.resultView}`
                                }
                                onClick={() =>
                                  setLabelConditions((prev) =>
                                    prev.includes(c)
                                      ? prev.filter((x) => x !== c)
                                      : [...prev, c],
                                  )
                                }
                              >
                                {t(`scan.cond_${c}`)}
                              </button>
                            ))}
                            <button
                              type="button"
                              className={styles.resultAdd}
                              disabled={labelUploading}
                              onClick={handleUploadLabel}
                            >
                              {t(labelUploading ? "scan.labelUploading" : "scan.labelUpload")}
                            </button>
                          </HStack>
                        </VStack>
                        )}

                        {/* 인식 정보 — 같은 패널 안의 접이식 하위 절 */}
                        <button
                          type="button"
                          className={styles.resultToggle}
                          aria-expanded={!infoCollapsed}
                          onClick={() => setInfoCollapsed((c) => !c)}
                        >
                          <span className={styles.resultTitle}>{t("scan.debugPanel")}</span>
                          <span className={styles.resultChevron} aria-hidden="true">
                            {infoCollapsed ? "\u25b8" : "\u25be"}
                          </span>
                        </button>
                        {!infoCollapsed && debugBody(result)}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* 인식 언어 = 대조할 카드 언어판. **셔터 옆(좌측 하단)** 에 둔다
            (design 2026-08-18). 종전엔 우상단이었는데, 인식이 어긋난 직후 바로
            바꾸는 자리라 손가락이 이미 가 있는 셔터 옆이 맞다.
            ⚠ 칩 위에 캡션 한 줄 (design 2026-08-27) — 칩은 'KO' 두 글자라 무엇을
              정하는 자리인지 안 보이는데, 인식 정확도가 여기에 달려 있다. */}
        {showLive && (
          <div className={styles.bottomTools}>
            <span className={styles.toolsCaption}>{t("scan.editionHint")}</span>
            {/* 언어판 · 갤러리 — 둘 다 "찍기 전에 정하는 것" 이라 한 묶음이다 (design) */}
            <div className={styles.toolsRow}>
              {/* 인식 언어 = 대조할 카드 언어판. 어긋나면 조용히 틀린 카드가 나오므로
                  설정 화면이 아니라 카메라 위에 둔다 (실패 직후 바로 바꿀 수 있어야 한다). */}
              <button
                type="button"
                className={styles.langChip}
                aria-haspopup="dialog"
                aria-expanded={langOpen}
                aria-label={t("scan.langChipLabel", { name: SCAN_LANG_LABEL[scanLang] })}
                onClick={() => setLangOpen(true)}
              >
                {SCAN_LANG_SHORT[scanLang]}
              </button>
              {/* 갤러리 불러오기 (SC-003 · FN-025) — 고른 사진이 라이브 스틸과 같은
                  판정 경로를 탄다. 네이티브에서만: 웹은 스캔 화면 자체가 없다. */}
              {isNative() && (
                <button
                  type="button"
                  className={styles.galleryButton}
                  aria-label={t("scan.gallery")}
                  disabled={galleryBusy}
                  onClick={() => void handleGallery()}
                >
                  <Icon name="Photo" size="md" ariaLabel={t("scan.gallery")} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* 줌 — 화면 우측에 세로로 (design SC-003 · FN-024). 하단은 셔터가 쓰므로
            가로 행으로 두면 셔터와 자리를 다툰다. 세로로 세우면 오른손 엄지가 닿는
            높이에 선다. 라벨링 모드에서는 감춘다 — 그쪽은 정지 사진 편집이 본론이다. */}
        {showLive && state === "active" && !isLabelMode && (
          <div className={styles.zoomColumn}>
            {ZOOM_STEPS.map((z) => (
              <button
                key={z}
                type="button"
                aria-pressed={Math.abs(zoom - z) < 0.05}
                className={
                  Math.abs(zoom - z) < 0.05
                    ? `${styles.zoomButton} ${styles.zoomButtonActive}`
                    : styles.zoomButton
                }
                onClick={() => applyZoom(z)}
              >
                {z}×
              </button>
            ))}
          </div>
        )}

        {/* 하단 줄 뒤 그늘 (design 2026-08-27) — 밝은 책상·조명 위에서는 흰 셔터도
            액센트 요약 버튼도 영상에 묻혔다. 아래 도구·셔터·요약보다 먼저 그린다. */}
        {showLive && <div className={styles.bottomScrim} aria-hidden="true" />}

        {/* 하단 컨트롤 */}
        <div className={styles.bottomControls}>
          {showLive ? (
            <button
              type="button"
              className={styles.captureButton}
              onClick={() => void handleCapture()}
              disabled={capturing}
              aria-label={t(capturing ? "scan.capturing" : "scan.capture")}
            >
              <span className={styles.captureCore} aria-hidden="true" />
            </button>
          ) : (
            <div className={styles.editBar}>
              <button type="button" className={styles.editButton} onClick={handleRetake}>
                {t("scan.retake")}
              </button>
              <button
                type="button"
                className={styles.matchModelButton}
                disabled={modelOutline === null || matchingTarget !== null}
                onClick={() => void handleMatch("model")}
              >
                {matchingTarget === "model" ? t("scan.matching") : t("scan.matchModel")}
              </button>
              <button
                type="button"
                className={styles.matchButton}
                disabled={matchingTarget !== null}
                onClick={() => void handleMatch("user")}
              >
                {matchingTarget === "user" ? t("scan.matching") : t("scan.matchUser")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SC-010d 스캔 이탈 폐기 확인 (FN-031) — 스캔 모드에서 담은 카드가 있을 때 X */}
      <ConfirmDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        variant="destructive"
        title={t("scanDiscard.title")}
        description={t("scanDiscard.body")}
        secondaryText={t("scanDiscard.cancel")}
        primaryText={t("scanDiscard.confirm")}
        onSecondaryPressed={() => setDiscardOpen(false)}
        onPrimaryPressed={() => {
          sessionClear();
          closeAfterDialogPop();
        }}
      />

      {/* 인식 언어 선택 시트 (design 2026-08-18).
          인라인 드롭다운이었는데 **카메라 위라 뷰파인더를 가린다** — 무슨 판을 찍는
          중인지 확인하려고 연 메뉴가 정작 그 화면을 덮었다. 시트로 내려 연다.
          ⚠ stage 밖에 둔다. stage 는 overflow:hidden 이라 안에 두면 시트가 잘린다.
          ⚠ 여기서 고르는 건 전역 에디션이 아니라 **인식 언어**(scanLang)다 —
            matcher·OCR 이 대조할 카드 언어판이라 축이 다르다. */}
      <Sheet
        open={langOpen}
        onClose={() => setLangOpen(false)}
        ariaLabel={t("scan.langSheetTitle")}
        backKind="scan-lang-sheet"
      >
        <VStack gap={4} padding={5}>
          <Text type="body" weight="semibold" as="p">{t("scan.langSheetTitle")}</Text>
          <HStack gap={1} wrap="wrap">
            {SCAN_LANGS.map((lang) => (
              <ToggleButton
                key={lang}
                size="sm"
                data-compact=""
                label={SCAN_LANG_LABEL[lang]}
                isPressed={lang === scanLang}
                onPressedChange={() => {
                  setScanLang(lang);
                  setLangOpen(false);
                }}
              />
            ))}
          </HStack>
          <Button
            variant="ghost"
            label={t("action.close", { ns: "common" })}
            clickAction={() => setLangOpen(false)}
          />
        </VStack>
      </Sheet>

      {/* 담기 시트 — 스캔 위에 얹힌다. activity 가 아니라 스캔이 그대로 남고,
          담는 동안은 scanFrozen 이 인식을 멈춘다(위 주석). */}
      {addSheetCard && (
        <AddToCollectionSheet
          open
          /* 스캔 결과만 보유를 만든다 — 보유의 정의가 "카메라에 실물을 댄 적 있다" 다
             (IA rev27). 다른 자리의 담기는 컬렉션에만 들어간다. */
          target="holding"
          onClose={() => setAddSheetCard(null)}
          cardSerialNo={addSheetCard.serialNo}
          cardName={addSheetCard.name}
        />
      )}
    </div>
  );
}
